
import { supabase, getCurrentOrganisationId, isMockMode } from './supabaseClient';
import {
  Ticket,
  Vehicle,
  Hire,
  Task,
  Driver,
  TimelineEvent,
  DashboardMetrics,
  RecentActivityItem,
  ReportData,
  ReportDateRange,
  TrialSavingsReportData,
  TrialSavingsReportType,
  FleetLead,
  FleetLeadDashboardData,
  FleetLeadEvent,
  FleetLeadStatus,
  FleetTrialState,
  DailyCheck,
  Defect,
  DefectAction
} from '../types';
import { compressImageFile } from '../utils/imageCompression';
import { normalizeTicketStatus } from '../utils/ticketStatus';
import { inferEnforcementType, type EnforcementType } from '../utils/enforcement';
import { normalizeStage } from '../utils/stage';
import pLimit from 'p-limit';
import { AuditService } from './auditService';
import {
  determineLiability,
  auditAgreement,
  type AuditResult,
  type ManualOverrideEvent,
  type NoHireConfirmation,
  type NoHireReason,
  type OverrideReason
} from './liabilityEngine';

// Concurrency limit for batch audit processing
const AUDIT_CONCURRENCY_LIMIT = 8;

const NON_PAYABLE_CLOSED_STATUSES = new Set(['cancelled', 'appeal_won', 'transferred']);

const hasDriverAssignment = (ticket: Partial<Ticket>): boolean =>
  Boolean(ticket.hire_id || ticket.assigned_driver_name || ticket.assigned_driver_email);

const isActionRequiredTicket = (ticket: Partial<Ticket>): boolean => {
  const status = normalizeTicketStatus(ticket.status);

  if (NON_PAYABLE_CLOSED_STATUSES.has(status)) return false;

  // Platform side is still unsettled.
  if (status !== 'paid') return true;

  // Platform paid + no assigned driver means no further recovery action needed.
  if (!hasDriverAssignment(ticket)) return false;

  // Assigned tickets remain actionable until the driver-side charge is settled.
  return ticket.driver_charge_status !== 'paid';
};

const TRIAL_REPORT_LIMITS: Record<TrialSavingsReportType, number> = {
  '3pcn_snapshot': 3,
  '10pcn_savings_report': 10,
};

const MANUAL_WORK_MINUTES_PER_PCN = 15;
const ASSISTED_WORK_MINUTES_PER_PCN = 5;

const money = (value: unknown): number => {
  const parsed = Number(value || 0);
  return Number.isFinite(parsed) ? parsed : 0;
};

const dateAgeDays = (value?: string | null): number | null => {
  if (!value) return null;
  const date = new Date(String(value).split('T')[0]);
  if (Number.isNaN(date.getTime())) return null;
  return Math.floor((Date.now() - date.getTime()) / DAY_MS);
};

const getDeadlineRisk = (ticket: Partial<Ticket>): 'low' | 'medium' | 'high' => {
  const status = normalizeTicketStatus(ticket.status);
  if (NON_PAYABLE_CLOSED_STATUSES.has(status) || status === 'paid') return 'low';

  const stage = String(ticket.stage || '').toLowerCase();
  if (stage.includes('charge certificate') || stage.includes('order for recovery') || stage.includes('ofr')) return 'high';

  const age = dateAgeDays(ticket.issue_date || ticket.created_at);
  if (age === null) return 'medium';
  if (age >= 21) return 'high';
  if (age >= 10) return 'medium';
  return 'low';
};

const incrementBucket = <T extends { count: number; exposure: number }>(
  map: Map<string, T>,
  key: string,
  exposure: number,
  factory: () => T
) => {
  const existing = map.get(key) || factory();
  existing.count += 1;
  existing.exposure += exposure;
  map.set(key, existing);
};

const buildTrialSavingsReport = (
  reportType: TrialSavingsReportType,
  org: any,
  tickets: Partial<Ticket>[]
): TrialSavingsReportData => {
  const targetCount = TRIAL_REPORT_LIMITS[reportType];
  const extensionStatus = String(org?.trial_extension_status || 'none');
  const processedCount = Math.min(Number(org?.trial_pcns_processed || tickets.length || 0), targetCount);
  const canViewReport = reportType === '3pcn_snapshot' || extensionStatus === 'unlocked';

  const rows = canViewReport
    ? tickets.slice(0, targetCount).map((ticket) => {
        const amount = money(ticket.amount);
        const liabilityReady = hasDriverAssignment(ticket);
        const recoveryVisible = Boolean(ticket.driver_charge_status || ticket.admin_fee_charged || ticket.driver_charge_amount);
        const evidenceGap = liabilityReady
          ? null
          : 'No matched driver, hirer, or hire agreement evidence on this PCN';
        const driverOrHirer = String(ticket.assigned_driver_name || ticket.assigned_driver_email || '').trim() || 'Unassigned';

        return {
          id: String(ticket.id || ''),
          pcnNumber: String(ticket.pcn_number || 'Unnumbered'),
          vrn: String(ticket.vrn || ''),
          issueDate: ticket.issue_date || null,
          authority: String(ticket.issuing_authority || 'Unknown authority'),
          location: String(ticket.location || 'Unknown location'),
          amount,
          discountAmount: ticket.discount_amount == null ? null : money(ticket.discount_amount),
          status: String(ticket.status || 'unknown'),
          stage: String(ticket.stage || 'Unknown stage'),
          decision: ticket.decision ? String(ticket.decision) : null,
          liabilityReady,
          recoveryVisible,
          evidenceGap,
          deadlineRisk: getDeadlineRisk(ticket),
          driverOrHirer,
        };
      })
    : [];

  const totalCostExposure = rows.reduce((sum, row) => sum + row.amount, 0);
  const liabilityReadyCount = rows.filter((row) => row.liabilityReady).length;
  const evidenceGapCount = rows.filter((row) => row.evidenceGap).length;
  const deadlineRisksCount = rows.filter((row) => row.deadlineRisk === 'high').length;
  const recoveryVisibleCount = rows.filter((row) => row.recoveryVisible).length;
  const recoveryOutstandingAmount = rows.reduce((sum, row) => {
    if (row.status.toLowerCase() === 'paid' && !row.recoveryVisible) return sum + row.amount;
    return sum;
  }, 0);

  const recoverySummary = rows.reduce(
    (summary, row) => {
      const status = String((tickets.find((ticket) => ticket.id === row.id) as any)?.driver_charge_status || '').toLowerCase();
      if (status === 'paid') summary.paid += 1;
      else if (status === 'invoiced') summary.invoiced += 1;
      else if (status === 'pending') summary.pending += 1;
      else summary.unassigned += 1;
      return summary;
    },
    { paid: 0, invoiced: 0, pending: 0, unassigned: 0 }
  );

  const driverMap = new Map<string, { name: string; count: number; exposure: number }>();
  const locationMap = new Map<string, { name: string; count: number; exposure: number }>();

  rows.forEach((row) => {
    if (row.driverOrHirer !== 'Unassigned') {
      incrementBucket(driverMap, row.driverOrHirer, row.amount, () => ({ name: row.driverOrHirer, count: 0, exposure: 0 }));
    }
    incrementBucket(locationMap, row.location, row.amount, () => ({ name: row.location, count: 0, exposure: 0 }));
  });

  const repeatDrivers = Array.from(driverMap.values())
    .filter((item) => item.count > 1)
    .sort((a, b) => b.exposure - a.exposure)
    .slice(0, 5);

  const repeatLocations = Array.from(locationMap.values())
    .filter((item) => item.count > 1)
    .sort((a, b) => b.exposure - a.exposure)
    .slice(0, 5);

  const recommendedPlan = (() => {
    if (reportType === '10pcn_savings_report' || rows.length >= 8 || totalCostExposure >= 1000) {
      return {
        name: 'Growth',
        reason: 'The sampled PCNs show enough recurring exposure to justify stronger automation and reporting.',
      };
    }
    if (rows.length >= 3) {
      return {
        name: 'Starter',
        reason: 'The trial has enough processed PCNs to replace manual extraction and give visibility on liability gaps.',
      };
    }
    return {
      name: 'Complete the 3-PCN trial',
      reason: 'More confirmed PCNs are needed before a plan recommendation is meaningful.',
    };
  })();

  return {
    reportType,
    generatedAt: new Date().toISOString(),
    trialStatus: org?.trial_status ?? null,
    trialEndsAt: org?.trial_ends_at ?? org?.trial_expires_at ?? null,
    extensionStatus,
    processedCount,
    targetCount,
    availablePcnCount: tickets.length,
    canViewReport,
    canRequestExtension: reportType === '3pcn_snapshot' && processedCount >= 3 && extensionStatus === 'none',
    metrics: {
      totalCostExposure: Math.round(totalCostExposure),
      estimatedTimeSavedMinutes: rows.length * (MANUAL_WORK_MINUTES_PER_PCN - ASSISTED_WORK_MINUTES_PER_PCN),
      deadlineRisksCount,
      liabilityReadyCount,
      evidenceGapCount,
      recoveryVisibleCount,
      recoveryOutstandingAmount: Math.round(recoveryOutstandingAmount),
      averagePcnValue: rows.length ? Number((totalCostExposure / rows.length).toFixed(2)) : 0,
    },
    recoverySummary,
    repeatDrivers,
    repeatLocations,
    recommendedPlan,
    rows,
  };
};

const COMPLIANCE_REMINDER_DAYS = [30, 7, 0] as const;
const DAY_MS = 1000 * 60 * 60 * 24;

const parseDateOnly = (value?: string | null) => {
  if (!value) return null;
  const date = new Date(String(value).split('T')[0]);
  return Number.isNaN(date.getTime()) ? null : date;
};

const daysUntilDate = (due: Date, baseDate = new Date()) =>
  Math.floor((due.getTime() - baseDate.getTime()) / DAY_MS);

const shouldCreateComplianceReminder = (days: number) =>
  COMPLIANCE_REMINDER_DAYS.includes(days as (typeof COMPLIANCE_REMINDER_DAYS)[number]) || days < 0;

const priorityFromDays = (days: number): Task['priority'] =>
  days <= 7 ? 'High' : 'Medium';

const severityFromDays = (days: number): Task['severity'] =>
  days < 0 ? 'critical' : days <= 7 ? 'high' : 'medium';

// --- EXPANDED MOCK DATA ---
const MOCK_TIMELINE: TimelineEvent[] = [
  { id: 't1', date: new Date(Date.now() - 86400000 * 2).toISOString(), title: 'PCN Received', description: 'Imported via Batch Upload', type: 'system' },
  { id: 't2', date: new Date(Date.now() - 86400000).toISOString(), title: 'Driver Identified', description: 'Matched to hire H-101 (John Smith)', type: 'system' },
  { id: 't3', date: new Date(Date.now() - 43200000).toISOString(), title: 'Notice Sent', description: 'Automated email sent to john@example.com', type: 'email', user: 'System' },
];

// Helper to generate a larger dataset for analytics
const generateAnalyticsData = (count: number) => {
  const drivers = ['John Smith', 'Sarah Jones', 'Mike Brown', 'David Wilson', 'Emma Taylor', 'Chris Evans'];
  const vehicles = ['LO62 NRE', 'KW18 JJS', 'AB12 CDE', 'RY70 XWZ', 'PL19 KMN'];
  const councils = ['Westminster City Council', 'Camden Council', 'Manchester City Council', 'TfL', 'Birmingham City Council'];
  const locations = ['High Street', 'Station Road', 'London Road', 'Church Street', 'Main Street', 'Victoria Road', 'Park Avenue', 'Market Place', 'Bridge Street', 'King Street'];
  const statuses = ['unassigned', 'active', 'paid', 'appeal_pending', 'transferred', 'cancelled'];
  
  const tickets: Ticket[] = [];
  
  for(let i=0; i < count; i++) {
    const status = statuses[Math.floor(Math.random() * statuses.length)];
    
    // Distribute dates over last 90 days
    const daysAgo = Math.floor(Math.random() * 90);
    const date = new Date();
    date.setDate(date.getDate() - daysAgo);

    tickets.push({
      id: `gen-${i}`,
      pcn_number: `PCN${10000 + i}`,
      vrn: vehicles[Math.floor(Math.random() * vehicles.length)],
      vehicle_id: 'v1',
      created_at: date.toISOString(),
      issue_date: date.toISOString(),
      contravention_date: date.toISOString(),
      issuing_authority: councils[Math.floor(Math.random() * councils.length)],
      location: locations[Math.floor(Math.random() * locations.length)], // Random location
      amount: 130,
      discount_amount: 65,
      status: status,
      stage: 'PCN Issued',
      decision: status === 'transferred' ? 'transfer' : (status === 'appeal_pending' ? 'appeal' : undefined),
      assigned_driver_name: drivers[Math.floor(Math.random() * drivers.length)],
      organisation_id: 'mock',
      hire_id: null,
      contravention_type: 'Parking'
    });
  }
  return tickets;
};

const MOCK_TICKETS: Ticket[] = [
  { 
    id: '1', 
    pcn_number: 'PCN88291', 
    vrn: 'LO62 NRE', 
    contravention_date: new Date(Date.now() - 1000 * 60 * 60 * 24 * 5).toISOString(), // 5 days ago
    issue_date: new Date(Date.now() - 1000 * 60 * 60 * 24 * 2).toISOString(), // 2 days ago
    created_at: new Date(Date.now() - 1000 * 60 * 60 * 24 * 2).toISOString(),
    stage: 'PCN Issued',
    issuing_authority: 'Westminster City Council', 
    amount: 130,
    discount_amount: 65,
    status: 'unassigned', 
    location: 'High Street, London', 
    organisation_id: 'mock', 
    vehicle_id: 'v1', 
    hire_id: null,
    contravention_type: 'Parking',
    timeline: [MOCK_TIMELINE[0]] 
  },
  { 
    id: '2', 
    pcn_number: 'PCN99210', 
    vrn: 'KW18 JJS', 
    contravention_date: new Date(Date.now() - 1000 * 60 * 60 * 24 * 10).toISOString(),
    issue_date: new Date(Date.now() - 1000 * 60 * 60 * 24 * 5).toISOString(),
    created_at: new Date(Date.now() - 1000 * 60 * 60 * 24 * 5).toISOString(),
    stage: 'Case Closed',
    issuing_authority: 'Manchester City Council', 
    amount: 130,
    discount_amount: 65,
    status: 'paid', 
    location: 'Main Road, Manchester', 
    organisation_id: 'mock', 
    vehicle_id: 'v2', 
    hire_id: null,
    contravention_type: 'Bus Lane',
    timeline: MOCK_TIMELINE 
  },
  { 
    id: '3', 
    pcn_number: 'PCN77211', 
    vrn: 'LO62 NRE', 
    contravention_date: new Date(Date.now() - 1000 * 60 * 60 * 24 * 14).toISOString(),
    issue_date: new Date(Date.now() - 1000 * 60 * 60 * 24 * 12).toISOString(),
    created_at: new Date(Date.now() - 1000 * 60 * 60 * 24 * 12).toISOString(),
    stage: 'Notice to Owner',
    issuing_authority: 'Westminster City Council', 
    amount: 65,
    discount_amount: 32.50,
    status: 'active', 
    location: 'Station Car Park', 
    organisation_id: 'mock', 
    vehicle_id: 'v1', 
    hire_id: 'h1',
    contravention_type: 'Parking', 
    assigned_driver_name: 'John Smith', 
    timeline: MOCK_TIMELINE 
  },
  { 
    id: '4', 
    pcn_number: 'PCN55112', 
    vrn: 'AB12 CDE', 
    contravention_date: new Date(Date.now() - 1000 * 60 * 60 * 24 * 20).toISOString(),
    issue_date: new Date(Date.now() - 1000 * 60 * 60 * 24 * 15).toISOString(),
    created_at: new Date(Date.now() - 1000 * 60 * 60 * 24 * 15).toISOString(),
    stage: 'Formal Representation',
    issuing_authority: 'Camden Council', 
    amount: 130,
    discount_amount: 65,
    status: 'appeal_rejected', 
    location: 'Camden High St', 
    organisation_id: 'mock', 
    vehicle_id: 'v3', 
    hire_id: null,
    contravention_type: 'Moving Traffic',
    timeline: MOCK_TIMELINE 
  },
];

// TicketDetail evidence is stored in Supabase Storage in prod; mock mode uses in-memory URLs.
const MOCK_TICKET_EVIDENCE: Record<string, Array<{ name: string; url: string }>> = {};

const MOCK_VEHICLES: Vehicle[] = [
  {
    id: 'v1',
    vrm: 'LO62 NRE',
    make: 'Ford',
    model: 'Transit',
    colour: 'White',
    status: 'Active',
    notes: '',
    tags: ['Van', 'Pool Car'],
    organisation_id: 'mock',
    insurance_provider: 'AXA',
    insurance_policy_number: 'AXA-1001',
    insurance_status: 'active',
    insurance_expiry_date: new Date(Date.now() + 8 * DAY_MS).toISOString()
  },
  {
    id: 'v2',
    vrm: 'KW18 JJS',
    make: 'Vauxhall',
    model: 'Vivaro',
    colour: 'Silver',
    status: 'Active',
    notes: '',
    tags: ['Van', 'PHV'],
    organisation_id: 'mock',
    insurance_provider: 'Allianz',
    insurance_policy_number: 'ALZ-2331',
    insurance_status: 'active',
    insurance_expiry_date: new Date(Date.now() + 33 * DAY_MS).toISOString()
  },
  {
    id: 'v3',
    vrm: 'AB12 CDE',
    make: 'Mercedes',
    model: 'Sprinter',
    colour: 'White',
    status: 'Active',
    notes: '',
    tags: ['Uber', 'Owned'],
    organisation_id: 'mock',
    insurance_provider: 'Zurich',
    insurance_policy_number: 'ZUR-1900',
    insurance_status: 'expired',
    insurance_expiry_date: new Date(Date.now() - 2 * DAY_MS).toISOString()
  },
];

const MOCK_HIRES: Hire[] = [
  { 
    id: 'h1', 
    vehicle_id: 'v1', 
    hire_ref: 'H-101', 
    driver_name: 'John Smith', 
    driver_email: 'john@example.com', 
    driver_phone: '07700900123', 
    start_date: new Date(Date.now() - 1000 * 60 * 60 * 24 * 20).toISOString(), 
    end_date: null, 
    organisation_id: 'mock', 
    vehicles: { vrm: 'LO62 NRE' },
    agreement_signed_at: new Date(Date.now() - 1000 * 60 * 60 * 24 * 21).toISOString(),
    agreement_has_liability_clause: false, // Needs auditing or manual check
    acceptance_type: 'wet_signature'
  },
  { 
    id: 'h2', 
    vehicle_id: 'v2', 
    hire_ref: 'H-102', 
    driver_name: 'Sarah Jones', 
    driver_email: 'sarah@example.com', 
    driver_phone: '07700900456', 
    start_date: new Date(Date.now() - 1000 * 60 * 60 * 24 * 5).toISOString(), 
    end_date: new Date(Date.now() + 1000 * 60 * 60 * 24 * 10).toISOString(), 
    organisation_id: 'mock', 
    vehicles: { vrm: 'KW18 JJS' },
    agreement_url: 'https://example.com/agreements/h102.pdf',
    agreement_signed_at: new Date(Date.now() - 1000 * 60 * 60 * 24 * 6).toISOString(),
    agreement_has_liability_clause: true,
    acceptance_type: 'digital_acceptance'
  }
];

const MOCK_TASKS: Task[] = [
  { id: 't1', title: 'PCN Expiring Soon', description: 'PCN88291 discount period ends in 24h. No driver assigned.', priority: 'High', type: 'expiry', related_id: '1', created_at: new Date().toISOString(), status: 'open' },
  { id: 't2', title: 'Appeal Rejected', description: 'Council rejected appeal for PCN99210. Action required.', priority: 'High', type: 'appeal', related_id: '2', created_at: new Date().toISOString(), status: 'open' },
  { id: 't3', title: 'Verify Batch Upload', description: '5 tickets from "Batch #102" need manual review.', priority: 'Medium', type: 'upload', created_at: new Date().toISOString(), status: 'open' },
];

const MOCK_DRIVERS: Driver[] = [
  { 
    id: 'd1', 
    name: 'John Smith', 
    email: 'john@example.com', 
    phone: '07700900123', 
    license_number: 'SMITH829102L91', 
    address_line1: '123 High Street', 
    city: 'London', 
    postcode: 'SW1A 1AA', 
    risk_score: 'High', 
    total_pcns: 4, 
    active_hires: 1, 
    last_contravention_date: new Date(Date.now() - 1000 * 60 * 60 * 24 * 5).toISOString(),
    license_expiry_date: new Date(Date.now() + 6 * DAY_MS).toISOString(),
    license_issue_country: 'GB',
    license_points: 3,
    license_status: 'valid',
    organisation_id: 'mock' 
  },
  { 
    id: 'd2', 
    name: 'Sarah Jones', 
    email: 'sarah@example.com', 
    phone: '07700900456', 
    license_number: 'JONES119283M12', 
    address_line1: '456 Low Road', 
    city: 'Manchester', 
    postcode: 'M1 1AB', 
    risk_score: 'Low', 
    total_pcns: 0, 
    active_hires: 1, 
    last_contravention_date: undefined,
    license_expiry_date: new Date(Date.now() + 40 * DAY_MS).toISOString(),
    license_issue_country: 'GB',
    license_points: 0,
    license_status: 'valid',
    organisation_id: 'mock' 
  },
  { 
    id: 'd3', 
    name: 'Mike Brown', 
    email: 'mike@example.com', 
    phone: '07700900789', 
    license_number: 'BROWN772819K11', 
    address_line1: '789 Mid Way', 
    city: 'Birmingham', 
    postcode: 'B1 1BB', 
    risk_score: 'Medium', 
    total_pcns: 2, 
    active_hires: 0, 
    last_contravention_date: new Date(Date.now() - 1000 * 60 * 60 * 24 * 45).toISOString(),
    license_expiry_date: new Date(Date.now() - DAY_MS).toISOString(),
    license_issue_country: 'GB',
    license_points: 6,
    license_status: 'expired',
    organisation_id: 'mock' 
  },
];

const MOCK_DAILY_CHECKS: DailyCheck[] = [];
const MOCK_DEFECTS: Defect[] = [];
const MOCK_DEFECT_ACTIONS: DefectAction[] = [];

type CsvImportResult = { success: number; merged?: number; errors: string[] };
type ImportProgress = { percent: number; message: string };

const clamp = (n: number, min: number, max: number) => Math.max(min, Math.min(max, n));

const readFileAsText = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read file'));
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.readAsText(file);
  });

// Minimal RFC4180-ish CSV parser (supports quoted fields + embedded commas/newlines)
const parseCsv = (text: string): string[][] => {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQuotes = false;

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];

    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += ch;
      }
      continue;
    }

    if (ch === '"') {
      inQuotes = true;
      continue;
    }

    if (ch === ',') {
      row.push(field);
      field = '';
      continue;
    }

    if (ch === '\r') continue;

    if (ch === '\n') {
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
      continue;
    }

    field += ch;
  }

  row.push(field);
  rows.push(row);

  return rows
    .map((r) => r.map((c) => c.trim()))
    .filter((r) => r.some((c) => c.length > 0));
};

const normalizeKey = (raw: string) =>
  raw
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

const normalizeVrm = (raw: string) => raw.replace(/\s+/g, '').toUpperCase();

const vrmVariantsForLookup = (raw: string) => {
  const variants = new Set<string>();
  const trimmed = String(raw || '').trim();
  if (trimmed) variants.add(trimmed.toUpperCase());
  const norm = normalizeVrm(trimmed);
  if (norm) variants.add(norm);
  if (/^[A-Z]{2}[0-9]{2}[A-Z]{3}$/.test(norm)) {
    variants.add(`${norm.slice(0, 4)} ${norm.slice(4)}`);
  }
  return Array.from(variants).filter(Boolean);
};

const isLikelyUkVrm = (raw: string) => {
  const vrm = normalizeVrm(raw);
  if (!/^[A-Z0-9]{5,8}$/.test(vrm)) return false;
  // Current format: AB12CDE; allow common legacy formats too
  const current = /^[A-Z]{2}[0-9]{2}[A-Z]{3}$/.test(vrm);
  const legacy = /^[A-Z]{1,3}[0-9]{1,4}[A-Z]{0,3}$/.test(vrm);
  return current || legacy;
};

const ensureVehiclesForVrms = async (orgId: string, vrms: string[]) => {
  const want = new Set<string>();
  const lookup = new Set<string>();
  for (const raw of vrms) {
    if (!raw) continue;
    if (!isLikelyUkVrm(raw)) continue;
    const norm = normalizeVrm(raw);
    if (!norm) continue;
    want.add(norm);
    for (const v of vrmVariantsForLookup(raw)) lookup.add(v);
  }

  const byNorm = new Map<string, string>();
  if (!want.size) return byNorm;

  const lookupArr = Array.from(lookup);
  for (let i = 0; i < lookupArr.length; i += 100) {
    const chunk = lookupArr.slice(i, i + 100);
    const { data, error } = await supabase
      .from('vehicles')
      .select('id, vrm')
      .eq('organisation_id', orgId)
      .in('vrm', chunk);
    if (error) throw error;
    for (const row of data || []) {
      const norm = normalizeVrm((row as any).vrm);
      if (!byNorm.has(norm)) byNorm.set(norm, String((row as any).id));
    }
  }

  const toInsert = Array.from(want)
    .filter((norm) => !byNorm.has(norm))
    .map((norm) => ({
      organisation_id: orgId,
      vrm: norm,
      status: 'Active',
      tags: [],
      notes: ''
    }));

  for (let i = 0; i < toInsert.length; i += 100) {
    const chunk = toInsert.slice(i, i + 100);
    const { data, error } = await supabase
      .from('vehicles')
      .insert(chunk as any)
      .select('id, vrm');

    if (error) {
      // Likely a race/unique constraint; re-read and continue.
      const msg = String((error as any)?.message || '');
      if ((error as any)?.code === '23505' || msg.includes('vehicles_organisation_id_vrm_key')) {
        // best-effort refresh for all wanted norms
        const wantArr = Array.from(want);
        for (let j = 0; j < wantArr.length; j += 100) {
          const wantChunk = wantArr.slice(j, j + 100);
          const variants = new Set<string>();
          for (const w of wantChunk) {
            variants.add(w);
            if (/^[A-Z]{2}[0-9]{2}[A-Z]{3}$/.test(w)) variants.add(`${w.slice(0, 4)} ${w.slice(4)}`);
          }
          const { data: refreshed, error: refreshErr } = await supabase
            .from('vehicles')
            .select('id, vrm')
            .eq('organisation_id', orgId)
            .in('vrm', Array.from(variants));
          if (refreshErr) throw refreshErr;
          for (const row of refreshed || []) {
            const norm = normalizeVrm((row as any).vrm);
            if (!byNorm.has(norm)) byNorm.set(norm, String((row as any).id));
          }
        }
        return byNorm;
      }
      throw error;
    }

    for (const row of data || []) {
      const norm = normalizeVrm((row as any).vrm);
      if (!byNorm.has(norm)) byNorm.set(norm, String((row as any).id));
    }
  }

  return byNorm;
};

const parseUkDateToIso = (raw: string): string | null => {
  const s = (raw || '').trim();
  if (!s) return null;

  // YYYY-MM-DD (ISO format)
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const d = new Date(`${s}T00:00:00Z`);
    if (!Number.isFinite(d.getTime())) return null;

    // Validate the parsed date matches input (catches invalid dates like 2025-02-31)
    const [year, month, day] = s.split('-').map(Number);
    if (d.getUTCFullYear() !== year || d.getUTCMonth() !== month - 1 || d.getUTCDate() !== day) {
      return null;
    }

    return d.toISOString();
  }

  // DD/MM/YYYY or DD-MM-YYYY (UK format)
  const m = s.match(/^(\d{2})[\/-](\d{2})[\/-](\d{4})$/);
  if (m) {
    const dd = Number(m[1]);
    const mm = Number(m[2]);
    const yyyy = Number(m[3]);

    // Validate ranges before creating Date
    if (dd < 1 || dd > 31 || mm < 1 || mm > 12 || yyyy < 1900 || yyyy > 2100) {
      return null;
    }

    const d = new Date(Date.UTC(yyyy, mm - 1, dd, 0, 0, 0));
    if (!Number.isFinite(d.getTime())) return null;

    // Reject impossible dates (e.g., 31/02/2025)
    if (d.getUTCFullYear() !== yyyy || d.getUTCMonth() !== mm - 1 || d.getUTCDate() !== dd) {
      return null;
    }

    return d.toISOString();
  }

  // SECURITY FIX: Removed unsafe Date.parse() fallback
  // Only accept explicit UK or ISO date formats to prevent ambiguous parsing
  // Reject any other format to ensure data integrity
  console.warn(`Invalid date format rejected: "${s}". Expected DD/MM/YYYY or YYYY-MM-DD`);
  return null;
};

const parsePounds = (raw: string): number | null => {
  const cleaned = (raw || '')
    .toString()
    .trim()
    .replace(/£/g, '')
    .replace(/,/g, '');
  if (!cleaned) return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
};

type TicketCreateAction = 'created' | 'merged';
type TicketCreateResult = {
  ticket: Ticket;
  action: TicketCreateAction;
  audit?: AuditResult; // Audit result from auto-assignment evaluation
};
type TicketBatchResult = {
  created: number;
  merged: number;
  tickets: Ticket[];
  audits?: Map<string, AuditResult>; // Map of ticket ID to audit result
};

const normalizePcnNumber = (raw?: string | null) => {
  const value = String(raw ?? '').trim();
  if (!value) return null;
  return value.toUpperCase();
};

const normalizeTicketStage = (raw?: string | null, enforcementType: EnforcementType = 'statutory') => {
  return normalizeStage(raw, enforcementType);
};

const stageRank = (stage?: string | null, enforcementType: EnforcementType = 'statutory') => {
  const normalized = normalizeTicketStage(stage, enforcementType);
  const map: Record<string, number> = {
    'PCN Issued': 1,
    'Notice to Owner': 2,
    'Charge Certificate': 3,
    'Order for Recovery': 4,
    'Parking Charge Notice': 1,
    'Notice to Driver': 1,
    'Notice to Keeper': 1,
    'Reminder': 2,
    'Final Reminder': 3,
    'Debt Recovery': 4,
    'Letter Before Claim': 5,
    'Court Claim': 6
  };
  return map[normalized] ?? 0;
};

const parseMaybeTimeline = (raw: any): any[] => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

const uniqStrings = (arr: any[]) => Array.from(new Set(arr.map((v) => String(v)).filter(Boolean)));

// Helper functions for human-readable labels
const getNoHireReasonLabel = (reason: NoHireReason): string => {
  const labels: Record<NoHireReason, string> = {
    hire_not_yet_uploaded: 'Hire record not yet uploaded',
    vehicle_company_owned: 'Vehicle was company-owned at the time',
    third_party_error: 'Third-party error',
    other: 'Other'
  };
  return labels[reason] || reason;
};

const getOverrideReasonLabel = (reason: OverrideReason): string => {
  const labels: Record<OverrideReason, string> = {
    wrong_hire_detected: 'Wrong hire detected',
    hire_dates_incorrect: 'Hire dates incorrect',
    manual_assignment_preferred: 'Manual assignment preferred',
    system_error: 'System error',
    other: 'Other'
  };
  return labels[reason] || reason;
};

export const ApiService = {
  // --- AI Action Telemetry ---
  // Fixed FleetMode plans do not meter-bill customers for AI extraction,
  // appeal generation, or liability transfer actions. These records remain
  // internal telemetry for audit, support, abuse prevention, and cost controls.

  /**
   * Record AI extraction telemetry after successful AI extraction.
   */
  async billAIExtraction(ticketId: string, metadata?: Record<string, any>): Promise<string | null> {
    if (isMockMode) {
      console.warn('🚨 BILLING SKIPPED: Running in mock mode. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in .env');
      return null;
    }
    try {
      console.log('💰 Billing AI extraction for ticket:', ticketId);
      const { UsageTrackingService } = await import('./usageTracking');
      const result = await UsageTrackingService.recordBillableAction(ticketId, 'ai_extraction', metadata);
      console.log('💰 Billing result:', result);
      return result.success ? result.usageRecordId || null : null;
    } catch (e) {
      console.error('❌ Failed to bill AI extraction:', e);
      return null;
    }
  },

  /**
   * Record appeal generation telemetry after successful appeal generation.
   */
  async billAppealGeneration(ticketId: string, metadata?: Record<string, any>): Promise<string | null> {
    if (isMockMode) return null;
    try {
      const { UsageTrackingService } = await import('./usageTracking');
      const result = await UsageTrackingService.recordBillableAction(ticketId, 'appeal_generation', metadata);
      return result.success ? result.usageRecordId || null : null;
    } catch (e) {
      console.warn('Failed to bill appeal generation:', e);
      return null;
    }
  },

  /**
   * Record liability transfer telemetry after successful pack generation.
   */
  async billLiabilityTransfer(ticketId: string, metadata?: Record<string, any>): Promise<string | null> {
    if (isMockMode) return null;
    try {
      const { UsageTrackingService } = await import('./usageTracking');
      const result = await UsageTrackingService.recordBillableAction(ticketId, 'liability_transfer', metadata);
      return result.success ? result.usageRecordId || null : null;
    } catch (e) {
      console.warn('Failed to bill liability transfer:', e);
      return null;
    }
  },

  /**
   * Mark AI action telemetry complete after successful processing.
   */
  async completeBilling(usageRecordId: string): Promise<boolean> {
    if (isMockMode) {
      console.warn('🚨 BILLING COMPLETION SKIPPED: Running in mock mode');
      return true;
    }
    try {
      console.log('💰 Completing billing for usage record:', usageRecordId);
      const { UsageTrackingService } = await import('./usageTracking');
      const result = await UsageTrackingService.completeAndBill(usageRecordId);
      console.log('💰 Billing completion result:', result);
      return result;
    } catch (e) {
      console.error('❌ Failed to complete billing:', e);
      return false;
    }
  },

  /**
   * Count PCNs toward the FleetMode trial after AI extraction has completed and
   * the user has accepted/saved the extracted key fields.
   */
  async incrementFleetTrialProcessed(processedCount: number): Promise<{
    trial_status: string;
    trial_pcns_processed: number;
    trial_pcn_limit: number;
    trial_expires_at: string | null;
    expired_by: 'time' | 'pcn_limit' | 'manual_or_existing' | null;
  } | null> {
    if (isMockMode || processedCount <= 0) return null;

    const { data, error } = await supabase.rpc('increment_fleetmode_trial_processed', {
      p_processed_count: processedCount,
    });
    if (error) throw error;

    const row = Array.isArray(data) ? data[0] : data;
    return row ?? null;
  },

  async getFleetTrialState(): Promise<FleetTrialState | null> {
    if (isMockMode) {
      return {
        subscription_tier: 'starter',
        commercial_status: 'trial',
        trial_status: 'active',
        trial_pcns_processed: 1,
        trial_pcn_limit: 3,
        trial_expires_at: new Date(Date.now() + 86400000 * 4).toISOString(),
        trial_extension_status: 'none',
        expired_by: null,
        locked: false,
      };
    }

    const normalize = (row: any): FleetTrialState | null => {
      if (!row) return null;
      const processed = Number(row.trial_pcns_processed ?? 0);
      const limit = Number(row.trial_pcn_limit ?? 3);
      const expiresAt = row.trial_expires_at || row.trial_ends_at || null;
      const expiresAtMs = expiresAt ? new Date(expiresAt).getTime() : null;
      const expiredBy =
        row.expired_by ??
        (typeof expiresAtMs === 'number' && Number.isFinite(expiresAtMs) && expiresAtMs <= Date.now()
          ? 'time'
          : processed >= limit
            ? 'pcn_limit'
            : row.trial_status === 'expired'
              ? 'manual_or_existing'
              : null);
      const paid = row.commercial_status === 'paid' || !['starter', 'trial'].includes(String(row.subscription_tier || ''));
      return {
        subscription_tier: row.subscription_tier ?? null,
        commercial_status: row.commercial_status ?? null,
        trial_status: row.trial_status ?? null,
        trial_pcns_processed: processed,
        trial_pcn_limit: limit,
        trial_expires_at: expiresAt,
        trial_extension_status: row.trial_extension_status ?? null,
        expired_by: expiredBy,
        locked: Boolean(row.locked ?? (!paid && Boolean(expiredBy))),
      };
    };

    try {
      const { data, error } = await supabase.rpc('sync_fleetmode_trial_status');
      if (error) throw error;
      return normalize(Array.isArray(data) ? data[0] : data);
    } catch (rpcError) {
      console.warn('Unable to sync FleetMode trial state; falling back to direct read:', rpcError);
      const orgId = await getCurrentOrganisationId();
      const { data, error } = await supabase
        .from('organisations')
        .select('subscription_tier, commercial_status, trial_status, trial_ends_at, trial_expires_at, trial_pcn_limit, trial_pcns_processed, trial_extension_status')
        .eq('id', orgId)
        .maybeSingle();
      if (error) throw error;
      return normalize(data);
    }
  },

  async isCurrentUserInternalStaff(requiredRoles: string[] = ['sales', 'support', 'admin']): Promise<boolean> {
    if (isMockMode) return true;
    try {
      const { data, error } = await supabase.rpc('is_internal_staff', {
        required_roles: requiredRoles,
      });
      if (error) {
        console.warn('Unable to check internal staff access:', error);
        return false;
      }
      return data === true;
    } catch (error) {
      console.warn('Unable to check internal staff access:', error);
      return false;
    }
  },

  async getFleetLeadDashboard(): Promise<FleetLeadDashboardData> {
    if (isMockMode) {
      const now = new Date();
      const soon = new Date(now.getTime() + 4 * 60 * 1000).toISOString();
      const old = new Date(now.getTime() - 18 * 60 * 1000).toISOString();
      const leads: FleetLead[] = [
        {
          id: 'lead-demo-1',
          organisation_id: 'org-demo-1',
          contact_name: 'Amara Collins',
          email: 'ops@northstarpco.example',
          phone: '07123 456789',
          company_name: 'Northstar PCO Hire',
          source: 'fleetpage',
          lead_score: 72,
          lead_owner: 'tobi',
          first_response_due_at: soon,
          sla_status: 'pending',
          next_action: 'Help process first PCN',
          last_activity_at: now.toISOString(),
          status: 'active_trial',
          notes: 'Runs 18 vehicles and charges drivers an admin fee.',
          created_at: now.toISOString(),
        },
        {
          id: 'lead-demo-2',
          organisation_id: 'org-demo-2',
          contact_name: 'Michael Adeyemi',
          email: 'director@cityfleet.example',
          phone: '07900 111222',
          company_name: 'CityFleet Rentals',
          source: 'pricing',
          lead_score: 91,
          lead_owner: 'joshua',
          first_response_due_at: old,
          sla_status: 'missed',
          next_action: 'Call about Growth plan fit',
          last_activity_at: old,
          status: 'qualified',
          notes: 'Viewed pricing after completing snapshot.',
          created_at: old,
        },
      ];
      const events: FleetLeadEvent[] = [
        {
          id: 'event-demo-1',
          organisation_id: 'org-demo-1',
          event_type: 'first_pcn_uploaded',
          source_page: 'ai-orchestrator',
          metadata_json: { token_cost_gbp: 0.02 },
          created_at: now.toISOString(),
        },
        {
          id: 'event-demo-2',
          organisation_id: 'org-demo-2',
          event_type: 'pricing_viewed',
          source_page: 'pricing',
          metadata_json: { plan: 'growth' },
          created_at: old,
        },
      ];
      return { isInternalStaff: true, leads, events };
    }

    const isInternalStaff = await ApiService.isCurrentUserInternalStaff();
    if (!isInternalStaff) return { isInternalStaff: false, leads: [], events: [] };

    const { data, error } = await supabase.rpc('get_fleetmode_lead_dashboard', {
      p_lead_limit: 250,
      p_event_limit: 1000,
    });
    if (error) throw error;

    const dashboard = (data || {}) as Partial<FleetLeadDashboardData>;

    return {
      isInternalStaff: true,
      leads: (dashboard.leads || []) as FleetLead[],
      events: (dashboard.events || []) as FleetLeadEvent[],
    };
  },

  async updateFleetLead(
    leadId: string,
    patch: Partial<Pick<FleetLead, 'lead_owner' | 'status' | 'next_action' | 'notes' | 'sla_status' | 'first_response_at'>>
  ): Promise<FleetLead> {
    if (isMockMode) {
      return {
        id: leadId,
        organisation_id: 'mock',
        company_name: 'Demo Fleet Ltd',
        lead_score: 0,
        sla_status: 'pending',
        status: (patch.status as FleetLeadStatus) || 'active_trial',
        created_at: new Date().toISOString(),
        ...patch,
      } as FleetLead;
    }

    const { data, error } = await supabase.rpc('update_fleetmode_lead', {
      p_lead_id: leadId,
      p_patch: patch,
    });
    if (error) throw error;
    return data as FleetLead;
  },

  async recordFleetModeLeadEvent(
    eventType: string,
    sourcePage: string,
    metadata: Record<string, any> = {}
  ): Promise<void> {
    if (isMockMode) {
      console.log('[FleetMode lead event]', { eventType, sourcePage, metadata });
      return;
    }

    const { error } = await supabase.rpc('record_fleetmode_lead_event', {
      p_event_type: eventType,
      p_source_page: sourcePage,
      p_metadata_json: metadata,
    });

    if (error) {
      console.warn('Unable to record FleetMode lead event:', error);
      throw error;
    }
  },

  /**
   * Mark billing as failed (no charge)
   */
  async failBilling(usageRecordId: string, reason: string): Promise<boolean> {
    if (isMockMode) return true;
    try {
      const { UsageTrackingService } = await import('./usageTracking');
      return await UsageTrackingService.markAsFailed(usageRecordId, reason);
    } catch (e) {
      console.warn('Failed to mark billing as failed:', e);
      return false;
    }
  },

  /**
   * Cancel billing within grace period
   */
  async cancelBilling(usageRecordId: string): Promise<boolean> {
    if (isMockMode) return true;
    try {
      const { UsageTrackingService } = await import('./usageTracking');
      return await UsageTrackingService.cancelWithinGracePeriod(usageRecordId);
    } catch (e) {
      console.warn('Failed to cancel billing:', e);
      return false;
    }
  },

  /**
   * Get billable usage breakdown for current period
   * Only counts the 3 billable actions: AI extraction, appeal generation, liability transfer
   */
  async getBillableUsage(period: 'month' | 'quarter') {
    const days = period === 'quarter' ? 90 : 30;
    const since = new Date(Date.now() - days * 86400000).toISOString();

    if (isMockMode) {
      return {
        ai_extraction: 0,
        appeal_generation: 0,
        liability_transfer: 0,
        total: 0
      };
    }

    const orgId = await getCurrentOrganisationId();
    const { data, error } = await supabase
      .from('usage_records')
      .select('billable_action, status')
      .eq('organisation_id', orgId)
      .gte('created_at', since)
      // Count both pending + completed to reflect actions performed this period (Stripe reporting may occur later).
      .in('status', ['pending', 'completed'])
      .limit(5000);

    if (error) {
      console.error('Error fetching billable usage:', error);
      return {
        ai_extraction: 0,
        appeal_generation: 0,
        liability_transfer: 0,
        total: 0
      };
    }

    const usage = {
      ai_extraction: 0,
      appeal_generation: 0,
      liability_transfer: 0,
      total: 0
    };

    for (const row of data || []) {
      const action = (row as any).billable_action as string;
      if (action === 'ai_extraction') usage.ai_extraction++;
      else if (action === 'appeal_generation') usage.appeal_generation++;
      else if (action === 'liability_transfer') usage.liability_transfer++;
    }

    usage.total = usage.ai_extraction + usage.appeal_generation + usage.liability_transfer;
    return usage;
  },

  async getUsageSummary(period: 'month' | 'quarter') {
    const days = period === 'quarter' ? 90 : 30;
    const since = new Date(Date.now() - days * 86400000).toISOString();

    if (isMockMode) {
      return {
        since,
        total: 0,
        byType: {
          ticket_created: 0,
          ticket_uploaded: 0,
          ai_extraction_used: 0,
          appeal_generated: 0
        },
        unbilled: 0
      };
    }

    const orgId = await getCurrentOrganisationId();
    const { data, error } = await supabase
      .from('usage_events')
      .select('event_type, billed, created_at')
      .eq('organisation_id', orgId)
      .gte('created_at', since)
      .limit(5000);

    if (error) throw error;

    const byType: Record<string, number> = {};
    let unbilled = 0;
    for (const row of data || []) {
      const type = (row as any).event_type as string;
      byType[type] = (byType[type] || 0) + 1;
      if (!(row as any).billed) unbilled++;
    }

    return {
      since,
      total: (data || []).length,
      byType,
      unbilled
    };
  },

  async mergeDuplicateTicket(existing: any, incoming: Partial<Ticket>, source: string): Promise<Ticket> {
    const existingTimeline = parseMaybeTimeline((existing as any)?.timeline);
    const oldStage = String((existing as any)?.stage ?? '');
    const incomingStage = String(incoming.stage ?? '');
    const incomingType = inferEnforcementType({
      issuing_authority: incoming.issuing_authority,
      document_type: incoming.document_type,
      document_intent: incoming.document_intent,
      key_phrases: (incoming as any)?.key_phrases,
      stage: incomingStage
    });
    const existingType = inferEnforcementType({
      issuing_authority: (existing as any)?.issuing_authority,
      document_type: (existing as any)?.document_type,
      document_intent: (existing as any)?.document_intent,
      key_phrases: (existing as any)?.key_phrases,
      stage: oldStage
    });
    const hasIncomingSignals = Boolean(
      incoming.issuing_authority || incoming.document_type || incoming.document_intent || incomingStage
    );
    const enforcementType = hasIncomingSignals ? incomingType : existingType;

    const oldStageRank = stageRank(oldStage, enforcementType);
    const incomingStageRank = stageRank(incomingStage, enforcementType);
    const defaultStage = enforcementType === 'private' ? 'Parking Charge Notice' : 'PCN Issued';
    const nextStage = normalizeTicketStage(
      (incomingStageRank > oldStageRank ? incomingStage : oldStage) || defaultStage,
      enforcementType
    );

    const terminalStatuses = new Set(['paid', 'cancelled', 'appeal_won', 'appeal_lost']);
    const existingStatus = String((existing as any)?.status ?? 'unassigned');
    const nextStatus =
      terminalStatuses.has(existingStatus)
        ? existingStatus
        : existingStatus === 'unassigned' && incomingStageRank > oldStageRank
          ? 'active'
          : existingStatus;

    const stageChanged = nextStage && normalizeTicketStage(oldStage, enforcementType) !== nextStage;
    const amountIncoming = typeof incoming.amount === 'number' ? incoming.amount : null;
    const amountExisting = typeof (existing as any)?.amount === 'number' ? (existing as any).amount : null;
    const shouldUpdateAmount =
      amountIncoming !== null &&
      (amountExisting === null ||
        amountExisting <= 0 ||
        (incomingStageRank >= oldStageRank && amountIncoming > amountExisting));

    const discountIncoming = typeof incoming.discount_amount === 'number' ? incoming.discount_amount : null;
    const discountExisting = typeof (existing as any)?.discount_amount === 'number' ? (existing as any).discount_amount : null;
    const shouldUpdateDiscount =
      discountIncoming !== null &&
      (discountExisting === null ||
        discountExisting <= 0 ||
        (incomingStageRank >= oldStageRank && discountIncoming > discountExisting));

    const lowConfidenceNext = uniqStrings([
      ...((existing as any)?.low_confidence_fields ?? []),
      ...(incoming.low_confidence_fields ?? [])
    ]);

    const event: TimelineEvent = {
      id: `t-${Date.now()}`,
      date: new Date().toISOString(),
      title: 'Duplicate merged',
      description:
        `A duplicate ticket was detected and merged (${source}).` +
        (stageChanged ? ` Stage updated: ${normalizeTicketStage(oldStage, enforcementType) || '—'} → ${nextStage}.` : ''),
      type: 'system'
    };

    const nextTimeline = [...existingTimeline, event];

    const updatePayload: any = {
      stage: nextStage || (existing as any)?.stage,
      status: nextStatus,
      updated_at: new Date().toISOString(),
      timeline: nextTimeline,
      low_confidence_fields: lowConfidenceNext
    };

    // If the existing ticket isn't linked to a vehicle yet, create/link based on VRN.
    if (!(existing as any)?.vehicle_id && incoming.vrn && isLikelyUkVrm(String(incoming.vrn))) {
      try {
        const orgId = await getCurrentOrganisationId();
        const vehicleMap = await ensureVehiclesForVrms(orgId, [String(incoming.vrn)]);
        updatePayload.vehicle_id = vehicleMap.get(normalizeVrm(String(incoming.vrn))) ?? null;
      } catch (e) {
        console.warn('Non-blocking: failed to link vehicle during merge:', e);
      }
    }

    if (shouldUpdateAmount) updatePayload.amount = amountIncoming;
    if (shouldUpdateDiscount) updatePayload.discount_amount = discountIncoming;

    if (!(existing as any)?.location && incoming.location) updatePayload.location = incoming.location;
    if (!(existing as any)?.issuing_authority && incoming.issuing_authority) updatePayload.issuing_authority = incoming.issuing_authority;
    if (!(existing as any)?.contravention_type && incoming.contravention_type) updatePayload.contravention_type = incoming.contravention_type;
    if (!(existing as any)?.document_url && incoming.document_url) updatePayload.document_url = incoming.document_url;
    if (!(existing as any)?.file_hash && ((incoming as any).ai_file_hash || (incoming as any).file_hash)) {
      updatePayload.file_hash = (incoming as any).ai_file_hash || (incoming as any).file_hash;
    }
    updatePayload.extraction_status = (incoming as any).ai_cache_hit ? 'duplicate_cached' : 'completed';
    updatePayload.user_confirmed = true;
    updatePayload.user_confirmed_at = new Date().toISOString();
    updatePayload.review_status = 'confirmed';

    const orgId = await getCurrentOrganisationId();
    const { data, error } = await supabase
      .from('tickets')
      .update(updatePayload)
      .eq('id', (existing as any).id)
      .eq('organisation_id', orgId)
      .select()
      .single();
    if (error) throw error;
    return data as any;
  },
  // ... (Existing Dashboard, Tickets, etc. methods remain unchanged) ...
  async getDashboardData(period: 'month' | 'quarter'): Promise<DashboardMetrics> {
    if (isMockMode) {
      const daysLookback = period === 'month' ? 30 : 90;
      const generated = generateAnalyticsData(period === 'month' ? 45 : 120);
      const allTickets = [...MOCK_TICKETS, ...generated];
      const now = new Date();
      const cutoff = new Date(now.getTime() - (daysLookback * 24 * 60 * 60 * 1000));
      const periodTickets = allTickets.filter(t => new Date(t.issue_date) >= cutoff);

      let atRisk = 0; 
      let paid = 0;   
      let saved = 0;  

      const statusCounts = { open: 0, paid: 0, appealed: 0, transferred: 0 };
      const driverMap: Record<string, { count: number, cost: number }> = {};
      const vehicleMap: Record<string, number> = {};
      const hotspotMap: Record<string, number> = {};
      const trendMap: Record<string, number> = {};

      periodTickets.forEach(t => {
        const status = t.status.toLowerCase();
        if (status === 'paid') {
          paid += t.amount;
          statusCounts.paid++;
        } else if (status === 'cancelled') {
          saved += t.amount;
        } else if (status === 'transferred' || t.decision === 'transfer') {
          statusCounts.transferred++;
        } else {
          atRisk += t.amount;
          if (status.includes('appeal')) statusCounts.appealed++;
          else statusCounts.open++;
        }

        if (t.assigned_driver_name) {
          if (!driverMap[t.assigned_driver_name]) driverMap[t.assigned_driver_name] = { count: 0, cost: 0 };
          driverMap[t.assigned_driver_name].count++;
          driverMap[t.assigned_driver_name].cost += t.amount;
        }
        if (t.vrn) vehicleMap[t.vrn] = (vehicleMap[t.vrn] || 0) + 1;
        if (t.location) hotspotMap[t.location] = (hotspotMap[t.location] || 0) + 1;
        
        const dateKey = t.issue_date.split('T')[0];
        trendMap[dateKey] = (trendMap[dateKey] || 0) + 1;
      });

      const today = new Date();
      const vehiclesOverdueDocs = MOCK_VEHICLES.filter((vehicle) => {
        const motDays = vehicle.mot_expiry_date ? daysUntilDate(new Date(vehicle.mot_expiry_date), today) : null;
        const taxDays = vehicle.tax_due_date ? daysUntilDate(new Date(vehicle.tax_due_date), today) : null;
        const insuranceDays = vehicle.insurance_expiry_date ? daysUntilDate(new Date(vehicle.insurance_expiry_date), today) : null;
        return (motDays !== null && motDays < 0) || (taxDays !== null && taxDays < 0) || (insuranceDays !== null && insuranceDays < 0);
      }).length;
      const driversLicenceRisk = MOCK_DRIVERS.filter((driver) => {
        const due = parseDateOnly(driver.license_expiry_date);
        if (!due) return false;
        return daysUntilDate(due, today) <= 30;
      }).length;
      const criticalBreachesOpen = MOCK_TASKS.filter((task) => {
        const severity = String(task.severity || '').toLowerCase();
        return task.status === 'open' && severity === 'critical';
      }).length;

      return {
        totalTickets: periodTickets.length,
        financials: { atRisk, paid, saved },
        byStage: statusCounts,
        topDrivers: Object.entries(driverMap).map(([name, data]) => ({ name, ...data })).sort((a, b) => b.count - a.count).slice(0, 10),
        topVehicles: Object.entries(vehicleMap).map(([vrn, count]) => ({ vrn, count })).sort((a, b) => b.count - a.count).slice(0, 10),
        hotspots: Object.entries(hotspotMap).map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count).slice(0, 10),
        trendData: Object.entries(trendMap).map(([date, count]) => ({ date, count })).sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()),
        compliance: {
          vehiclesOverdueDocs,
          driversLicenceRisk,
          criticalBreachesOpen
        }
      };
    }

    // Real Supabase implementation (lightweight aggregation in JS)
    try {
      const orgId = await getCurrentOrganisationId();
      const daysLookback = period === 'month' ? 30 : 90;
      const cutoffIso = new Date(Date.now() - daysLookback * 24 * 60 * 60 * 1000).toISOString();

      const { data, error } = await supabase
        .from('tickets')
        .select(`
          status,
          amount,
          decision,
          vrn,
          location,
          issue_date,
          created_at,
          hires (
            driver_name,
            drivers ( name )
          )
        `)
        .eq('organisation_id', orgId)
        .gte('created_at', cutoffIso)
        .order('created_at', { ascending: false })
        .limit(5000);

      if (error) throw error;

      const tickets = (data || []) as any[];

      let atRisk = 0;
      let paid = 0;
      let saved = 0;

      const byStage = { open: 0, paid: 0, appealed: 0, transferred: 0 };
      const driverMap: Record<string, { count: number; cost: number }> = {};
      const vehicleMap: Record<string, number> = {};
      const hotspotMap: Record<string, number> = {};
      const trendMap: Record<string, number> = {};

      for (const t of tickets) {
        const status = String(t.status || '').toLowerCase();
        const amount = Number(t.amount || 0) || 0;
        const decision = String(t.decision || '').toLowerCase();

        if (status === 'paid') {
          paid += amount;
          byStage.paid++;
        } else if (status === 'cancelled' || status === 'appeal_won') {
          saved += amount;
        } else if (status === 'transferred' || decision === 'transfer') {
          byStage.transferred++;
        } else {
          atRisk += amount;
          if (status.includes('appeal')) byStage.appealed++;
          else byStage.open++;
        }

        const hire = Array.isArray(t.hires) ? t.hires[0] : t.hires;
        const driversRel = hire?.drivers;
        const driverRel = Array.isArray(driversRel) ? driversRel[0] : driversRel;
        const driver = String(driverRel?.name || hire?.driver_name || '').trim();
        if (driver) {
          if (!driverMap[driver]) driverMap[driver] = { count: 0, cost: 0 };
          driverMap[driver].count++;
          driverMap[driver].cost += amount;
        }

        const vrn = String(t.vrn || '').trim();
        if (vrn) vehicleMap[vrn] = (vehicleMap[vrn] || 0) + 1;

        const location = String(t.location || '').trim();
        if (location) hotspotMap[location] = (hotspotMap[location] || 0) + 1;

        // Use created_at for trend data (when ticket was added to system)
        const createdDate = String(t.created_at || t.issue_date || '');
        const dateKey = createdDate ? createdDate.split('T')[0] : '';
        if (dateKey) trendMap[dateKey] = (trendMap[dateKey] || 0) + 1;
      }

      const compliance = await ApiService.getComplianceCounts().catch(() => ({
        vehiclesOverdueDocs: 0,
        driversLicenceRisk: 0,
        criticalBreachesOpen: 0
      }));

      return {
        totalTickets: tickets.length,
        financials: { atRisk, paid, saved },
        byStage,
        topDrivers: Object.entries(driverMap)
          .map(([name, d]) => ({ name, ...d }))
          .sort((a, b) => b.count - a.count)
          .slice(0, 10),
        topVehicles: Object.entries(vehicleMap)
          .map(([vrn, count]) => ({ vrn, count }))
          .sort((a, b) => b.count - a.count)
          .slice(0, 10),
        hotspots: Object.entries(hotspotMap)
          .map(([name, count]) => ({ name, count }))
          .sort((a, b) => b.count - a.count)
          .slice(0, 10),
        trendData: Object.entries(trendMap)
          .map(([date, count]) => ({ date, count }))
          .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()),
        compliance
      };
    } catch (e) {
      console.error('Failed to load dashboard data:', e);
      throw e;
    }
  },

  async getRecentActivity(limit = 15): Promise<RecentActivityItem[]> {
    const clampLimit = Math.max(1, Math.min(50, Number(limit) || 15));

    if (isMockMode) {
      const now = Date.now();
      const mockActivity: RecentActivityItem[] = [
        {
          id: 'mock-1',
          kind: 'transfer',
          action: 'Liability Transferred',
          desc: 'PCN88291 transferred to John Smith',
          at: new Date(now - 2 * 60 * 60 * 1000).toISOString()
        },
        {
          id: 'mock-2',
          kind: 'appeal',
          action: 'Appeal Rejected',
          desc: 'Camden Council • PCN99210',
          at: new Date(now - 5 * 60 * 60 * 1000).toISOString()
        },
        {
          id: 'mock-3',
          kind: 'upload',
          action: 'Batch Upload',
          desc: 'Imported 12 new tickets',
          at: new Date(now - 28 * 60 * 60 * 1000).toISOString()
        }
      ];
      return mockActivity.slice(0, clampLimit);
    }

    try {
      const orgId = await getCurrentOrganisationId();

      const { data, error } = await supabase
        .from('tickets')
        .select('id, pcn_number, issuing_authority, created_at, updated_at, status, decision, timeline')
        .eq('organisation_id', orgId)
        .order('updated_at', { ascending: false })
        .limit(200);

      if (error) throw error;

      const candidates: RecentActivityItem[] = [];
      const tickets = (data || []) as any[];

      const toKind = (title: string, description: string): RecentActivityItem['kind'] => {
        const haystack = `${title} ${description}`.toLowerCase();
        if (haystack.includes('transfer') || haystack.includes('liability')) return 'transfer';
        if (haystack.includes('appeal')) return 'appeal';
        if (haystack.includes('upload') || haystack.includes('import')) return 'upload';
        if (haystack.includes('paid') || haystack.includes('invoice') || haystack.includes('billing')) return 'payment';
        if (haystack.includes('created')) return 'ticket';
        return 'system';
      };

      for (const t of tickets) {
        const ticketId = String(t.id);
        const pcn = String(t.pcn_number || '').trim();
        const authority = String(t.issuing_authority || '').trim();

        const timeline = parseMaybeTimeline(t.timeline);
        for (const raw of timeline) {
          const at = String(raw?.date || '');
          const when = new Date(at);
          if (Number.isNaN(when.getTime())) continue;

          const title = String(raw?.title || 'Activity').trim() || 'Activity';
          const description = String(raw?.description || '').trim();
          const desc = [pcn ? `PCN ${pcn}` : '', authority ? authority : '', description]
            .filter(Boolean)
            .join(' • ');

          candidates.push({
            id: `t-${ticketId}-${String(raw?.id || when.getTime())}`,
            kind: toKind(title, description),
            action: title,
            desc: desc || (pcn ? `PCN ${pcn}` : 'Ticket activity'),
            at: when.toISOString(),
            ticketId
          });
        }

        if (timeline.length === 0) {
          const at = String(t.created_at || t.updated_at || '');
          const when = new Date(at);
          if (!Number.isNaN(when.getTime())) {
            candidates.push({
              id: `t-${ticketId}-created`,
              kind: 'ticket',
              action: 'Ticket Added',
              desc: [pcn ? `PCN ${pcn}` : '', authority ? authority : ''].filter(Boolean).join(' • ') || 'Ticket added',
              at: when.toISOString(),
              ticketId
            });
          }
        }
      }

      // Newest first + de-dupe noisy entries
      candidates.sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());
      const seen = new Set<string>();
      const unique: RecentActivityItem[] = [];
      for (const item of candidates) {
        const key = `${item.ticketId || ''}:${item.action}:${item.at}`;
        if (seen.has(key)) continue;
        seen.add(key);
        unique.push(item);
        if (unique.length >= clampLimit) break;
      }

      return unique;
    } catch (e) {
      console.error('Failed to load recent activity:', e);
      return [];
    }
  },

  async getTickets(params: any = {}) {
    const isActionRequiredMode = params.status === 'action_required';
    const page = Math.max(1, Number(params.page) || 1);
    const limit = Math.max(1, Math.min(100, Number(params.limit) || 10));
    const rangeStart = (page - 1) * limit;
    const rangeEnd = rangeStart + limit - 1;

    const sortBy = params.sortBy || 'created_at';
    const sortAscending = params.sortOrder === 'asc';

    const sortRows = <T extends Record<string, any>>(rows: T[]) =>
      [...rows].sort((a, b) => {
        const valA = a?.[sortBy];
        const valB = b?.[sortBy];

        if (typeof valA === 'number' && typeof valB === 'number') {
          return sortAscending ? valA - valB : valB - valA;
        }

        const strA = String(valA ?? '').toLowerCase();
        const strB = String(valB ?? '').toLowerCase();
        if (strA < strB) return sortAscending ? -1 : 1;
        if (strA > strB) return sortAscending ? 1 : -1;
        return 0;
      });

    if (isMockMode) {
      let data = [...MOCK_TICKETS];
      if (params.status && params.status !== 'All') {
        if (params.status === 'unassigned_only') data = data.filter(t => normalizeTicketStatus(t.status) === 'unassigned');
        else if (params.status === 'action_required') data = data.filter(isActionRequiredTicket);
        else data = data.filter(t => normalizeTicketStatus(t.status) === normalizeTicketStatus(params.status));
      }
      if (params.stage && params.stage !== 'All') {
        if (params.stage === 'NtO/Postal PCN') {
          data = data.filter(t => {
            const normalized = normalizeTicketStage(
              t.stage,
              inferEnforcementType({
                issuing_authority: t.issuing_authority,
                document_type: t.document_type,
                document_intent: t.document_intent,
                key_phrases: (t as any)?.key_phrases,
                stage: t.stage
              })
            );
            return normalized === 'PCN Issued' || normalized === 'Notice to Owner' || normalized === 'PCN/NtO';
          });
        } else {
          data = data.filter(t => t.stage === params.stage);
        }
      }
      if (params.search) {
        const s = params.search.toLowerCase();
        data = data.filter(t => t.pcn_number.toLowerCase().includes(s) || t.vrn.toLowerCase().includes(s));
      }
      if (params.createdFrom) {
        const fromTime = new Date(String(params.createdFrom)).getTime();
        if (!Number.isNaN(fromTime)) {
          data = data.filter((t) => {
            const createdTime = new Date(String(t.created_at || t.issue_date || '')).getTime();
            return !Number.isNaN(createdTime) && createdTime >= fromTime;
          });
        }
      }
      if (params.createdTo) {
        const toTime = new Date(String(params.createdTo)).getTime();
        if (!Number.isNaN(toTime)) {
          data = data.filter((t) => {
            const createdTime = new Date(String(t.created_at || t.issue_date || '')).getTime();
            return !Number.isNaN(createdTime) && createdTime < toTime;
          });
        }
      }
      if (params.driver) {
        const d = params.driver.toLowerCase();
        data = data.filter(t => t.assigned_driver_name?.toLowerCase().includes(d) || t.assigned_driver_email?.toLowerCase().includes(d));
      }
      const sorted = sortRows(data);
      const totalCount = sorted.length;
      const pageRows = sorted.slice(rangeStart, rangeStart + limit);
      return { data: pageRows, count: totalCount };
    }

    // Real Supabase implementation
    try {
      const orgId = await getCurrentOrganisationId();

      const applyCommonFilters = (query: any) => {
        // Date filters (Entry date / created_at)
        if (params.createdFrom) query = query.gte('created_at', String(params.createdFrom));
        if (params.createdTo) query = query.lt('created_at', String(params.createdTo));

        if (params.stage && params.stage !== 'All') {
          if (params.stage === 'NtO/Postal PCN') {
            query = query.in('stage', ['PCN Issued', 'Notice to Owner', 'PCN/NtO']);
          } else {
            query = query.eq('stage', params.stage);
          }
        }

        if (params.search) {
          query = query.or(`pcn_number.ilike.%${params.search}%,vrn.ilike.%${params.search}%`);
        }

        if (params.driver) {
          // Search in hire driver name
          query = query.filter('hires.driver_name', 'ilike', `%${params.driver}%`);
        }

        return query;
      };

      // Build query with joins for related data
      const fullTicketSelect = `
          *,
          vehicles(vrm, make, model),
          hires(
            id,
            driver_name,
            start_date,
            end_date,
            drivers(name, email)
          )
        `;

      let query: any = supabase
        .from('tickets')
        .select(fullTicketSelect, { count: 'exact' })
        .eq('organisation_id', orgId);

      query = applyCommonFilters(query);

      // Apply filters
      if (params.status && params.status !== 'All') {
        if (params.status === 'unassigned_only') {
          query = query.eq('status', 'unassigned');
        } else if (params.status === 'action_required') {
          // Apply this in memory to support combined platform + driver settlement rules.
        } else {
          query = query.eq('status', normalizeTicketStatus(params.status));
        }
      }

      // Action required needs full-set evaluation before pagination to keep counts/pages correct.
      if (isActionRequiredMode) {
        let actionCandidatesQuery: any = supabase
          .from('tickets')
          .select(`
            id,
            status,
            hire_id,
            assigned_driver_name,
            assigned_driver_email,
            driver_charge_status,
            pcn_number,
            vrn,
            created_at,
            issue_date,
            contravention_date,
            stage,
            amount,
            issuing_authority,
            hires(driver_name)
          `)
          .eq('organisation_id', orgId);

        actionCandidatesQuery = applyCommonFilters(actionCandidatesQuery);
        const { data: candidateRows, error: candidateError } = await actionCandidatesQuery;
        if (candidateError) {
          console.error('Error fetching action-required candidates:', candidateError);
          throw candidateError;
        }

        const actionableRows = sortRows((candidateRows || []).filter(isActionRequiredTicket));
        const actionableCount = actionableRows.length;
        const pageIds = actionableRows
          .slice(rangeStart, rangeStart + limit)
          .map((row: any) => row.id)
          .filter(Boolean);

        if (!pageIds.length) {
          return { data: [], count: actionableCount };
        }

        const { data: pageRows, error: pageError } = await supabase
          .from('tickets')
          .select(fullTicketSelect)
          .eq('organisation_id', orgId)
          .in('id', pageIds);

        if (pageError) {
          console.error('Error fetching action-required page:', pageError);
          throw pageError;
        }

        const byId = new Map((pageRows || []).map((ticket: any) => [ticket.id, ticket]));
        const orderedRows = pageIds
          .map((id: string) => byId.get(id))
          .filter(Boolean);

        return {
          data: orderedRows,
          count: actionableCount
        };
      }

      // Apply sorting + pagination for non-action-required queries.
      query = query
        .order(sortBy, { ascending: sortAscending })
        .range(rangeStart, rangeEnd);

      const { data, error, count } = await query;

      if (error) {
        console.error('Error fetching tickets:', error);
        throw error;
      }

      return {
        data: data || [],
        count: count || 0
      };
    } catch (error) {
      console.error('Failed to fetch tickets:', error);
      throw error;
    }
  },

  async getDriverTickets(driverId: string): Promise<{ data: Ticket[]; count: number }> {
    if (isMockMode) {
      const now = Date.now();
      const hires = MOCK_HIRES.filter((h: any) => h.driver_id === driverId);
      const hireIds = new Set(hires.map((h) => h.id));
      const hiresByVehicleId = new Map<string, Array<{ start: number; end: number | null }>>();
      for (const h of hires) {
        if (!h.vehicle_id) continue;
        const start = new Date(h.start_date).getTime();
        const end = h.end_date ? new Date(h.end_date).getTime() : null;
        const bucket = hiresByVehicleId.get(h.vehicle_id) ?? [];
        bucket.push({ start: Number.isFinite(start) ? start : now, end: end && Number.isFinite(end) ? end : null });
        hiresByVehicleId.set(h.vehicle_id, bucket);
      }

      const inHireWindow = (hire: { start: number; end: number | null }, t: number) =>
        t >= hire.start && (hire.end == null || t <= hire.end);

      const uniq = new Map<string, Ticket>();
      for (const t of MOCK_TICKETS) {
        if (!t?.id) continue;
        const ts = new Date((t as any).contravention_date || (t as any).issue_date || (t as any).created_at || 0).getTime();
        const ticketTime = Number.isFinite(ts) ? ts : now;

        // Explicit link always counts.
        if ((t as any).hire_id && hireIds.has(String((t as any).hire_id))) {
          uniq.set(t.id, t);
          continue;
        }

        // Otherwise, only count if vehicle matches AND hire period covers contravention date.
        const matches = hiresByVehicleId.get(String((t as any).vehicle_id || ''));
        if (!matches?.length) continue;
        if (matches.some((h) => inHireWindow(h, ticketTime))) {
          uniq.set(t.id, t);
        }
      }

      const data = Array.from(uniq.values()).sort((a, b) => {
        const at = new Date((a as any).contravention_date || (a as any).issue_date || (a as any).created_at || 0).getTime();
        const bt = new Date((b as any).contravention_date || (b as any).issue_date || (b as any).created_at || 0).getTime();
        return bt - at;
      });

      return { data, count: data.length };
    }

    try {
      const orgId = await getCurrentOrganisationId();

      const ticketSelect = `
        *,
        vehicles(vrm, make, model),
        hires(
          id,
          driver_id,
          driver_name,
          vehicle_id,
          start_date,
          end_date
        )
      `;

      // Primary attribution: a ticket belongs to a driver if:
      // - it is explicitly linked via hire_id, OR
      // - its vehicle matches a hire vehicle AND the hire period covers the ticket contravention date.
      const { data: hires, error: hiresError } = await supabase
        .from('hires')
        .select('id, vehicle_id, start_date, end_date')
        .eq('organisation_id', orgId)
        .eq('driver_id', driverId)
        .order('start_date', { ascending: false });

      if (hiresError) {
        console.error('Error fetching hires for driver:', hiresError);
        return { data: [], count: 0 };
      }

      const hireRows = (hires || []) as Array<{ id: string; vehicle_id: string | null; start_date: string; end_date: string | null }>;
      if (!hireRows.length) return { data: [], count: 0 };

      const hireIds = new Set(hireRows.map((h) => h.id));
      const hiresByVehicleId = new Map<string, Array<{ start: number; end: number | null }>>();
      for (const h of hireRows) {
        if (!h.vehicle_id) continue;
        const start = new Date(h.start_date).getTime();
        const end = h.end_date ? new Date(h.end_date).getTime() : null;
        const bucket = hiresByVehicleId.get(h.vehicle_id) ?? [];
        bucket.push({ start: Number.isFinite(start) ? start : Date.now(), end: end && Number.isFinite(end) ? end : null });
        hiresByVehicleId.set(h.vehicle_id, bucket);
      }

      const inHireWindow = (hire: { start: number; end: number | null }, t: number) =>
        t >= hire.start && (hire.end == null || t <= hire.end);

      const uniq = new Map<string, Ticket>();

      const chunk = <T,>(arr: T[], size: number) => {
        const out: T[][] = [];
        for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
        return out;
      };

      // Fetch tickets explicitly linked to this driver's hires.
      for (const ids of chunk(Array.from(hireIds), 100)) {
        const { data, error } = await supabase
          .from('tickets')
          .select(ticketSelect)
          .eq('organisation_id', orgId)
          .in('hire_id', ids)
          .order('contravention_date', { ascending: false })
          .limit(500);
        if (error) throw error;
        for (const t of (data || []) as any[]) {
          if (t?.id) uniq.set(String(t.id), t as any);
        }
      }

      // Fetch tickets by vehicle_id (most reliable) and filter by hire-period overlap.
      const vehicleIds = Array.from(hiresByVehicleId.keys());
      for (const ids of chunk(vehicleIds, 100)) {
        const { data, error } = await supabase
          .from('tickets')
          .select(ticketSelect)
          .eq('organisation_id', orgId)
          .in('vehicle_id', ids)
          .order('contravention_date', { ascending: false })
          .limit(500);
        if (error) throw error;

        for (const t of (data || []) as any[]) {
          if (!t?.id) continue;

          // Keep explicit links even if the date is out-of-window (manual override).
          if (t.hire_id && hireIds.has(String(t.hire_id))) {
            uniq.set(String(t.id), t);
            continue;
          }

          const ts = new Date(t.contravention_date || t.issue_date || t.created_at || 0).getTime();
          const ticketTime = Number.isFinite(ts) ? ts : Date.now();
          const matches = hiresByVehicleId.get(String(t.vehicle_id || ''));
          if (!matches?.length) continue;
          if (matches.some((h) => inHireWindow(h, ticketTime))) {
            uniq.set(String(t.id), t);
          }
        }
      }

      const data = Array.from(uniq.values()).sort((a, b) => {
        const at = new Date((a as any).contravention_date || (a as any).issue_date || (a as any).created_at || 0).getTime();
        const bt = new Date((b as any).contravention_date || (b as any).issue_date || (b as any).created_at || 0).getTime();
        return bt - at;
      });

      return { data, count: data.length };
    } catch (error) {
      console.error('Failed to fetch driver tickets:', error);
      throw error;
    }
  },

  async searchTickets(
    query: string,
    limit = 10
  ): Promise<Array<Pick<Ticket, 'id' | 'pcn_number' | 'vrn' | 'issuing_authority' | 'status' | 'issue_date'>>> {
    const raw = String(query || '').trim();
    if (!raw) return [];

    // Keep PostgREST filter safe: strip punctuation that can break `.or(...)` syntax.
    const q = raw.replace(/[^a-zA-Z0-9\s-]/g, ' ').replace(/\s+/g, ' ').trim();
    if (!q) return [];

    if (isMockMode) {
      const needle = q.toLowerCase();
      return MOCK_TICKETS
        .filter((t) => String(t.pcn_number || '').toLowerCase().includes(needle) || String(t.vrn || '').toLowerCase().includes(needle))
        .slice(0, limit)
        .map((t) => ({
          id: t.id,
          pcn_number: t.pcn_number,
          vrn: t.vrn,
          issuing_authority: t.issuing_authority,
          status: t.status,
          issue_date: t.issue_date
        }));
    }

    const orgId = await getCurrentOrganisationId();
    const pattern = `%${q}%`;

    const { data, error } = await supabase
      .from('tickets')
      .select('id, pcn_number, vrn, issuing_authority, status, issue_date')
      .eq('organisation_id', orgId)
      .or(`pcn_number.ilike.${pattern},vrn.ilike.${pattern}`)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) throw error;
    return (data || []) as any;
  },

  async getTicketById(id: string) {
    if (isMockMode) {
      const ticket = MOCK_TICKETS.find(t => t.id === id);
      if (!ticket) throw new Error('Ticket not found');
      return ticket;
    }

    // Real Supabase implementation
    try {
      const orgId = await getCurrentOrganisationId();

      const { data, error } = await supabase
        .from('tickets')
        .select(`
          *,
          vehicles(id, vrm, make, model, colour),
          hires(
            id,
            hire_ref,
            driver_name,
            start_date,
            end_date,
            hirer_address,
            agreement_url,
            agreement_signed_at,
            agreement_has_liability_clause,
            acceptance_type,
            drivers(id, name, email, phone, risk_score)
          )
        `)
        .eq('id', id)
        .eq('organisation_id', orgId)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          throw new Error('Ticket not found');
        }
        console.error('Error fetching ticket:', error);
        throw error;
      }

      return {
        ...(data as any),
        license_document_url: (data as any)?.license_document_url ?? (data as any)?.license_doc_url ?? undefined
      };
    } catch (error) {
      console.error('Failed to fetch ticket by ID:', error);
      throw error;
    }
  },

  // Backward-compatible alias used by some pages/components.
  async getTicket(id: string) {
    return this.getTicketById(id);
  },

  async updateTicket(id: string, updates: Partial<Ticket>) {
    if (isMockMode) {
      const idx = MOCK_TICKETS.findIndex(t => t.id === id);
      if (idx !== -1) {
        const next = { ...updates } as any;
        if (Object.prototype.hasOwnProperty.call(next, 'status')) next.status = normalizeTicketStatus((next as any).status);
        MOCK_TICKETS[idx] = { ...MOCK_TICKETS[idx], ...next };
        return MOCK_TICKETS[idx];
      }
      return null;
    }

    // Real Supabase implementation
    try {
      const orgId = await getCurrentOrganisationId();

      // First verify the ticket belongs to this organisation
      const { data: existing, error: checkError } = await supabase
        .from('tickets')
        .select('id')
        .eq('id', id)
        .eq('organisation_id', orgId)
        .single();

      if (checkError || !existing) {
        throw new Error('Ticket not found or access denied');
      }

      const normalizedUpdates: any = { ...updates };
      if (Object.prototype.hasOwnProperty.call(normalizedUpdates, 'status')) {
        normalizedUpdates.status = normalizeTicketStatus(normalizedUpdates.status);
      }

      // Perform the update, but be tolerant of schema drift (e.g., missing columns not yet migrated).
      let attempts = 0;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        attempts++;
        const { data, error } = await supabase
          .from('tickets')
          .update({
            ...normalizedUpdates,
            updated_at: new Date().toISOString()
          })
          .eq('id', id)
          .eq('organisation_id', orgId)
          .select()
          .single();

        if (!error) return data;

        const msg = String((error as any)?.message || '');
        const code = String((error as any)?.code || '');
        const m = msg.match(/Could not find the '([^']+)' column/i);
        if (code === 'PGRST204' && m?.[1] && attempts < 4) {
          const missingColumn = m[1];
          if (Object.prototype.hasOwnProperty.call(normalizedUpdates, missingColumn)) {
            console.warn(`[ApiService.updateTicket] Missing column '${missingColumn}' (schema cache). Retrying without it.`);
            delete normalizedUpdates[missingColumn];
            continue;
          }
        }

        console.error('Error updating ticket:', error);
        throw error;
      }
    } catch (error) {
      console.error('Failed to update ticket:', error);
      throw error;
    }
  },

  async deleteTicket(id: string) {
    if (isMockMode) {
      const idx = MOCK_TICKETS.findIndex(t => t.id === id);
      if (idx !== -1) MOCK_TICKETS.splice(idx, 1);
      // Best-effort: remove tasks linked to this ticket (mock uses related_id)
      for (let i = MOCK_TASKS.length - 1; i >= 0; i--) {
        if ((MOCK_TASKS[i] as any).related_id === id) MOCK_TASKS.splice(i, 1);
      }
      return;
    }

    try {
      const orgId = await getCurrentOrganisationId();

      // Best-effort: delete related tasks (schema differs across versions); never block ticket deletion on this.
      try {
        const { error } = await supabase
          .from('tasks')
          .delete()
          .eq('organisation_id', orgId)
          .eq('related_entity_id', id);
        if (error) throw error;
      } catch (e: any) {
        const msg = String(e?.message || '').toLowerCase();
        if (msg.includes('related_entity_id')) {
          try {
            const { error } = await supabase
              .from('tasks')
              .delete()
              .eq('organisation_id', orgId)
              .eq('related_id', id);
            if (error) throw error;
          } catch (e2) {
            console.warn('Failed to delete related tasks for ticket (non-blocking):', e2);
          }
        } else {
          console.warn('Failed to delete related tasks for ticket (non-blocking):', e);
        }
      }

      const { error } = await supabase
        .from('tickets')
        .delete()
        .eq('id', id)
        .eq('organisation_id', orgId);

      if (error) {
        console.error('Error deleting ticket:', error);
        throw error;
      }
    } catch (error) {
      console.error('Failed to delete ticket:', error);
      throw error;
    }
  },

  async createTicketBatch(tickets: Partial<Ticket>[]): Promise<TicketBatchResult> {
    if (isMockMode) {
      const nowIso = new Date().toISOString();
      const createdMockTickets: Ticket[] = tickets.map((t, i) => ({
        id: `new-${Date.now()}-${i}`,
        created_at: nowIso,
        pcn_number: t.pcn_number || 'UNKNOWN',
        vrn: t.vrn || 'UNKNOWN',
        issue_date: t.issue_date || nowIso,
        contravention_date: t.contravention_date || nowIso,
        stage: t.stage || 'PCN Issued',
        amount: t.amount || 0,
        discount_amount: t.discount_amount,
        status: 'unassigned',
        location: t.location || 'Unknown',
        issuing_authority: t.issuing_authority || 'Unknown Authority',
        organisation_id: 'mock',
        vehicle_id: 'unknown',
        hire_id: null,
        contravention_type: t.contravention_type,
        low_confidence_fields: (t as any).low_confidence_fields || [],
        document_url: t.document_url,
        file_hash: (t as any).ai_file_hash || (t as any).file_hash || null,
        extraction_status: (t as any).ai_cache_hit ? 'duplicate_cached' : 'completed',
        user_confirmed: true,
        user_confirmed_at: nowIso,
        review_status: 'confirmed',
        timeline: [
          {
            id: `t-${Date.now()}-${i}`,
            date: nowIso,
            title: 'Ticket Created',
            description: 'Imported via Batch Upload',
            type: 'system'
          }
        ]
      })) as any;

      // Match existing behavior: newest tickets first.
      for (let i = createdMockTickets.length - 1; i >= 0; i--) MOCK_TICKETS.unshift(createdMockTickets[i]);

      return { created: createdMockTickets.length, merged: 0, tickets: createdMockTickets as any };
    }

    // Real Supabase implementation
    try {
      const orgId = await getCurrentOrganisationId();
      const { data: { user } } = await supabase.auth.getUser();

      const nowIso = new Date().toISOString();

      const normalized = tickets.map((t, i) => {
        const pcn_number = normalizePcnNumber(t.pcn_number) || `UPLOAD-${Date.now()}-${i}-${Math.random().toString(16).slice(2, 6)}`;
        return { ...t, pcn_number };
      });

      const pcns = normalized.map((t) => t.pcn_number!).filter(Boolean);
      const fileHashes = normalized
        .map((t) => String((t as any).ai_file_hash || (t as any).file_hash || '').trim())
        .filter(Boolean);
      const existingByPcn = new Map<string, any>();
      const existingByFileHash = new Map<string, any>();
      for (let i = 0; i < pcns.length; i += 100) {
        const chunk = pcns.slice(i, i + 100);
        const { data, error } = await supabase
          .from('tickets')
          .select('*')
          .eq('organisation_id', orgId)
          .in('pcn_number', chunk);
        if (error) throw error;
        (data || []).forEach((t: any) => existingByPcn.set(String(t.pcn_number).toUpperCase(), t));
      }
      for (let i = 0; i < fileHashes.length; i += 100) {
        const chunk = fileHashes.slice(i, i + 100);
        const { data, error } = await supabase
          .from('tickets')
          .select('*')
          .eq('organisation_id', orgId)
          .in('file_hash', chunk);
        if (error) throw error;
        (data || []).forEach((t: any) => existingByFileHash.set(String(t.file_hash), t));
      }

      const findExistingTicket = (t: Partial<Ticket>) => {
        const pcnMatch = existingByPcn.get(String(t.pcn_number).toUpperCase());
        if (pcnMatch) return pcnMatch;
        const hash = String((t as any).ai_file_hash || (t as any).file_hash || '').trim();
        return hash ? existingByFileHash.get(hash) : null;
      };

      const toMerge = normalized.filter((t) => Boolean(findExistingTicket(t)));
      const toCreate = normalized.filter((t) => !findExistingTicket(t));

      // Auto-create/link vehicles from VRN so uploads populate the Vehicles section.
      const vehicleMap = await ensureVehiclesForVrms(
        orgId,
        toCreate.map((t) => String(t.vrn || '').trim()).filter(Boolean)
      );

      // Prepare tickets with organisation_id and default values
      const ticketsToInsert = toCreate.map(t => ({
        organisation_id: orgId,
        pcn_number: t.pcn_number || `UPLOAD-${Date.now()}-${Math.random().toString(16).slice(2, 6)}`,
        vrn: t.vrn || 'UNKNOWN',
        vehicle_id: isLikelyUkVrm(String(t.vrn || '')) ? (vehicleMap.get(normalizeVrm(String(t.vrn || ''))) ?? null) : null,
        issue_date: t.issue_date || nowIso,
        contravention_date: t.contravention_date || nowIso,
        stage: t.stage || 'PCN Issued',
        amount: t.amount || 0,
        discount_amount: t.discount_amount,
        status: 'unassigned',
        location: t.location || 'Unknown',
        issuing_authority: t.issuing_authority || 'Unknown Authority',
        contravention_type: t.contravention_type,
        document_url: t.document_url,
        file_hash: (t as any).ai_file_hash || (t as any).file_hash || null,
        extraction_status: (t as any).ai_cache_hit ? 'duplicate_cached' : 'completed',
        user_confirmed: true,
        user_confirmed_at: nowIso,
        reviewed_by_user_id: user?.id || null,
        review_status: 'confirmed',
        ai_reextraction_count: (t as any).ai_cache_hit ? 0 : 1,
        low_confidence_fields: t.low_confidence_fields || [],
        timeline: JSON.stringify([{
          id: `t-${Date.now()}`,
          date: nowIso,
          title: 'Ticket Created',
          description: 'Imported via Batch Upload',
          type: 'system'
        }])
      }));

      let createdTickets: any[] = [];
      if (ticketsToInsert.length) {
        const { data, error } = await supabase
          .from('tickets')
          .insert(ticketsToInsert)
          .select();

        if (error) {
          console.error('Error creating ticket batch:', error);
          throw error;
        }
        createdTickets = data || [];
      }

      // Note: Batch upload is FREE - we only bill for AI extraction (if used), appeals, and liability transfers

      // STEP 2: AUTO - Run auditAgreement on all created tickets with controlled parallelism
      // Using pLimit(8) for bounded parallelism (5-10 concurrent audits)
      const auditLimit = pLimit(AUDIT_CONCURRENCY_LIMIT);
      const audits = new Map<string, AuditResult>();

      // Process audits in parallel with concurrency limit
      const auditPromises = createdTickets.map((ticket) =>
        auditLimit(async () => {
          try {
            const { audit } = await determineLiability(ticket.id, orgId);
            audits.set(ticket.id, audit);

            const updatePayload: any = { audit_result: audit };

            // STEP 3: AUTO - Auto-assign if eligible
            if (audit.autoAssignable && audit.hireId) {
              const autoAssignTimeline: TimelineEvent = {
                id: `auto-${Date.now()}-${Math.random().toString(16).slice(2, 6)}`,
                date: new Date().toISOString(),
                title: 'Auto-assigned to Hire',
                description: `Confidence: ${audit.confidenceScore}% | ${audit.reason}`,
                type: 'system'
              };

              const existingTimeline = typeof ticket.timeline === 'string'
                ? JSON.parse(ticket.timeline)
                : (ticket.timeline || []);
              const updatedTimeline = [...existingTimeline, autoAssignTimeline];

              updatePayload.hire_id = audit.hireId;
              updatePayload.status = 'active';
              updatePayload.auto_assigned = true;
              updatePayload.timeline = updatedTimeline;
            }

            const { data: updatedTicket } = await supabase
              .from('tickets')
              .update(updatePayload)
              .eq('id', ticket.id)
              .select()
              .single();

            if (updatedTicket) {
              // Update the ticket in our array
              const idx = createdTickets.findIndex((t: any) => t.id === ticket.id);
              if (idx !== -1) {
                createdTickets[idx] = updatedTicket;
              }
            }
          } catch (auditError) {
            // Don't fail batch if audit fails for a single ticket
            console.warn(`Audit failed for ticket ${ticket.id}:`, auditError);
          }
        })
      );

      // Wait for all audits to complete
      await Promise.all(auditPromises);

      let merged = 0;
      const mergedTickets: any[] = [];
      for (const t of toMerge) {
        const existing = findExistingTicket(t);
        if (!existing) continue;
        const next = await ApiService.mergeDuplicateTicket(existing, t, 'Batch upload');
        mergedTickets.push(next);
        merged++;
      }

      return {
        created: createdTickets.length,
        merged,
        tickets: [...createdTickets, ...mergedTickets] as any,
        audits
      };
    } catch (error) {
      console.error('Failed to create ticket batch:', error);
      throw error;
    }
  },

  async createTicket(ticket: Partial<Ticket>): Promise<TicketCreateResult> {
    if (isMockMode) {
      const created: Ticket = {
        id: `manual-${Date.now()}`,
        created_at: new Date().toISOString(),
        pcn_number: ticket.pcn_number || 'UNKNOWN',
        vrn: ticket.vrn || 'UNKNOWN',
        issue_date: ticket.issue_date || new Date().toISOString(),
        contravention_date: ticket.contravention_date || new Date().toISOString(),
        stage: ticket.stage || 'PCN Issued',
        amount: ticket.amount || 0,
        discount_amount: ticket.discount_amount ?? undefined,
        status: 'unassigned',
        location: ticket.location || 'Unknown',
        issuing_authority: ticket.issuing_authority || 'Unknown Authority',
        organisation_id: 'mock',
        vehicle_id: 'unknown',
        hire_id: null,
        contravention_type: ticket.contravention_type || 'Parking Violation',
        timeline: [
          {
            id: `t-${Date.now()}`,
            date: new Date().toISOString(),
            title: 'Ticket Created',
            description: 'Created manually',
            type: 'system'
          }
        ]
      } as any;
      MOCK_TICKETS.unshift(created);
      return { ticket: created, action: 'created' };
    }

    try {
      const orgId = await getCurrentOrganisationId();
      const pcnNormalized = normalizePcnNumber(ticket.pcn_number);
      if (!pcnNormalized) throw new Error('PCN number is required');

      const vrn = ticket.vrn || 'UNKNOWN';
      const vehicleMap = await ensureVehiclesForVrms(orgId, [String(vrn || '').trim()]);
      const vehicle_id = isLikelyUkVrm(String(vrn || '')) ? (vehicleMap.get(normalizeVrm(String(vrn || ''))) ?? null) : null;

      const timeline: TimelineEvent[] = [
        {
          id: `t-${Date.now()}`,
          date: new Date().toISOString(),
          title: 'Ticket Created',
          description: 'Created manually',
          type: 'system'
        }
      ];

      const insertPayload = {
        organisation_id: orgId,
        pcn_number: pcnNormalized,
        vrn,
        vehicle_id,
        issue_date: ticket.issue_date || new Date().toISOString(),
        contravention_date: ticket.contravention_date || new Date().toISOString(),
        stage: ticket.stage || 'PCN Issued',
        amount: ticket.amount || 0,
        discount_amount: ticket.discount_amount ?? null,
        status: 'unassigned',
        location: ticket.location || 'Unknown',
        issuing_authority: ticket.issuing_authority || 'Unknown Authority',
        contravention_type: ticket.contravention_type || 'Parking Violation',
        low_confidence_fields: ticket.low_confidence_fields || [],
        timeline
      };

      const { data, error } = await supabase
        .from('tickets')
        .insert(insertPayload as any)
        .select()
        .single();

      if (error) {
        if ((error as any)?.code === '23505') {
          const { data: existing, error: fetchErr } = await supabase
            .from('tickets')
            .select('*')
            .eq('organisation_id', orgId)
            .eq('pcn_number', pcnNormalized)
            .single();
          if (fetchErr || !existing) throw error;
          const merged = await ApiService.mergeDuplicateTicket(existing, insertPayload as any, 'Manual entry');
          return { ticket: merged as any, action: 'merged' };
        }

        console.error('Error creating ticket:', error);
        throw error;
      }

      // Note: Ticket creation is FREE - we only bill for AI extraction, appeals, and liability transfers

      // STEP 2: AUTO - Run auditAgreement and determineLiability
      // This is the SINGLE SOURCE OF TRUTH for hire detection
      let finalTicket = data;
      let auditResult: AuditResult | null = null;

      try {
        // Run liability determination (which calls auditAgreement internally)
        const { audit } = await determineLiability(data.id, orgId);
        auditResult = audit;

        const updatePayload: any = { audit_result: audit };

        // STEP 3: AUTO - Evaluate Auto-Assignment
        if (audit.autoAssignable && audit.hireId) {
          // Auto-assign the hire
          const autoAssignTimeline: TimelineEvent = {
            id: `auto-${Date.now()}`,
            date: new Date().toISOString(),
            title: 'Auto-assigned to Hire',
            description: `Confidence: ${audit.confidenceScore}% | ${audit.reason}`,
            type: 'system'
          };

          const updatedTimeline = [...(data.timeline || timeline), autoAssignTimeline];

          updatePayload.hire_id = audit.hireId;
          updatePayload.status = 'active';
          updatePayload.auto_assigned = true;
          updatePayload.timeline = updatedTimeline;
        }

        const { data: updatedTicket, error: updateError } = await supabase
          .from('tickets')
          .update(updatePayload)
          .eq('id', data.id)
          .select()
          .single();

        if (!updateError && updatedTicket) {
          finalTicket = updatedTicket;
        }
      } catch (liabilityError) {
        // Don't fail ticket creation if liability determination fails
        console.warn('Liability determination failed for new ticket:', liabilityError);
      }

      return {
        ticket: finalTicket as any,
        action: 'created',
        audit: auditResult || undefined
      };
    } catch (error) {
      console.error('Failed to create ticket:', error);
      throw error;
    }
  },

  async generateLiabilityPack(
    ticketId: string,
    type: 'TMA_2004' | 'POFA_2012',
    representationText: string
  ) {
    if (isMockMode) {
      await new Promise(resolve => setTimeout(resolve, 2000));
      const idx = MOCK_TICKETS.findIndex(t => t.id === ticketId);
      if (idx !== -1) {
        MOCK_TICKETS[idx].liability_pack = {
          type,
          status: 'generated',
          generated_at: new Date().toISOString(),
          url: `https://example.com/pack-${type.toLowerCase()}.pdf`
        };
        MOCK_TICKETS[idx].decision = 'transfer';
      }
      return `https://example.com/pack-${type.toLowerCase()}.pdf`;
    }

    try {
      // SECURITY: Get current organization for multi-tenant isolation
      const currentOrgId = getCurrentOrganisationId();
      if (!currentOrgId) {
        throw new Error('Organization context required');
      }

      // Fetch ticket with hire and driver data
      const { data: ticket, error: ticketError } = await supabase
        .from('tickets')
        .select(`
          *,
          hires (
            id,
            driver_id,
            driver_name,
            start_date,
            end_date,
            agreement_url,
            hirer_address,
            created_at,
            updated_at
          )
        `)
        .eq('id', ticketId)
        .eq('organisation_id', currentOrgId) // SECURITY: Organization scoping for defense-in-depth
        .single();

      if (ticketError || !ticket) {
        throw new Error(`Failed to fetch ticket: ${ticketError?.message || 'Ticket not found'}`);
      }

      const hire = Array.isArray(ticket.hires) ? ticket.hires[0] : ticket.hires;
      if (!hire) {
        throw new Error('No hire agreement found for this ticket');
      }

      if (!hire.agreement_url) {
        throw new Error('Hire agreement URL not found');
      }

      // Fetch driver data if exists
      let driver = null;
      if (hire.driver_id) {
        const { data: driverData } = await supabase
          .from('drivers')
          .select('*')
          .eq('id', hire.driver_id)
          .single();

        driver = driverData;
      }

      // Generate PDF using utility
      const { generateLiabilityPackPdf } = await import('../utils/generateLiabilityPackPdf');

      const agreementPdfUrl = await ApiService
        .resolveDocumentUrl(hire.agreement_url, 60 * 60)
        .catch(() => hire.agreement_url);

      const pdfBlob = await generateLiabilityPackPdf({
        ticket,
        hire,
        driver,
        representationText,
        agreementPdfUrl
      });

      // Upload to Supabase Storage
      const fileName = `liability-pack-${ticketId}-${Date.now()}.pdf`;

      const { error: uploadError } = await supabase.storage
        .from('liability-packs')
        .upload(fileName, pdfBlob, {
          contentType: 'application/pdf',
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) {
        throw new Error(`Failed to upload PDF: ${uploadError.message}`);
      }

      // SECURITY: Use signed URL with 24-hour expiry instead of public URL
      // This prevents unauthorized access and ensures GDPR compliance
      const { data: signedUrlData, error: signedUrlError } = await supabase.storage
        .from('liability-packs')
        .createSignedUrl(fileName, 86400); // 24 hours expiry

      if (signedUrlError || !signedUrlData) {
        throw new Error(`Failed to create signed URL: ${signedUrlError?.message || 'Unknown error'}`);
      }

      console.log('Successfully generated liability pack with signed URL (24hr expiry)');

      return signedUrlData.signedUrl;
    } catch (err) {
      console.error('Error generating liability pack:', err);
      throw err;
    }
  },

  /**
   * Confirm that no hire exists for a ticket
   *
   * Two-step confirmation flow:
   * 1. User clicks "Confirm no hire exists"
   * 2. User selects reason from dropdown + optional note
   *
   * Effect:
   * - Locks L001a (confirmed no hire)
   * - Upgrades to Tier 2 evidence
   * - Sets liability_result = 'fleet'
   * - Allows automation to proceed
   */
  async confirmNoHire(
    ticketId: string,
    reason: NoHireReason,
    reasonNote?: string
  ): Promise<void> {
    if (isMockMode) {
      const idx = MOCK_TICKETS.findIndex(t => t.id === ticketId);
      if (idx !== -1) {
        MOCK_TICKETS[idx].liability_result = 'fleet';
        MOCK_TICKETS[idx].status = 'active';
      }
      return;
    }

    try {
      const orgId = await getCurrentOrganisationId();
      const userId = (await supabase.auth.getUser()).data.user?.id;

      // Get current ticket
      const { data: ticket, error: fetchError } = await supabase
        .from('tickets')
        .select('*')
        .eq('id', ticketId)
        .single();

      if (fetchError || !ticket) {
        throw new Error('Ticket not found');
      }

      // Build confirmation record
      const confirmation: NoHireConfirmation = {
        ticketId,
        confirmedByUserId: userId || 'unknown',
        reason,
        reasonNote,
        timestamp: new Date().toISOString()
      };

      // Create timeline event
      const confirmationTimeline: TimelineEvent = {
        id: `nohire-${Date.now()}`,
        date: new Date().toISOString(),
        title: 'No Hire Confirmed',
        description: `Reason: ${getNoHireReasonLabel(reason)}${reasonNote ? ` - ${reasonNote}` : ''}`,
        type: 'system'
      };

      const currentTimeline = Array.isArray(ticket.timeline) ? ticket.timeline : [];
      const updatedTimeline = [...currentTimeline, confirmationTimeline];

      // Update ticket with confirmed status
      const { error: updateError } = await supabase
        .from('tickets')
        .update({
          liability_result: 'fleet',
          liability_confidence: 80, // Upgraded confidence after confirmation
          status: 'active',
          no_hire_confirmed: true,
          no_hire_confirmation: confirmation,
          timeline: updatedTimeline,
          // Update audit result to reflect confirmation
          audit_result: {
            ...((ticket.audit_result as any) || {}),
            confidenceScore: 80,
            evidenceTier: 2, // Upgraded from Tier 5 to Tier 2 (L001a confirmed)
            reason: 'No hire confirmed by user',
            blockingFactors: []
          }
        })
        .eq('id', ticketId);

      if (updateError) {
        console.error('Error confirming no hire:', updateError);
        throw updateError;
      }

      // Log the confirmation in audit log
      await supabase
        .from('audit_logs')
        .insert({
          organisation_id: orgId,
          action: 'no_hire_confirmed',
          entity_type: 'ticket',
          entity_id: ticketId,
          user_id: userId,
          details: confirmation
        });

    } catch (error) {
      console.error('Failed to confirm no hire:', error);
      throw error;
    }
  },

  /**
   * Log a manual override when user changes hire assignment
   *
   * Creates an immutable audit log entry for:
   * - Legal defensibility
   * - Enterprise trust
   * - ML feedback loop
   * - Internal debugging
   */
  async logManualOverride(
    ticketId: string,
    previousHireId: string | null,
    newHireId: string | null,
    reason: OverrideReason,
    reasonNote?: string
  ): Promise<void> {
    if (isMockMode) {
      console.log('Mock: Manual override logged', { ticketId, previousHireId, newHireId, reason });
      return;
    }

    try {
      const orgId = await getCurrentOrganisationId();
      const userId = (await supabase.auth.getUser()).data.user?.id;

      // Get current ticket for previous confidence score
      const { data: ticket } = await supabase
        .from('tickets')
        .select('liability_confidence, audit_result')
        .eq('id', ticketId)
        .single();

      const previousConfidenceScore = ticket?.liability_confidence || 0;
      const previousAuditResult = ticket?.audit_result as AuditResult | undefined;

      // Build override event
      const overrideEvent: ManualOverrideEvent = {
        ticketId,
        previousHireId,
        newHireId,
        overriddenByUserId: userId || 'unknown',
        reason,
        reasonNote,
        timestamp: new Date().toISOString(),
        previousConfidenceScore,
        previousAuditResult
      };

      // Create timeline event
      const overrideTimeline: TimelineEvent = {
        id: `override-${Date.now()}`,
        date: new Date().toISOString(),
        title: 'Hire Assignment Override',
        description: `Changed from ${previousHireId || 'unassigned'} to ${newHireId || 'unassigned'}. Reason: ${getOverrideReasonLabel(reason)}`,
        type: 'user',
        user: userId || 'Unknown'
      };

      // Get current timeline
      const { data: currentTicket } = await supabase
        .from('tickets')
        .select('timeline, override_logs')
        .eq('id', ticketId)
        .single();

      const currentTimeline = Array.isArray(currentTicket?.timeline) ? currentTicket.timeline : [];
      const currentOverrides = Array.isArray(currentTicket?.override_logs) ? currentTicket.override_logs : [];

      // Update ticket with override log
      const { error: updateError } = await supabase
        .from('tickets')
        .update({
          hire_id: newHireId,
          auto_assigned: false, // Clear auto-assigned flag on manual override
          timeline: [...currentTimeline, overrideTimeline],
          override_logs: [...currentOverrides, overrideEvent]
        })
        .eq('id', ticketId);

      if (updateError) {
        console.error('Error logging manual override:', updateError);
        throw updateError;
      }

      // Also log to audit_logs table
      await supabase
        .from('audit_logs')
        .insert({
          organisation_id: orgId,
          action: 'hire_override',
          entity_type: 'ticket',
          entity_id: ticketId,
          user_id: userId,
          details: overrideEvent
        });

    } catch (error) {
      console.error('Failed to log manual override:', error);
      throw error;
    }
  },

  async getTasks() {
    if (isMockMode) {
      ApiService.ensureComplianceTasks().catch((err) => console.warn('Mock compliance task sync failed:', err));
      return [...MOCK_TASKS].sort((a, b) => {
        const aTime = new Date(a.created_at || 0).getTime();
        const bTime = new Date(b.created_at || 0).getTime();
        return bTime - aTime;
      });
    }

    // Real Supabase implementation
    try {
      await ApiService.ensureComplianceTasks();
      const orgId = await getCurrentOrganisationId();

      const { data, error } = await supabase
        .from('tasks')
        .select('*')
        .eq('organisation_id', orgId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching tasks:', error);
        throw error;
      }

      return (data || []).map((t: any) => ({
        ...t,
        source: t.source || 'workflow',
        severity: t.severity || (String(t.priority || '').toLowerCase() === 'high' ? 'high' : 'medium'),
        metadata: t.metadata || {},
        related_id: t.related_id ?? t.related_entity_id ?? undefined
      }));
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
      throw error;
    }
  },

  async ensureComplianceTasks() {
    const now = new Date();
    const buildTitle = (label: 'MOT' | 'Tax' | 'Insurance' | 'Licence', days: number, reference: string) => {
      if (days < 0) return `${label} overdue (${Math.abs(days)}d) - ${reference}`;
      if (days === 0) return `${label} due today - ${reference}`;
      return `${label} due in ${days}d - ${reference}`;
    };

    const buildDescription = (
      label: 'MOT' | 'Tax' | 'Insurance' | 'Licence',
      due: Date,
      status?: string | null
    ) => {
      const dueDate = due.toLocaleDateString('en-GB');
      const statusText = status ? `Status: ${status}. ` : '';
      return `${label} deadline: ${dueDate}. ${statusText}Please renew or verify before the due date.`;
    };

    type ComplianceCandidate = {
      breachCode: string;
      title: string;
      description: string;
      priority: Task['priority'];
      severity: Task['severity'];
      dueDate: string;
      relatedEntityId: string;
      relatedEntityType: 'vehicle' | 'driver';
    };

    const addCandidate = (
      candidates: ComplianceCandidate[],
      args: {
        label: 'MOT' | 'Tax' | 'Insurance' | 'Licence';
        id: string;
        reference: string;
        dueDate: Date;
        status?: string | null;
        relatedEntityType: 'vehicle' | 'driver';
        breachPrefix: string;
      }
    ) => {
      const days = daysUntilDate(args.dueDate, now);
      if (!shouldCreateComplianceReminder(days)) return;

      const windowLabel = days < 0 ? 'overdue' : `${days}d`;
      candidates.push({
        breachCode: `${args.breachPrefix}:${args.id}:${windowLabel}`,
        title: buildTitle(args.label, days, args.reference),
        description: buildDescription(args.label, args.dueDate, args.status),
        priority: priorityFromDays(days),
        severity: severityFromDays(days),
        dueDate: args.dueDate.toISOString(),
        relatedEntityId: args.id,
        relatedEntityType: args.relatedEntityType
      });
    };

    if (isMockMode) {
      const existingBreachCodes = new Set(
        MOCK_TASKS.filter((task) => task.status === 'open').map((task) => task.breach_code).filter(Boolean)
      );
      const candidates: ComplianceCandidate[] = [];

      for (const vehicle of MOCK_VEHICLES.filter((v) => v.status !== 'Inactive')) {
        const motDue = parseDateOnly(vehicle.mot_expiry_date);
        const taxDue = parseDateOnly(vehicle.tax_due_date);
        const insuranceDue = parseDateOnly(vehicle.insurance_expiry_date);

        if (motDue) {
          addCandidate(candidates, {
            label: 'MOT',
            id: vehicle.id,
            reference: vehicle.vrm,
            dueDate: motDue,
            status: vehicle.mot_status,
            relatedEntityType: 'vehicle',
            breachPrefix: 'vehicle_mot'
          });
        }

        if (taxDue) {
          addCandidate(candidates, {
            label: 'Tax',
            id: vehicle.id,
            reference: vehicle.vrm,
            dueDate: taxDue,
            status: vehicle.tax_status,
            relatedEntityType: 'vehicle',
            breachPrefix: 'vehicle_tax'
          });
        }

        if (insuranceDue) {
          addCandidate(candidates, {
            label: 'Insurance',
            id: vehicle.id,
            reference: vehicle.vrm,
            dueDate: insuranceDue,
            status: vehicle.insurance_status,
            relatedEntityType: 'vehicle',
            breachPrefix: 'vehicle_insurance'
          });
        }
      }

      for (const driver of MOCK_DRIVERS) {
        const licenceDue = parseDateOnly(driver.license_expiry_date);
        if (!licenceDue) continue;
        addCandidate(candidates, {
          label: 'Licence',
          id: driver.id,
          reference: driver.name,
          dueDate: licenceDue,
          status: driver.license_status,
          relatedEntityType: 'driver',
          breachPrefix: 'driver_licence'
        });
      }

      for (const candidate of candidates) {
        if (existingBreachCodes.has(candidate.breachCode)) continue;
        MOCK_TASKS.unshift({
          id: `task-${Date.now()}-${candidate.relatedEntityId}-${candidate.breachCode}`,
          title: candidate.title,
          description: candidate.description,
          priority: candidate.priority,
          type: 'expiry',
          status: 'open',
          source: 'compliance',
          severity: candidate.severity,
          breach_code: candidate.breachCode,
          related_id: candidate.relatedEntityId,
          metadata: { related_entity_type: candidate.relatedEntityType },
          created_at: new Date().toISOString()
        });
        existingBreachCodes.add(candidate.breachCode);
      }

      return;
    }

    const orgId = await getCurrentOrganisationId();
    const [
      { data: vehicles, error: vehiclesError },
      { data: drivers, error: driversError },
      { data: existingTasks, error: tasksError }
    ] = await Promise.all([
      supabase
        .from('vehicles')
        .select('id, vrm, status, mot_status, mot_expiry_date, tax_status, tax_due_date, insurance_status, insurance_expiry_date')
        .eq('organisation_id', orgId),
      supabase
        .from('drivers')
        .select('id, name, license_status, license_expiry_date')
        .eq('organisation_id', orgId),
      supabase
        .from('tasks')
        .select('breach_code, status')
        .eq('organisation_id', orgId)
        .eq('source', 'compliance')
        .eq('status', 'open')
    ]);

    if (vehiclesError || driversError || tasksError) {
      console.error('Failed to load compliance dependencies:', vehiclesError || driversError || tasksError);
      return;
    }

    const existingBreachCodes = new Set(
      (existingTasks || []).map((task: any) => String(task.breach_code || '')).filter(Boolean)
    );
    const candidates: ComplianceCandidate[] = [];

    for (const vehicle of (vehicles || []).filter((item: any) => item.status !== 'Inactive')) {
      const motDue = parseDateOnly(vehicle.mot_expiry_date);
      const taxDue = parseDateOnly(vehicle.tax_due_date);
      const insuranceDue = parseDateOnly(vehicle.insurance_expiry_date);

      if (motDue) {
        addCandidate(candidates, {
          label: 'MOT',
          id: vehicle.id,
          reference: vehicle.vrm,
          dueDate: motDue,
          status: vehicle.mot_status,
          relatedEntityType: 'vehicle',
          breachPrefix: 'vehicle_mot'
        });
      }

      if (taxDue) {
        addCandidate(candidates, {
          label: 'Tax',
          id: vehicle.id,
          reference: vehicle.vrm,
          dueDate: taxDue,
          status: vehicle.tax_status,
          relatedEntityType: 'vehicle',
          breachPrefix: 'vehicle_tax'
        });
      }

      if (insuranceDue) {
        addCandidate(candidates, {
          label: 'Insurance',
          id: vehicle.id,
          reference: vehicle.vrm,
          dueDate: insuranceDue,
          status: vehicle.insurance_status,
          relatedEntityType: 'vehicle',
          breachPrefix: 'vehicle_insurance'
        });
      }
    }

    for (const driver of drivers || []) {
      const licenceDue = parseDateOnly((driver as any).license_expiry_date);
      if (!licenceDue) continue;
      addCandidate(candidates, {
        label: 'Licence',
        id: (driver as any).id,
        reference: (driver as any).name,
        dueDate: licenceDue,
        status: (driver as any).license_status,
        relatedEntityType: 'driver',
        breachPrefix: 'driver_licence'
      });
    }

    const toInsert = candidates
      .filter((candidate) => !existingBreachCodes.has(candidate.breachCode))
      .map((candidate) => ({
        organisation_id: orgId,
        title: candidate.title,
        description: candidate.description,
        priority: candidate.priority,
        type: 'expiry',
        status: 'open',
        source: 'compliance',
        severity: candidate.severity,
        assignee_user_id: null,
        breach_code: candidate.breachCode,
        metadata: { related_entity_type: candidate.relatedEntityType },
        related_entity_id: candidate.relatedEntityId,
        related_entity_type: candidate.relatedEntityType,
        due_date: candidate.dueDate
      }));

    if (toInsert.length === 0) return;

    const { error: insertError } = await supabase.from('tasks').insert(toInsert);
    if (insertError) {
      console.error('Failed to insert compliance tasks:', insertError);
      return;
    }

    await AuditService.log({
      action: 'compliance_tasks_created',
      category: 'settings',
      description: `Generated ${toInsert.length} compliance reminder task(s).`,
      resource_type: 'task',
      metadata: {
        source: 'compliance',
        created_count: toInsert.length
      }
    });
  },

  async createTask(task: {
    title: string;
    description?: string;
    priority?: 'High' | 'Medium' | 'Low';
    type?: 'expiry' | 'appeal' | 'upload' | 'assignment';
    relatedId?: string;
    relatedEntityType?: string;
    dueDate?: string;
    source?: Task['source'];
    severity?: Task['severity'];
    assigneeUserId?: string | null;
    breachCode?: string | null;
    metadata?: Record<string, any>;
  }) {
    const title = String(task.title || '').trim();
    if (!title) throw new Error('Task title is required');

    if (isMockMode) {
      const nowIso = new Date().toISOString();
      const t: Task = {
        id: `task-${Date.now()}`,
        title,
        description: String(task.description || '').trim(),
        priority: task.priority || 'High',
        type: task.type || 'appeal',
        related_id: task.relatedId,
        created_at: nowIso,
        status: 'open',
        source: task.source || 'workflow',
        severity: task.severity || 'medium',
        assignee_user_id: task.assigneeUserId || null,
        breach_code: task.breachCode || null,
        metadata: task.metadata || {}
      };
      MOCK_TASKS.unshift(t);
      return t;
    }

    const orgId = await getCurrentOrganisationId();
    const insertPayload: any = {
      organisation_id: orgId,
      title,
      description: task.description ?? null,
      priority: task.priority || 'High',
      type: task.type || 'appeal',
      status: 'open',
      source: task.source || 'workflow',
      severity: task.severity || (String(task.priority || '').toLowerCase() === 'high' ? 'high' : 'medium'),
      assignee_user_id: task.assigneeUserId ?? null,
      breach_code: task.breachCode ?? null,
      metadata: task.metadata ?? {},
      related_entity_id: task.relatedId ?? null,
      related_entity_type: task.relatedEntityType ?? (task.relatedId ? 'ticket' : null),
      due_date: task.dueDate ?? null
    };

    const { data, error } = await supabase.from('tasks').insert(insertPayload).select('*').single();
    if (error) throw error;
    return {
      ...(data as any),
      related_id: (data as any).related_id ?? (data as any).related_entity_id ?? undefined,
      source: (data as any).source || 'workflow',
      severity: (data as any).severity || 'medium',
      metadata: (data as any).metadata || {}
    } as Task;
  },

  async completeTask(id: string) {
    if (isMockMode) {
      const idx = MOCK_TASKS.findIndex(t => t.id === id);
      if (idx !== -1) MOCK_TASKS[idx].status = 'resolved';
      return;
    }

    await ApiService.resolveComplianceTask(id, 'Resolved from action inbox');
  },

  async saveVehicleCompliance(payload: {
    vehicleId: string;
    insurance_provider?: string | null;
    insurance_policy_number?: string | null;
    insurance_status?: string | null;
    insurance_expiry_date?: string | null;
    insurance_doc_url?: string | null;
    mot_doc_url?: string | null;
    tax_doc_url?: string | null;
  }) {
    if (isMockMode) {
      const idx = MOCK_VEHICLES.findIndex((vehicle) => vehicle.id === payload.vehicleId);
      if (idx === -1) return null;
      MOCK_VEHICLES[idx] = {
        ...MOCK_VEHICLES[idx],
        insurance_provider: payload.insurance_provider || undefined,
        insurance_policy_number: payload.insurance_policy_number || undefined,
        insurance_status: payload.insurance_status || undefined,
        insurance_expiry_date: payload.insurance_expiry_date || undefined,
        insurance_doc_url: payload.insurance_doc_url || undefined,
        mot_doc_url: payload.mot_doc_url || undefined,
        tax_doc_url: payload.tax_doc_url || undefined
      };
      return MOCK_VEHICLES[idx];
    }

    const orgId = await getCurrentOrganisationId();
    const updatePayload = {
      insurance_provider: payload.insurance_provider ?? null,
      insurance_policy_number: payload.insurance_policy_number ?? null,
      insurance_status: payload.insurance_status ?? null,
      insurance_expiry_date: payload.insurance_expiry_date ?? null,
      insurance_doc_url: payload.insurance_doc_url ?? null,
      mot_doc_url: payload.mot_doc_url ?? null,
      tax_doc_url: payload.tax_doc_url ?? null,
      updated_at: new Date().toISOString()
    };

    const { data, error } = await supabase
      .from('vehicles')
      .update(updatePayload)
      .eq('id', payload.vehicleId)
      .eq('organisation_id', orgId)
      .select('*')
      .single();
    if (error) throw error;

    await AuditService.log({
      action: 'vehicle_compliance_updated',
      category: 'vehicle',
      description: 'Vehicle compliance details updated.',
      resource_type: 'vehicle',
      resource_id: payload.vehicleId,
      metadata: updatePayload
    });

    return data as Vehicle;
  },

  async saveDriverLicenceCompliance(payload: {
    driverId: string;
    license_expiry_date?: string | null;
    license_issue_country?: string | null;
    license_points?: number | null;
    license_status?: string | null;
    license_last_verified_at?: string | null;
    license_verified_by?: string | null;
  }) {
    if (isMockMode) {
      const idx = MOCK_DRIVERS.findIndex((driver) => driver.id === payload.driverId);
      if (idx === -1) return null;
      MOCK_DRIVERS[idx] = {
        ...MOCK_DRIVERS[idx],
        license_expiry_date: payload.license_expiry_date || undefined,
        license_issue_country: payload.license_issue_country || undefined,
        license_points: payload.license_points ?? undefined,
        license_status: payload.license_status || undefined,
        license_last_verified_at: payload.license_last_verified_at || undefined,
        license_verified_by: payload.license_verified_by || undefined
      };
      return MOCK_DRIVERS[idx];
    }

    const orgId = await getCurrentOrganisationId();
    const updatePayload = {
      license_expiry_date: payload.license_expiry_date ?? null,
      license_issue_country: payload.license_issue_country ?? null,
      license_points: payload.license_points ?? null,
      license_status: payload.license_status ?? null,
      license_last_verified_at: payload.license_last_verified_at ?? null,
      license_verified_by: payload.license_verified_by ?? null,
      updated_at: new Date().toISOString()
    };

    const { data, error } = await supabase
      .from('drivers')
      .update(updatePayload)
      .eq('id', payload.driverId)
      .eq('organisation_id', orgId)
      .select('*')
      .single();
    if (error) throw error;

    await AuditService.log({
      action: 'driver_licence_compliance_updated',
      category: 'driver',
      description: 'Driver licence compliance details updated.',
      resource_type: 'driver',
      resource_id: payload.driverId,
      metadata: updatePayload
    });

    return data as Driver;
  },

  async assignComplianceTask(taskId: string, userId: string | null) {
    if (isMockMode) {
      const task = MOCK_TASKS.find((item) => item.id === taskId);
      if (!task) return null;
      task.assignee_user_id = userId;
      return task;
    }

    const orgId = await getCurrentOrganisationId();
    const { data, error } = await supabase
      .from('tasks')
      .update({
        assignee_user_id: userId,
        updated_at: new Date().toISOString()
      })
      .eq('id', taskId)
      .eq('organisation_id', orgId)
      .select('*')
      .single();
    if (error) throw error;

    await AuditService.log({
      action: 'compliance_task_assigned',
      category: 'settings',
      description: 'Compliance task assignment updated.',
      resource_type: 'task',
      resource_id: taskId,
      metadata: { assignee_user_id: userId }
    });

    return data as Task;
  },

  async resolveComplianceTask(taskId: string, resolutionNote: string) {
    if (isMockMode) {
      const task = MOCK_TASKS.find((item) => item.id === taskId);
      if (!task) return null;
      task.status = 'resolved';
      task.metadata = {
        ...(task.metadata || {}),
        resolution_note: resolutionNote,
        resolved_at: new Date().toISOString()
      };
      return task;
    }

    const orgId = await getCurrentOrganisationId();
    const { data: currentTask } = await supabase
      .from('tasks')
      .select('metadata')
      .eq('id', taskId)
      .eq('organisation_id', orgId)
      .maybeSingle();

    const nextMetadata = {
      ...(currentTask as any)?.metadata,
      resolution_note: resolutionNote,
      resolved_at: new Date().toISOString()
    };

    const { data, error } = await supabase
      .from('tasks')
      .update({
        status: 'resolved',
        metadata: nextMetadata,
        updated_at: new Date().toISOString()
      })
      .eq('id', taskId)
      .eq('organisation_id', orgId)
      .select('*')
      .single();
    if (error) throw error;

    await AuditService.log({
      action: 'compliance_task_resolved',
      category: 'settings',
      description: 'Compliance task resolved.',
      resource_type: 'task',
      resource_id: taskId,
      metadata: { resolution_note: resolutionNote }
    });

    return data as Task;
  },

  async getComplianceCounts() {
    if (isMockMode) {
      const today = new Date();
      return {
        vehiclesOverdueDocs: MOCK_VEHICLES.filter((vehicle) => {
          const motDays = vehicle.mot_expiry_date ? daysUntilDate(new Date(vehicle.mot_expiry_date), today) : null;
          const taxDays = vehicle.tax_due_date ? daysUntilDate(new Date(vehicle.tax_due_date), today) : null;
          const insuranceDays = vehicle.insurance_expiry_date ? daysUntilDate(new Date(vehicle.insurance_expiry_date), today) : null;
          return (motDays !== null && motDays < 0) || (taxDays !== null && taxDays < 0) || (insuranceDays !== null && insuranceDays < 0);
        }).length,
        driversLicenceRisk: MOCK_DRIVERS.filter((driver) => {
          const due = parseDateOnly(driver.license_expiry_date);
          if (!due) return false;
          return daysUntilDate(due, today) <= 30;
        }).length,
        criticalBreachesOpen: MOCK_TASKS.filter((task) => task.status === 'open' && task.severity === 'critical').length
      };
    }

    const orgId = await getCurrentOrganisationId();
    const today = new Date();
    const [vehicleResp, driverResp, taskResp] = await Promise.all([
      supabase
        .from('vehicles')
        .select('mot_expiry_date, tax_due_date, insurance_expiry_date')
        .eq('organisation_id', orgId),
      supabase
        .from('drivers')
        .select('license_expiry_date, license_status')
        .eq('organisation_id', orgId),
      supabase
        .from('tasks')
        .select('id')
        .eq('organisation_id', orgId)
        .eq('status', 'open')
        .eq('severity', 'critical')
        .in('source', ['compliance', 'defect'])
    ]);

    if (vehicleResp.error || driverResp.error || taskResp.error) {
      throw vehicleResp.error || driverResp.error || taskResp.error;
    }

    const vehiclesOverdueDocs = (vehicleResp.data || []).filter((vehicle: any) => {
      const motDue = parseDateOnly(vehicle.mot_expiry_date);
      const taxDue = parseDateOnly(vehicle.tax_due_date);
      const insuranceDue = parseDateOnly(vehicle.insurance_expiry_date);
      return (
        (motDue && daysUntilDate(motDue, today) < 0) ||
        (taxDue && daysUntilDate(taxDue, today) < 0) ||
        (insuranceDue && daysUntilDate(insuranceDue, today) < 0)
      );
    }).length;

    const driversLicenceRisk = (driverResp.data || []).filter((driver: any) => {
      const due = parseDateOnly(driver.license_expiry_date);
      const status = String(driver.license_status || '').toLowerCase();
      return (due && daysUntilDate(due, today) <= 30) || ['expired', 'suspended', 'revoked'].includes(status);
    }).length;

    return {
      vehiclesOverdueDocs,
      driversLicenceRisk,
      criticalBreachesOpen: (taskResp.data || []).length
    };
  },

  async getDefects(filters?: { vehicleId?: string; status?: Defect['status']; severity?: Defect['severity'] }) {
    if (isMockMode) {
      return MOCK_DEFECTS.filter((defect) => {
        if (filters?.vehicleId && defect.vehicle_id !== filters.vehicleId) return false;
        if (filters?.status && defect.status !== filters.status) return false;
        if (filters?.severity && defect.severity !== filters.severity) return false;
        return true;
      });
    }

    const orgId = await getCurrentOrganisationId();
    let query = supabase
      .from('defects')
      .select('*')
      .eq('organisation_id', orgId)
      .order('created_at', { ascending: false });

    if (filters?.vehicleId) query = query.eq('vehicle_id', filters.vehicleId);
    if (filters?.status) query = query.eq('status', filters.status);
    if (filters?.severity) query = query.eq('severity', filters.severity);

    const { data, error } = await query;
    if (error) {
      const code = String((error as any)?.code || '');
      const rawMessage = String((error as any)?.message || '').toLowerCase();
      const details = String((error as any)?.details || '').toLowerCase();

      // Missing table/schema cache miss after migrations
      if (
        code === '42P01' ||
        code === 'PGRST205' ||
        rawMessage.includes("could not find the table 'defects'") ||
        rawMessage.includes('relation "defects" does not exist')
      ) {
        throw new Error(
          'Defects tables are unavailable in this environment. Apply migration 20260226141000_compliance_release_b.sql to the same Supabase project used by this app, then redeploy/restart and retry.'
        );
      }

      // Permission/RLS issues should surface distinctly from migration issues
      if (
        code === '42501' ||
        rawMessage.includes('permission denied') ||
        details.includes('permission denied')
      ) {
        throw new Error(
          'Defects access is blocked by database permissions/RLS for this user or tenant. Verify defects policies and organisation linkage.'
        );
      }
      throw error;
    }
    return (data || []) as Defect[];
  },

  async createDailyCheck(payload: {
    vehicleId: string;
    checkDate?: string;
    status: 'pass' | 'fail';
    notes?: string;
    checkedBy?: string;
    defect?: {
      title: string;
      description?: string;
      severity: Defect['severity'];
    };
  }) {
    const checkDate = payload.checkDate || new Date().toISOString().slice(0, 10);

    if (isMockMode) {
      const dailyCheck: DailyCheck = {
        id: `check-${Date.now()}`,
        organisation_id: 'mock',
        vehicle_id: payload.vehicleId,
        check_date: checkDate,
        status: payload.status,
        notes: payload.notes,
        checked_by: payload.checkedBy,
        created_at: new Date().toISOString()
      };
      MOCK_DAILY_CHECKS.unshift(dailyCheck);

      let defect: Defect | null = null;
      if (payload.status === 'fail' && payload.defect) {
        defect = await ApiService.reportDefect({
          vehicleId: payload.vehicleId,
          dailyCheckId: dailyCheck.id,
          title: payload.defect.title,
          description: payload.defect.description,
          severity: payload.defect.severity
        });
      }

      return { dailyCheck, defect };
    }

    const orgId = await getCurrentOrganisationId();
    const { data: dailyCheck, error } = await supabase
      .from('daily_checks')
      .insert({
        organisation_id: orgId,
        vehicle_id: payload.vehicleId,
        check_date: checkDate,
        status: payload.status,
        notes: payload.notes ?? null,
        checked_by: payload.checkedBy ?? null
      })
      .select('*')
      .single();
    if (error) throw error;

    let defect: Defect | null = null;
    if (payload.status === 'fail' && payload.defect) {
      defect = await ApiService.reportDefect({
        vehicleId: payload.vehicleId,
        dailyCheckId: (dailyCheck as any).id,
        title: payload.defect.title,
        description: payload.defect.description,
        severity: payload.defect.severity
      });
    }

    await AuditService.log({
      action: 'daily_check_created',
      category: 'vehicle',
      description: 'Daily vehicle check submitted.',
      resource_type: 'daily_check',
      resource_id: (dailyCheck as any).id,
      metadata: {
        vehicle_id: payload.vehicleId,
        status: payload.status
      }
    });

    return { dailyCheck: dailyCheck as DailyCheck, defect };
  },

  async reportDefect(payload: {
    vehicleId: string;
    dailyCheckId?: string | null;
    title: string;
    description?: string;
    severity: Defect['severity'];
  }) {
    if (isMockMode) {
      const defect: Defect = {
        id: `defect-${Date.now()}`,
        organisation_id: 'mock',
        vehicle_id: payload.vehicleId,
        daily_check_id: payload.dailyCheckId || null,
        title: payload.title,
        description: payload.description,
        severity: payload.severity,
        status: 'open',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      MOCK_DEFECTS.unshift(defect);
      MOCK_DEFECT_ACTIONS.unshift({
        id: `action-${Date.now()}`,
        defect_id: defect.id,
        organisation_id: 'mock',
        action_type: 'reported',
        notes: payload.description || null,
        created_at: new Date().toISOString()
      });

      if (payload.severity === 'critical') {
        await ApiService.createTask({
          title: `Critical defect reported - ${defect.title}`,
          description: defect.description,
          priority: 'High',
          type: 'assignment',
          relatedId: defect.vehicle_id,
          relatedEntityType: 'vehicle',
          source: 'defect',
          severity: 'critical',
          breachCode: `defect:${defect.id}:critical`,
          metadata: {
            defect_id: defect.id,
            vehicle_id: defect.vehicle_id
          }
        });
      }

      return defect;
    }

    const orgId = await getCurrentOrganisationId();
    const { data: defect, error } = await supabase
      .from('defects')
      .insert({
        organisation_id: orgId,
        vehicle_id: payload.vehicleId,
        daily_check_id: payload.dailyCheckId ?? null,
        title: payload.title,
        description: payload.description ?? null,
        severity: payload.severity,
        status: 'open'
      })
      .select('*')
      .single();
    if (error) throw error;

    await supabase.from('defect_actions').insert({
      organisation_id: orgId,
      defect_id: (defect as any).id,
      action_type: 'reported',
      notes: payload.description ?? null,
      metadata: {}
    });

    if ((defect as any).severity === 'critical') {
      await ApiService.createTask({
        title: `Critical defect reported - ${(defect as any).title}`,
        description: (defect as any).description || 'Vehicle blocked from dispatch until sign-off.',
        priority: 'High',
        type: 'assignment',
        relatedId: (defect as any).vehicle_id,
        relatedEntityType: 'vehicle',
        source: 'defect',
        severity: 'critical',
        breachCode: `defect:${(defect as any).id}:critical`,
        metadata: {
          defect_id: (defect as any).id,
          vehicle_id: (defect as any).vehicle_id
        }
      });
    }

    await AuditService.log({
      action: 'defect_reported',
      category: 'vehicle',
      description: 'Vehicle defect reported.',
      resource_type: 'defect',
      resource_id: (defect as any).id,
      metadata: {
        vehicle_id: payload.vehicleId,
        severity: payload.severity
      }
    });

    return defect as Defect;
  },

  async updateDefectStatus(defectId: string, status: Defect['status'], notes?: string) {
    const validTransitions: Record<Defect['status'], Defect['status'][]> = {
      open: ['in_review', 'resolved'],
      in_review: ['open', 'resolved'],
      resolved: ['in_review', 'signed_off'],
      signed_off: []
    };

    if (isMockMode) {
      const defect = MOCK_DEFECTS.find((item) => item.id === defectId);
      if (!defect) return null;
      const fromStatus = defect.status;
      if (!validTransitions[fromStatus].includes(status)) {
        throw new Error(`Invalid defect transition: ${fromStatus} -> ${status}`);
      }
      defect.status = status;
      defect.updated_at = new Date().toISOString();
      if (status === 'resolved') defect.resolved_at = new Date().toISOString();
      MOCK_DEFECT_ACTIONS.unshift({
        id: `action-${Date.now()}`,
        defect_id: defectId,
        organisation_id: 'mock',
        action_type: 'status_changed',
        from_status: fromStatus,
        to_status: status,
        notes: notes || null,
        created_at: new Date().toISOString()
      });
      return defect;
    }

    const orgId = await getCurrentOrganisationId();
    const { data: currentDefect } = await supabase
      .from('defects')
      .select('status')
      .eq('id', defectId)
      .eq('organisation_id', orgId)
      .maybeSingle();

    const fromStatus = (currentDefect as any)?.status as Defect['status'] | undefined;
    if (!fromStatus) throw new Error('Defect not found');
    if (!validTransitions[fromStatus].includes(status)) {
      throw new Error(`Invalid defect transition: ${fromStatus} -> ${status}`);
    }

    const { data, error } = await supabase
      .from('defects')
      .update({
        status,
        resolved_at: status === 'resolved' ? new Date().toISOString() : null,
        updated_at: new Date().toISOString()
      })
      .eq('id', defectId)
      .eq('organisation_id', orgId)
      .select('*')
      .single();
    if (error) throw error;

    await supabase.from('defect_actions').insert({
      organisation_id: orgId,
      defect_id: defectId,
      action_type: 'status_changed',
      from_status: fromStatus,
      to_status: status,
      notes: notes ?? null,
      metadata: {}
    });

    await AuditService.log({
      action: 'defect_status_updated',
      category: 'vehicle',
      description: 'Defect status updated.',
      resource_type: 'defect',
      resource_id: defectId,
      metadata: {
        status,
        notes: notes || null
      }
    });

    return data as Defect;
  },

  async signOffDefect(defectId: string, signoffPayload: { notes?: string; signedOffBy?: string }) {
    if (isMockMode) {
      const defect = MOCK_DEFECTS.find((item) => item.id === defectId);
      if (!defect) return null;
      if (defect.status !== 'resolved') {
        throw new Error('Only resolved defects can be signed off.');
      }
      defect.status = 'signed_off';
      defect.signed_off_at = new Date().toISOString();
      defect.updated_at = new Date().toISOString();
      MOCK_DEFECT_ACTIONS.unshift({
        id: `action-${Date.now()}`,
        defect_id: defectId,
        organisation_id: 'mock',
        action_type: 'signed_off',
        from_status: 'resolved',
        to_status: 'signed_off',
        notes: signoffPayload.notes || null,
        metadata: { signed_off_by: signoffPayload.signedOffBy || null },
        created_at: new Date().toISOString()
      });
      return defect;
    }

    const orgId = await getCurrentOrganisationId();
    const { data: currentDefect } = await supabase
      .from('defects')
      .select('status')
      .eq('id', defectId)
      .eq('organisation_id', orgId)
      .maybeSingle();
    if ((currentDefect as any)?.status !== 'resolved') {
      throw new Error('Only resolved defects can be signed off.');
    }

    const { data, error } = await supabase
      .from('defects')
      .update({
        status: 'signed_off',
        signed_off_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .eq('id', defectId)
      .eq('organisation_id', orgId)
      .select('*')
      .single();
    if (error) throw error;

    await supabase.from('defect_actions').insert({
      organisation_id: orgId,
      defect_id: defectId,
      action_type: 'signed_off',
      from_status: 'resolved',
      to_status: 'signed_off',
      notes: signoffPayload.notes ?? null,
      metadata: { signed_off_by: signoffPayload.signedOffBy || null },
      created_by: signoffPayload.signedOffBy ?? null
    });

    await supabase
      .from('tasks')
      .update({ status: 'resolved' })
      .eq('organisation_id', orgId)
      .eq('source', 'defect')
      .eq('status', 'open')
      .contains('metadata', { defect_id: defectId });

    await AuditService.log({
      action: 'defect_signed_off',
      category: 'vehicle',
      description: 'Defect signed off and dispatch block cleared.',
      resource_type: 'defect',
      resource_id: defectId,
      metadata: {
        notes: signoffPayload.notes || null
      }
    });

    return data as Defect;
  },

  async getVehicleDispatchStatus(vehicleId: string) {
    if (isMockMode) {
      const openCriticalCount = MOCK_DEFECTS.filter(
        (defect) =>
          defect.vehicle_id === vehicleId &&
          defect.severity === 'critical' &&
          ['open', 'in_review', 'resolved'].includes(defect.status)
      ).length;
      return {
        blocked: openCriticalCount > 0,
        openCriticalCount,
        reason: openCriticalCount > 0 ? 'Critical defect requires sign-off before dispatch.' : null
      };
    }

    const orgId = await getCurrentOrganisationId();
    const { data, error } = await supabase
      .from('defects')
      .select('id')
      .eq('organisation_id', orgId)
      .eq('vehicle_id', vehicleId)
      .eq('severity', 'critical')
      .in('status', ['open', 'in_review', 'resolved']);
    if (error) throw error;

    const openCriticalCount = (data || []).length;
    return {
      blocked: openCriticalCount > 0,
      openCriticalCount,
      reason: openCriticalCount > 0 ? 'Critical defect requires sign-off before dispatch.' : null
    };
  },

  async getDrivers() {
    if (isMockMode) return MOCK_DRIVERS;

    // Real Supabase implementation
    try {
      const orgId = await getCurrentOrganisationId();

      const { data, error } = await supabase
        .from('drivers')
        .select('*')
        .eq('organisation_id', orgId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching drivers:', error);
        throw error;
      }

      return (data || []).map((driver: any) => ({
        ...driver,
        license_document_url: driver.license_document_url ?? driver.license_doc_url ?? undefined
      }));
    } catch (error) {
      console.error('Failed to fetch drivers:', error);
      throw error;
    }
  },

  async getDriverByName(name: string) {
    if (isMockMode) {
      return MOCK_DRIVERS.find(d => d.name.toLowerCase() === name.toLowerCase()) || null;
    }

    // Real Supabase implementation
    try {
      const orgId = await getCurrentOrganisationId();

      const { data, error } = await supabase
        .from('drivers')
        .select('*')
        .eq('organisation_id', orgId)
        .ilike('name', name)
        .limit(1)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          return null;
        }
        console.error('Error fetching driver by name:', error);
        throw error;
      }

      return {
        ...(data as any),
        license_document_url: (data as any)?.license_document_url ?? (data as any)?.license_doc_url ?? undefined
      };
    } catch (error) {
      console.error('Failed to fetch driver by name:', error);
      return null;
    }
  },

  async createDriver(driver: Partial<Driver>) {
    if (isMockMode) {
      const newDriver: Driver = {
        id: `d-${Date.now()}`,
        name: driver.name || 'Unknown',
        email: driver.email || '',
        phone: driver.phone || '',
        license_number: driver.license_number || '',
        license_expiry_date: driver.license_expiry_date,
        license_issue_country: driver.license_issue_country,
        license_points: driver.license_points,
        license_status: driver.license_status,
        license_last_verified_at: driver.license_last_verified_at,
        license_verified_by: driver.license_verified_by,
        risk_score: 'Low',
        total_pcns: 0,
        active_hires: 0,
        organisation_id: 'mock'
      };
      MOCK_DRIVERS.push(newDriver);
      return newDriver;
    }

    // Real Supabase implementation
    try {
      const orgId = await getCurrentOrganisationId();

      const { data, error } = await supabase
        .from('drivers')
        .insert({
          organisation_id: orgId,
          name: driver.name || 'Unknown',
          email: driver.email,
          phone: driver.phone,
          license_number: driver.license_number,
          risk_score: driver.risk_score || 'Low',
          address_line1: driver.address_line1,
          city: driver.city,
          postcode: driver.postcode,
          license_doc_url: driver.license_document_url,
          license_expiry_date: driver.license_expiry_date || null,
          license_issue_country: driver.license_issue_country || null,
          license_points: driver.license_points ?? null,
          license_status: driver.license_status || null,
          license_last_verified_at: driver.license_last_verified_at || null,
          license_verified_by: driver.license_verified_by || null
        })
        .select()
        .single();

      if (error) {
        console.error('Error creating driver:', error);
        throw error;
      }

      await AuditService.log({
        action: 'driver_created',
        category: 'driver',
        description: 'Driver record created.',
        resource_type: 'driver',
        resource_id: (data as any)?.id,
        metadata: {
          name: driver.name || null,
          license_expiry_date: driver.license_expiry_date || null
        }
      });

      return {
        ...(data as any),
        license_document_url: (data as any)?.license_document_url ?? (data as any)?.license_doc_url ?? undefined
      };
    } catch (error) {
      console.error('Failed to create driver:', error);
      throw error;
    }
  },

  async getReports(dateRange: ReportDateRange = 'Last 6 Months'): Promise<ReportData> {
    if (isMockMode) {
      const authorityMap: Record<string, number> = {};
      let totalLiability = 0;

      MOCK_TICKETS.forEach(t => {
        const auth = t.issuing_authority || 'Unknown';
        const amount = Number(t.amount || 0) || 0;
        totalLiability += amount;
        authorityMap[auth] = (authorityMap[auth] || 0) + amount;
      });

      const costByCouncil = Object.entries(authorityMap)
        .map(([name, value]) => ({ name, value }))
        .sort((a, b) => b.value - a.value)
        .slice(0, 5);

      return {
        kpis: { totalSpend: totalLiability, totalSaved: 450, avgTicketCost: 65.50, appealSuccessRate: 80 },
        costByAuthority: costByCouncil,
        spendingTrend: [{ month: 'Jan', actual: 1250, projected: 1200 }, { month: 'Feb', actual: 980, projected: 1100 }, { month: 'Mar', actual: 1450, projected: 1150 }, { month: 'Apr', actual: 800, projected: 1050 }, { month: 'May', actual: 600, projected: 900 }],
        contraventionTypes: [{ name: 'Bus Lane', count: 45 }, { name: 'Parking', count: 32 }, { name: 'Moving Traffic', count: 21 }, { name: 'Congestion', count: 12 }, { name: 'Low Emission', count: 5 }],
        appealPerformance: [{ name: 'Won (Liability Transferred)', value: 65, color: '#10b981' }, { name: 'Won (Cancelled)', value: 15, color: '#3b82f6' }, { name: 'Lost (Paid)', value: 20, color: '#ef4444' }]
      };
    }

    const rangeStart = (() => {
      const now = new Date();
      switch (dateRange) {
        case 'Last 30 Days': {
          const d = new Date(now);
          d.setDate(d.getDate() - 30);
          return d;
        }
        case 'Last Quarter': {
          const d = new Date(now);
          d.setDate(d.getDate() - 90);
          return d;
        }
        case 'Year to Date':
          return new Date(now.getFullYear(), 0, 1);
        case 'Last 6 Months':
        default: {
          const d = new Date(now);
          d.setMonth(d.getMonth() - 6);
          return d;
        }
      }
    })();

    const now = new Date();

    try {
      const orgId = await getCurrentOrganisationId();
      const startIso = rangeStart.toISOString();
      const endIso = now.toISOString();

      const { data, error } = await supabase
        .from('tickets')
        .select('issue_date, amount, status, issuing_authority, contravention_type, decision')
        .eq('organisation_id', orgId)
        .gte('issue_date', startIso)
        .lte('issue_date', endIso)
        .order('issue_date', { ascending: true })
        .limit(10000);

      if (error) throw error;

      const tickets = (data || []) as any[];

      let totalLiability = 0;
      let totalSpend = 0;
      let totalSaved = 0;

      const authorityMap: Record<string, number> = {};
      const contraventionMap: Record<string, number> = {};
      const monthlyPaidMap = new Map<string, number>();

      let appealRelatedCount = 0;
      let appealSuccessCount = 0;
      const appealBuckets = { transferred: 0, cancelled: 0, lost: 0 };

      for (const t of tickets) {
        const amount = Number(t.amount || 0) || 0;
        totalLiability += amount;

        const authority = String(t.issuing_authority || 'Unknown').trim() || 'Unknown';
        authorityMap[authority] = (authorityMap[authority] || 0) + amount;

        const contraventionType = String(t.contravention_type || 'Unknown').trim() || 'Unknown';
        contraventionMap[contraventionType] = (contraventionMap[contraventionType] || 0) + 1;

        const status = String(t.status || '').toLowerCase();
        const decision = String(t.decision || '').toLowerCase();

        const isPaid = status === 'paid';
        const isSaved = status === 'cancelled' || status === 'appeal_won' || status === 'transferred';

        if (isPaid) totalSpend += amount;
        if (isSaved) totalSaved += amount;

        const appealRelated =
          decision === 'appeal' ||
          status === 'appealing' ||
          status.includes('appeal');

        if (appealRelated) {
          appealRelatedCount++;
          const isAppealSuccess = status === 'appeal_won' || status === 'cancelled' || status === 'transferred' || decision === 'transfer';
          if (isAppealSuccess) appealSuccessCount++;
        }

        if (status === 'transferred' || decision === 'transfer') appealBuckets.transferred++;
        else if (status === 'cancelled' || status === 'appeal_won') appealBuckets.cancelled++;
        else if (status === 'paid' || status === 'appeal_lost' || status === 'appeal_rejected') appealBuckets.lost++;

        const issueDate = String(t.issue_date || '');
        if (isPaid && issueDate) {
          const d = new Date(issueDate);
          const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
          monthlyPaidMap.set(key, (monthlyPaidMap.get(key) || 0) + amount);
        }
      }

      const avgTicketCost = tickets.length ? totalLiability / tickets.length : 0;
      const appealSuccessRate = appealRelatedCount ? Math.round((appealSuccessCount / appealRelatedCount) * 100) : 0;

      const costByAuthority = Object.entries(authorityMap)
        .map(([name, value]) => ({ name, value }))
        .sort((a, b) => b.value - a.value)
        .slice(0, 5);

      const contraventionTypes = Object.entries(contraventionMap)
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);

      const monthFmt = new Intl.DateTimeFormat('en-GB', { month: 'short', year: '2-digit' });
      const startMonth = new Date(rangeStart.getFullYear(), rangeStart.getMonth(), 1);
      const endMonth = new Date(now.getFullYear(), now.getMonth(), 1);

      const spendingTrend: { month: string; actual: number; projected: number }[] = [];
      for (let d = new Date(startMonth); d <= endMonth; d.setMonth(d.getMonth() + 1)) {
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        const actual = monthlyPaidMap.get(key) || 0;

        let projected = actual;
        const isCurrentMonth = d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
        if (isCurrentMonth) {
          const dayOfMonth = now.getDate();
          const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
          projected = dayOfMonth > 0 ? (actual / dayOfMonth) * daysInMonth : actual;
        }

        spendingTrend.push({
          month: monthFmt.format(d),
          actual: Math.round(actual),
          projected: Math.round(projected)
        });
      }

      const appealPerformance = [
        { name: 'Won (Liability Transferred)', value: appealBuckets.transferred, color: '#10b981' },
        { name: 'Won (Cancelled)', value: appealBuckets.cancelled, color: '#3b82f6' },
        { name: 'Lost (Paid)', value: appealBuckets.lost, color: '#ef4444' }
      ];

      return {
        kpis: {
          totalSpend: Math.round(totalLiability),
          totalSaved: Math.round(totalSaved),
          avgTicketCost: Number(avgTicketCost.toFixed(2)),
          appealSuccessRate
        },
        costByAuthority,
        spendingTrend,
        contraventionTypes,
        appealPerformance
      };
    } catch (e) {
      console.error('Failed to load reports:', e);
      throw e;
    }
  },

  async getTrialSavingsReport(reportType: TrialSavingsReportType = '3pcn_snapshot'): Promise<TrialSavingsReportData> {
    if (isMockMode) {
      const mockOrg = {
        trial_status: 'active',
        trial_ends_at: new Date(Date.now() + 86400000 * 4).toISOString(),
        trial_pcn_limit: 3,
        trial_pcns_processed: reportType === '10pcn_savings_report' ? 10 : 3,
        trial_extension_status: reportType === '10pcn_savings_report' ? 'unlocked' : 'none',
      };
      return buildTrialSavingsReport(reportType, mockOrg, MOCK_TICKETS.slice(0, TRIAL_REPORT_LIMITS[reportType]));
    }

    const orgId = await getCurrentOrganisationId();
    const targetCount = TRIAL_REPORT_LIMITS[reportType];

    const [{ data: org, error: orgError }, { data: tickets, error: ticketsError }] = await Promise.all([
      supabase
        .from('organisations')
        .select('id, subscription_tier, current_plan, trial_status, trial_ends_at, trial_expires_at, trial_pcn_limit, trial_pcns_processed, trial_extension_status, commercial_status')
        .eq('id', orgId)
        .maybeSingle(),
      supabase
        .from('tickets')
        .select('id, pcn_number, vrn, issue_date, created_at, issuing_authority, location, amount, discount_amount, status, stage, decision, hire_id, assigned_driver_name, assigned_driver_email, driver_charge_status, driver_charge_amount, admin_fee_charged')
        .eq('organisation_id', orgId)
        .order('created_at', { ascending: true })
        .limit(targetCount)
    ]);

    if (orgError) throw orgError;
    if (ticketsError) throw ticketsError;

    const report = buildTrialSavingsReport(reportType, org, (tickets || []) as Partial<Ticket>[]);

    if (report.canViewReport) {
      try {
        await supabase.rpc('cache_fleetmode_trial_report', {
          p_report_type: report.reportType,
          p_pcns_included: report.rows.map((row) => row.id),
          p_total_cost_exposure: report.metrics.totalCostExposure,
          p_estimated_time_saved_minutes: report.metrics.estimatedTimeSavedMinutes,
          p_deadline_risks_count: report.metrics.deadlineRisksCount,
          p_liability_ready_count: report.metrics.liabilityReadyCount,
          p_evidence_gap_count: report.metrics.evidenceGapCount,
          p_recovery_visibility_summary_json: {
            recoverySummary: report.recoverySummary,
            recoveryVisibleCount: report.metrics.recoveryVisibleCount,
            recoveryOutstandingAmount: report.metrics.recoveryOutstandingAmount,
            repeatDrivers: report.repeatDrivers,
            repeatLocations: report.repeatLocations,
            recommendedPlan: report.recommendedPlan,
          },
        });
      } catch (cacheError) {
        console.warn('Unable to cache FleetMode trial report:', cacheError);
      }
    }

    return report;
  },

  async requestTrialSavingsExtension(): Promise<void> {
    if (isMockMode) return;
    const { error } = await supabase.rpc('request_fleetmode_trial_extension');
    if (error) throw error;
  },

  async getVehicles() {
    if (isMockMode) return MOCK_VEHICLES;

    // Real Supabase implementation
    try {
      const orgId = await getCurrentOrganisationId();

      const { data, error } = await supabase
        .from('vehicles')
        .select('*')
        .eq('organisation_id', orgId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching vehicles:', error);
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error('Failed to fetch vehicles:', error);
      throw error;
    }
  },

  async saveVehicle(v: Partial<Vehicle>): Promise<Vehicle | undefined> {
    if (isMockMode) {
      if (v.id) {
        const idx = MOCK_VEHICLES.findIndex(existing => existing.id === v.id);
        if (idx !== -1) {
          MOCK_VEHICLES[idx] = { ...MOCK_VEHICLES[idx], ...v };
          return MOCK_VEHICLES[idx];
        }
      } else {
        const newVehicle = { id: `v-${Date.now()}`, organisation_id: 'mock', status: 'Active', notes: '', tags: [], ...v } as Vehicle;
        MOCK_VEHICLES.push(newVehicle);
        return newVehicle;
      }
      return undefined;
    }

    // Real Supabase implementation
    try {
      const orgId = await getCurrentOrganisationId();
      const vrmNormalized = v.vrm ? normalizeVrm(String(v.vrm)) : '';

      if (v.id) {
        // Update existing vehicle
        const { data, error } = await supabase
          .from('vehicles')
          .update({
            vrm: vrmNormalized || v.vrm,
            make: v.make,
            model: v.model,
            colour: v.colour,
            status: v.status,
            tags: v.tags,
            notes: v.notes,
            // DVLA fields
            year_of_manufacture: v.year_of_manufacture || null,
            fuel_type: v.fuel_type || null,
            engine_capacity: v.engine_capacity || null,
            co2_emissions: v.co2_emissions || null,
            marked_for_export: v.marked_for_export || false,
            tax_status: v.tax_status || null,
            tax_due_date: v.tax_due_date || null,
            mot_status: v.mot_status || null,
            mot_expiry_date: v.mot_expiry_date || null,
            insurance_provider: v.insurance_provider || null,
            insurance_policy_number: v.insurance_policy_number || null,
            insurance_status: v.insurance_status || null,
            insurance_expiry_date: v.insurance_expiry_date || null,
            insurance_doc_url: v.insurance_doc_url || null,
            mot_doc_url: v.mot_doc_url || null,
            tax_doc_url: v.tax_doc_url || null,
            updated_at: new Date().toISOString()
          })
          .eq('id', v.id)
          .eq('organisation_id', orgId)
          .select()
          .single();

        if (error) {
          console.error('Error updating vehicle:', error);

          // Handle duplicate VRM error when updating
          if ((error as any)?.code === '23505' && (error as any)?.message?.includes('vehicles_organisation_id_vrm_key')) {
            throw new Error(`Another vehicle with registration ${v.vrm} already exists in your fleet`);
          }

          throw error;
        }

        await AuditService.log({
          action: 'vehicle_updated',
          category: 'vehicle',
          description: 'Vehicle record updated.',
          resource_type: 'vehicle',
          resource_id: v.id,
          metadata: { vrm: vrmNormalized || v.vrm || null }
        });

        return data as Vehicle;
      } else {
        // Create new vehicle
        const { data, error } = await supabase
          .from('vehicles')
          .insert({
            organisation_id: orgId,
            vrm: vrmNormalized || v.vrm || '',
            make: v.make,
            model: v.model,
            colour: v.colour,
            status: v.status || 'Active',
            tags: v.tags || [],
            notes: v.notes || '',
            // DVLA fields
            year_of_manufacture: v.year_of_manufacture || null,
            fuel_type: v.fuel_type || null,
            engine_capacity: v.engine_capacity || null,
            co2_emissions: v.co2_emissions || null,
            marked_for_export: v.marked_for_export || false,
            tax_status: v.tax_status || null,
            tax_due_date: v.tax_due_date || null,
            mot_status: v.mot_status || null,
            mot_expiry_date: v.mot_expiry_date || null,
            insurance_provider: v.insurance_provider || null,
            insurance_policy_number: v.insurance_policy_number || null,
            insurance_status: v.insurance_status || null,
            insurance_expiry_date: v.insurance_expiry_date || null,
            insurance_doc_url: v.insurance_doc_url || null,
            mot_doc_url: v.mot_doc_url || null,
            tax_doc_url: v.tax_doc_url || null
          })
          .select()
          .single();

        if (error) {
          console.error('Error creating vehicle:', error);

          // Handle duplicate VRM error
          if ((error as any)?.code === '23505' && (error as any)?.message?.includes('vehicles_organisation_id_vrm_key')) {
            throw new Error(`Vehicle with registration ${v.vrm} already exists in your fleet`);
          }

          throw error;
        }

        await AuditService.log({
          action: 'vehicle_created',
          category: 'vehicle',
          description: 'Vehicle record created.',
          resource_type: 'vehicle',
          resource_id: (data as any)?.id,
          metadata: { vrm: vrmNormalized || v.vrm || null }
        });

        return data as Vehicle;
      }
    } catch (error: any) {
      console.error('Failed to save vehicle:', error);

      // Handle duplicate VRM error
      if (error?.code === '23505' && error?.message?.includes('vehicles_organisation_id_vrm_key')) {
        throw new Error(`This vehicle registration already exists in your fleet`);
      }

      throw error;
    }
  },

  async deleteVehicle(id: string) {
    if (isMockMode) {
      const idx = MOCK_VEHICLES.findIndex(v => v.id === id);
      if (idx !== -1) MOCK_VEHICLES.splice(idx, 1);
      return;
    }

    // Real Supabase implementation
    try {
      const orgId = await getCurrentOrganisationId();

      const { error } = await supabase
        .from('vehicles')
        .delete()
        .eq('id', id)
        .eq('organisation_id', orgId);

      if (error) {
        console.error('Error deleting vehicle:', error);
        throw error;
      }
    } catch (error) {
      console.error('Failed to delete vehicle:', error);
      throw error;
    }
  },
  async importVehicles(file: File, opts?: { onProgress?: (p: ImportProgress) => void }): Promise<CsvImportResult> {
    const onProgress = opts?.onProgress;
    if (isMockMode) {
      onProgress?.({ percent: 5, message: 'Reading CSV…' });
      const text = await readFileAsText(file);
      onProgress?.({ percent: 20, message: 'Parsing CSV…' });
      const rows = parseCsv(text);
      if (rows.length === 0) return { success: 0, errors: ['CSV is empty'] };

      onProgress?.({ percent: 35, message: 'Validating rows…' });
      const headerRow = rows[0].map(normalizeKey);
      const hasHeader = headerRow.includes('vrm') || headerRow.includes('registration') || headerRow.includes('registration_number');
      const headers = hasHeader ? headerRow : ['vrm', 'make', 'model', 'colour', 'tags', 'status', 'notes'];
      const dataRows = hasHeader ? rows.slice(1) : rows;

      const idx = {
        vrm: headers.findIndex((h) => h === 'vrm' || h === 'registration' || h === 'registration_number'),
        make: headers.findIndex((h) => h === 'make'),
        model: headers.findIndex((h) => h === 'model'),
        colour: headers.findIndex((h) => h === 'colour' || h === 'color'),
        tags: headers.findIndex((h) => h === 'tags' || h === 'tag'),
        status: headers.findIndex((h) => h === 'status'),
        notes: headers.findIndex((h) => h === 'notes' || h === 'note')
      };

      if (idx.vrm === -1) return { success: 0, errors: ['Missing required CSV header: VRM'] };

      const errors: string[] = [];
      const seen = new Set<string>();
      let success = 0;

      dataRows.forEach((r, i) => {
        const rowNum = (hasHeader ? 2 : 1) + i;
        const vrmRaw = r[idx.vrm] ?? '';
        const vrm = normalizeVrm(vrmRaw);
        if (!vrm) return;
        if (!isLikelyUkVrm(vrm)) {
          errors.push(`Row ${rowNum}: Invalid VRM format`);
          return;
        }
        if (seen.has(vrm)) {
          errors.push(`Row ${rowNum}: Duplicate VRM in CSV (${vrm})`);
          return;
        }
        seen.add(vrm);

        MOCK_VEHICLES.push({
          id: `imp-${Date.now()}-${rowNum}`,
          vrm,
          make: idx.make >= 0 ? (r[idx.make] ?? '') : '',
          model: idx.model >= 0 ? (r[idx.model] ?? '') : '',
          colour: idx.colour >= 0 ? (r[idx.colour] ?? '') : '',
          status: (idx.status >= 0 ? (r[idx.status] ?? '') : '') === 'Inactive' ? 'Inactive' : 'Active',
          notes: idx.notes >= 0 ? (r[idx.notes] ?? 'Imported via CSV') : 'Imported via CSV',
          tags: idx.tags >= 0 && r[idx.tags] ? String(r[idx.tags]).split(/[;,]/).map((t) => t.trim()).filter(Boolean) : [],
          organisation_id: 'mock'
        } as Vehicle);
        success++;
        if (i % 20 === 0) {
          const pct = 35 + Math.floor((i / Math.max(1, dataRows.length)) * 60);
          onProgress?.({ percent: clamp(pct, 35, 95), message: 'Importing…' });
        }
      });

      onProgress?.({ percent: 100, message: 'Done' });
      return { success, errors };
    }

    onProgress?.({ percent: 5, message: 'Reading CSV…' });
    const text = await readFileAsText(file);
    onProgress?.({ percent: 20, message: 'Parsing CSV…' });
    const rows = parseCsv(text);
    if (rows.length === 0) return { success: 0, errors: ['CSV is empty'] };

    onProgress?.({ percent: 35, message: 'Validating rows…' });
    const headerRow = rows[0].map(normalizeKey);
    const hasHeader = headerRow.includes('vrm') || headerRow.includes('registration') || headerRow.includes('registration_number');
    const headers = hasHeader ? headerRow : ['vrm', 'make', 'model', 'colour', 'tags', 'status', 'notes'];
    const dataRows = hasHeader ? rows.slice(1) : rows;

    const idx = {
      vrm: headers.findIndex((h) => h === 'vrm' || h === 'registration' || h === 'registration_number'),
      make: headers.findIndex((h) => h === 'make'),
      model: headers.findIndex((h) => h === 'model'),
      colour: headers.findIndex((h) => h === 'colour' || h === 'color'),
      tags: headers.findIndex((h) => h === 'tags' || h === 'tag'),
      status: headers.findIndex((h) => h === 'status'),
      notes: headers.findIndex((h) => h === 'notes' || h === 'note')
    };

    if (idx.vrm === -1) return { success: 0, errors: ['Missing required CSV header: VRM'] };

    const errors: string[] = [];
    const seen = new Set<string>();
    const candidates: { rowNum: number; vehicle: Partial<Vehicle> }[] = [];

    dataRows.forEach((r, i) => {
      const rowNum = (hasHeader ? 2 : 1) + i;
      const vrmRaw = r[idx.vrm] ?? '';
      const vrm = normalizeVrm(vrmRaw);
      if (!vrm) return;
      if (!isLikelyUkVrm(vrm)) {
        errors.push(`Row ${rowNum}: Invalid VRM format`);
        return;
      }
      if (seen.has(vrm)) {
        errors.push(`Row ${rowNum}: Duplicate VRM in CSV (${vrm})`);
        return;
      }
      seen.add(vrm);

      candidates.push({
        rowNum,
        vehicle: {
          vrm,
          make: idx.make >= 0 ? (r[idx.make] ?? '') : '',
          model: idx.model >= 0 ? (r[idx.model] ?? '') : '',
          colour: idx.colour >= 0 ? (r[idx.colour] ?? '') : '',
          status: (idx.status >= 0 ? (r[idx.status] ?? '') : '') === 'Inactive' ? 'Inactive' : 'Active',
          notes: idx.notes >= 0 ? (r[idx.notes] ?? 'Imported via CSV') : 'Imported via CSV',
          tags: idx.tags >= 0 && r[idx.tags] ? String(r[idx.tags]).split(/[;,]/).map((t) => t.trim()).filter(Boolean) : []
        }
      });
    });

    if (candidates.length === 0) return { success: 0, errors: errors.length ? errors : ['No valid rows found'] };

    onProgress?.({ percent: 55, message: 'Checking existing vehicles…' });
    const orgId = await getCurrentOrganisationId();
    const vrms = candidates.map((c) => c.vehicle.vrm!).filter(Boolean);

    // Detect duplicates against existing vehicle records
    const existingSet = new Set<string>();
    for (let i = 0; i < vrms.length; i += 100) {
      const chunk = vrms.slice(i, i + 100);
      const { data, error } = await supabase
        .from('vehicles')
        .select('vrm')
        .eq('organisation_id', orgId)
        .in('vrm', chunk);
      if (error) throw error;
      (data || []).forEach((v: any) => existingSet.add(normalizeVrm(v.vrm)));
    }

    const toInsert = candidates.filter((c) => {
      const vrm = c.vehicle.vrm!;
      if (existingSet.has(vrm)) {
        errors.push(`Row ${c.rowNum}: VRM already exists in your fleet (${vrm})`);
        return false;
      }
      return true;
    });

    let success = 0;
    const insertPayload = toInsert.map((c) => ({
      organisation_id: orgId,
      vrm: c.vehicle.vrm,
      make: c.vehicle.make,
      model: c.vehicle.model,
      colour: c.vehicle.colour,
      status: c.vehicle.status || 'Active',
      notes: c.vehicle.notes || '',
      tags: c.vehicle.tags || []
    }));

    onProgress?.({ percent: 70, message: 'Saving vehicles…' });
    for (let i = 0; i < insertPayload.length; i += 50) {
      const chunk = insertPayload.slice(i, i + 50);
      const { error } = await supabase.from('vehicles').insert(chunk);
      if (!error) {
        success += chunk.length;
        const pct = 70 + Math.floor(((i + chunk.length) / Math.max(1, insertPayload.length)) * 25);
        onProgress?.({ percent: clamp(pct, 70, 95), message: 'Saving vehicles…' });
        continue;
      }

      // Fallback to per-row inserts for better error reporting
      for (let j = 0; j < chunk.length; j++) {
        const payload = chunk[j];
        const rowNum = toInsert[i + j]?.rowNum ?? 0;
        const { error: rowErr } = await supabase.from('vehicles').insert(payload);
        if (rowErr) {
          errors.push(`Row ${rowNum || '?'}: ${rowErr.message}`);
        } else {
          success++;
        }
        if (j % 10 === 0) {
          const done = Math.min(insertPayload.length, i + j + 1);
          const pct = 70 + Math.floor((done / Math.max(1, insertPayload.length)) * 25);
          onProgress?.({ percent: clamp(pct, 70, 95), message: 'Saving vehicles…' });
        }
      }
    }

    onProgress?.({ percent: 100, message: 'Done' });
    return { success, errors };
  },

  async importTicketsFromCsv(file: File, opts?: { onProgress?: (p: ImportProgress) => void }): Promise<CsvImportResult> {
    const onProgress = opts?.onProgress;
    onProgress?.({ percent: 5, message: 'Reading CSV…' });
    const text = await readFileAsText(file);
    onProgress?.({ percent: 20, message: 'Parsing CSV…' });
    const rows = parseCsv(text);
    if (rows.length === 0) return { success: 0, errors: ['CSV is empty'] };

    onProgress?.({ percent: 35, message: 'Validating rows…' });
    const headerRow = rows[0].map(normalizeKey);
    const hasHeader = headerRow.includes('vrm') || headerRow.includes('date') || headerRow.includes('location') || headerRow.includes('amount');
    const headers = hasHeader ? headerRow : ['vrm', 'date', 'location', 'amount', 'pcn_number', 'authority'];
    const dataRows = hasHeader ? rows.slice(1) : rows;

    const idx = {
      vrm: headers.findIndex((h) => h === 'vrm' || h === 'registration' || h === 'registration_number' || h === 'vehicle'),
      date: headers.findIndex((h) => h === 'date' || h === 'contravention_date' || h === 'contravention'),
      location: headers.findIndex((h) => h === 'location' || h === 'place'),
      amount: headers.findIndex((h) => h === 'amount' || h === 'charge' || h === 'penalty'),
      pcn: headers.findIndex((h) => h === 'pcn' || h === 'pcn_number' || h === 'pcn_no' || h === 'ticket'),
      authority: headers.findIndex((h) => h === 'authority' || h === 'council' || h === 'issuing_authority'),
      issueDate: headers.findIndex((h) => h === 'issue_date')
    };

    const missingHeaders: string[] = [];
    if (idx.vrm === -1) missingHeaders.push('VRM');
    if (idx.date === -1) missingHeaders.push('Date');
    if (idx.location === -1) missingHeaders.push('Location');
    if (idx.amount === -1) missingHeaders.push('Amount');
    if (missingHeaders.length) return { success: 0, errors: [`Missing required CSV header(s): ${missingHeaders.join(', ')}`] };

    const errors: string[] = [];
    const seenKeys = new Set<string>();
    const seenProvidedPcn = new Set<string>();
    const candidates: { rowNum: number; ticket: Partial<Ticket>; pcnProvided: boolean }[] = [];
    const today = Date.now();
    const minYear = 2000;

    dataRows.forEach((r, i) => {
      const rowNum = (hasHeader ? 2 : 1) + i;
      const vrmRaw = r[idx.vrm] ?? '';
      const vrm = normalizeVrm(vrmRaw);
      if (!vrm) return;
      if (!isLikelyUkVrm(vrm)) {
        errors.push(`Row ${rowNum}: Invalid VRM format`);
        return;
      }

      const dateRaw = r[idx.date] ?? '';
      const contraIso = parseUkDateToIso(String(dateRaw));
      if (!contraIso) {
        errors.push(`Row ${rowNum}: Invalid date format`);
        return;
      }
      // Use validated ISO string to create Date object safely
      const contraDate = new Date(contraIso);
      const contraMs = contraDate.getTime();
      const contraYear = contraDate.getUTCFullYear();
      if (contraYear < minYear) {
        errors.push(`Row ${rowNum}: Date is before ${minYear}`);
        return;
      }
      if (contraMs > today + 24 * 60 * 60 * 1000) {
        errors.push(`Row ${rowNum}: Date cannot be in the future`);
        return;
      }

      const location = String(r[idx.location] ?? '').trim();
      if (!location) {
        errors.push(`Row ${rowNum}: Location is required`);
        return;
      }

      const amountRaw = r[idx.amount] ?? '';
      const amount = parsePounds(String(amountRaw));
      if (amount === null || amount <= 0) {
        errors.push(`Row ${rowNum}: Amount must be a positive number`);
        return;
      }
      if (amount > 10_000) {
        errors.push(`Row ${rowNum}: Amount looks too high`);
        return;
      }

      const key = `${vrm}|${contraIso.slice(0, 10)}|${location.toLowerCase()}`;
      if (seenKeys.has(key)) {
        errors.push(`Row ${rowNum}: Duplicate VRM/Date/Location in CSV`);
        return;
      }
      seenKeys.add(key);

      const rawPcn = idx.pcn >= 0 ? String(r[idx.pcn] ?? '').trim() : '';
      const pcnProvided = rawPcn.length > 0;
      const pcn_number = pcnProvided ? rawPcn : `CSV-${Date.now()}-${rowNum}-${Math.random().toString(16).slice(2, 6)}`;
      if (pcnProvided) {
        const keyPcn = pcn_number.toLowerCase();
        if (seenProvidedPcn.has(keyPcn)) {
          errors.push(`Row ${rowNum}: Duplicate PCN in CSV (${pcn_number})`);
          return;
        }
        seenProvidedPcn.add(keyPcn);
      }

      const issuing_authority = idx.authority >= 0 ? String(r[idx.authority] ?? '').trim() : '';

      const issueIso = idx.issueDate >= 0 ? parseUkDateToIso(String(r[idx.issueDate] ?? '')) : null;

      candidates.push({
        rowNum,
        pcnProvided,
        ticket: {
          pcn_number,
          vrn: vrm,
          contravention_date: contraIso,
          issue_date: issueIso || contraIso,
          location,
          amount,
          issuing_authority: issuing_authority || 'Unknown Authority',
          stage: 'PCN Issued',
          status: 'unassigned'
        }
      });
    });

    if (candidates.length === 0) return { success: 0, errors: errors.length ? errors : ['No valid rows found'] };

    if (isMockMode) {
      candidates.forEach((c, i) => {
        MOCK_TICKETS.unshift({
          id: `csv-${Date.now()}-${i}`,
          pcn_number: c.ticket.pcn_number || `CSV-${i}`,
          vrn: c.ticket.vrn || 'UNKNOWN',
          issue_date: c.ticket.issue_date || new Date().toISOString(),
          contravention_date: c.ticket.contravention_date || new Date().toISOString(),
          stage: c.ticket.stage || 'PCN Issued',
          amount: c.ticket.amount || 0,
          discount_amount: c.ticket.discount_amount,
          status: 'unassigned',
          location: c.ticket.location || 'Unknown',
          issuing_authority: c.ticket.issuing_authority || 'Unknown Authority',
          organisation_id: 'mock',
          vehicle_id: 'unknown',
          hire_id: null,
          timeline: [{ id: `t-${Date.now()}`, date: new Date().toISOString(), title: 'Ticket Created', description: 'Imported via CSV', type: 'system' }]
        } as any);
      });
      onProgress?.({ percent: 100, message: 'Done' });
      return { success: candidates.length, errors };
    }

    onProgress?.({ percent: 55, message: 'Checking duplicates…' });
    const orgId = await getCurrentOrganisationId();

    // Duplicate detection against existing tickets (VRM + date window)
    const uniqueVrms = Array.from(new Set(candidates.map((c) => c.ticket.vrn!).filter(Boolean)));
    const dates = candidates.map((c) => c.ticket.contravention_date!).filter(Boolean);
    const minDate = dates.reduce((a, b) => (a < b ? a : b));
    const maxDate = dates.reduce((a, b) => (a > b ? a : b));

    const existingKeys = new Set<string>();
    for (let i = 0; i < uniqueVrms.length; i += 50) {
      const chunkVrms = uniqueVrms.slice(i, i + 50);
      const { data, error } = await supabase
        .from('tickets')
        .select('vrn, contravention_date, location')
        .eq('organisation_id', orgId)
        .in('vrn', chunkVrms)
        .gte('contravention_date', minDate)
        .lte('contravention_date', maxDate);
      if (error) throw error;
      (data || []).forEach((t: any) => {
        const key = `${normalizeVrm(t.vrn)}|${String(t.contravention_date).slice(0, 10)}|${String(t.location || '').toLowerCase()}`;
        existingKeys.add(key);
      });
    }

    const toInsert = candidates.filter((c) => {
      const key = `${c.ticket.vrn}|${String(c.ticket.contravention_date).slice(0, 10)}|${String(c.ticket.location || '').toLowerCase()}`;
      if (existingKeys.has(key)) {
        errors.push(`Row ${c.rowNum}: Possible duplicate ticket already exists for this VRM/date/location`);
        return false;
      }
      return true;
    });

    // Optional: PCN duplicates (if provided in CSV) — merge instead of creating new rows
    const providedPcns = candidates
      .filter((c) => c.pcnProvided)
      .map((c) => normalizePcnNumber(c.ticket.pcn_number))
      .filter(Boolean) as string[];
    const pcnsToMerge = new Set<string>();
    if (providedPcns.length) {
      const existingPcnSet = new Set<string>();
      for (let i = 0; i < providedPcns.length; i += 100) {
        const chunk = providedPcns.slice(i, i + 100);
        const { data, error } = await supabase
          .from('tickets')
          .select('pcn_number')
          .eq('organisation_id', orgId)
          .in('pcn_number', chunk);
        if (error) throw error;
        (data || []).forEach((t: any) => existingPcnSet.add(String(t.pcn_number).toLowerCase()));
      }

      for (const c of candidates) {
        if (!c.pcnProvided) continue;
        const pcn = normalizePcnNumber(c.ticket.pcn_number);
        if (pcn && existingPcnSet.has(pcn.toLowerCase())) pcnsToMerge.add(pcn.toLowerCase());
      }
    }

    const toInsertAfterPcn = toInsert.filter((c) => {
      if (!c.pcnProvided) return true;
      const pcn = normalizePcnNumber(c.ticket.pcn_number);
      return !(pcn && pcnsToMerge.has(pcn.toLowerCase()));
    });

    let success = 0;
    let merged = 0;
    const insertedTicketIds: string[] = [];

    const buildPayload = (t: Partial<Ticket>) => ({
      organisation_id: orgId,
      pcn_number: normalizePcnNumber(t.pcn_number) || `CSV-${Date.now()}-${Math.random().toString(16).slice(2, 6)}`,
      vrn: t.vrn || 'UNKNOWN',
      issue_date: t.issue_date || new Date().toISOString(),
      contravention_date: t.contravention_date || new Date().toISOString(),
      stage: t.stage || 'PCN Issued',
      amount: t.amount || 0,
      discount_amount: t.discount_amount ?? null,
      status: 'unassigned',
      location: t.location || 'Unknown',
      issuing_authority: t.issuing_authority || 'Unknown Authority',
      contravention_type: t.contravention_type,
      document_url: t.document_url,
      low_confidence_fields: t.low_confidence_fields || [],
      timeline: JSON.stringify([
        {
          id: `t-${Date.now()}`,
          date: new Date().toISOString(),
          title: 'Ticket Created',
          description: 'Imported via CSV',
          type: 'system'
        }
      ])
    });

    onProgress?.({ percent: 70, message: 'Saving tickets…' });
    for (let i = 0; i < toInsertAfterPcn.length; i += 25) {
      const chunk = toInsertAfterPcn.slice(i, i + 25);
      const payload = chunk.map((c) => buildPayload(c.ticket));
      const { data, error } = await supabase.from('tickets').insert(payload).select('id');
      if (!error) {
        success += payload.length;
        (data || []).forEach((r: any) => insertedTicketIds.push(r.id));
        const pct = 70 + Math.floor(((i + payload.length) / Math.max(1, toInsertAfterPcn.length)) * 25);
        onProgress?.({ percent: clamp(pct, 70, 95), message: 'Saving tickets…' });
        continue;
      }

      // Fallback to per-row inserts for better error reporting
      for (const row of chunk) {
        const { data: rowData, error: rowErr } = await supabase.from('tickets').insert(buildPayload(row.ticket)).select('id').single();
        if (rowErr) {
          // If duplicate PCN, merge into the existing ticket instead of creating a new one
          if ((rowErr as any)?.code === '23505' && row.pcnProvided) {
            try {
              const pcn = normalizePcnNumber(row.ticket.pcn_number);
              const { data: existing, error: fetchErr } = await supabase
                .from('tickets')
                .select('*')
                .eq('organisation_id', orgId)
                .eq('pcn_number', pcn)
                .single();
              if (fetchErr || !existing) throw rowErr;
              await ApiService.mergeDuplicateTicket(existing, row.ticket, 'CSV import');
              merged++;
              continue;
            } catch (e: any) {
              errors.push(`Row ${row.rowNum}: ${e?.message || rowErr.message}`);
              continue;
            }
          }
          errors.push(`Row ${row.rowNum}: ${rowErr.message}`);
        } else {
          success++;
          if (rowData?.id) insertedTicketIds.push(rowData.id);
        }
      }
    }

    if (pcnsToMerge.size) {
      onProgress?.({ percent: 92, message: 'Merging duplicates…' });
      const mergeCandidates = candidates.filter((c) => {
        if (!c.pcnProvided) return false;
        const pcn = normalizePcnNumber(c.ticket.pcn_number);
        return !!(pcn && pcnsToMerge.has(pcn.toLowerCase()));
      });

      const mergePcns = mergeCandidates.map((c) => normalizePcnNumber(c.ticket.pcn_number)).filter(Boolean) as string[];
      const existingByPcn = new Map<string, any>();
      for (let i = 0; i < mergePcns.length; i += 100) {
        const chunk = mergePcns.slice(i, i + 100);
        const { data, error } = await supabase
          .from('tickets')
          .select('*')
          .eq('organisation_id', orgId)
          .in('pcn_number', chunk);
        if (error) throw error;
        (data || []).forEach((t: any) => existingByPcn.set(String(t.pcn_number).toUpperCase(), t));
      }

      for (const c of mergeCandidates) {
        const pcn = normalizePcnNumber(c.ticket.pcn_number);
        const existing = pcn ? existingByPcn.get(pcn.toUpperCase()) : null;
        if (!existing) {
          errors.push(`Row ${c.rowNum}: Could not find existing ticket to merge (${c.ticket.pcn_number})`);
          continue;
        }
        try {
          await ApiService.mergeDuplicateTicket(existing, c.ticket, 'CSV import');
          merged++;
        } catch (e: any) {
          errors.push(`Row ${c.rowNum}: ${e?.message || 'Failed to merge duplicate ticket'}`);
        }
      }
    }

    if (insertedTicketIds.length) {
      // Note: CSV import is FREE - we only bill for AI extraction, appeals, and liability transfers
    }

    onProgress?.({ percent: 100, message: 'Done' });
    return { success, merged, errors };
  },

  async getHires() {
    if (isMockMode) return MOCK_HIRES;

    // Real Supabase implementation
    try {
      const orgId = await getCurrentOrganisationId();

      const { data, error } = await supabase
        .from('hires')
        .select(`
          *,
          vehicles(id, vrm, make, model),
          drivers(id, name, email, phone, risk_score)
        `)
        .eq('organisation_id', orgId)
        .order('start_date', { ascending: false });

      if (error) {
        console.error('Error fetching hires:', error);
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error('Failed to fetch hires:', error);
      throw error;
    }
  },

  // FIX: Return a shallow copy to trigger React state updates if needed
  async getHireById(id: string) {
    if (isMockMode) {
        const hire = MOCK_HIRES.find(h => h.id === id);
        return hire ? { ...hire } : null;
    }

    // Real Supabase implementation
    try {
      const orgId = await getCurrentOrganisationId();

      const { data, error } = await supabase
        .from('hires')
        .select(`
          *,
          vehicles(id, vrm, make, model, colour),
          drivers(id, name, email, phone, risk_score, license_number, address_line1, city, postcode)
        `)
        .eq('id', id)
        .eq('organisation_id', orgId)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          return null;
        }
        console.error('Error fetching hire:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Failed to fetch hire by ID:', error);
      throw error;
    }
  },

  // --- Smart Hire Suggestions ---
  async suggestHiresForTicket(params: { vrm: string; contraventionDate: string; limit?: number }) {
    const limit = params.limit ?? 3;
    if (isMockMode) {
      const dt = new Date(params.contraventionDate);
      const match = (h: Hire) => h.vehicle_id && MOCK_VEHICLES.some(v => v.id === h.vehicle_id && v.vrm === params.vrm);
      const hires = MOCK_HIRES.filter(match).slice(0, 25);

      const scored = hires.map(h => {
        const start = new Date(h.start_date);
        const end = h.end_date ? new Date(h.end_date) : null;
        const inRange = dt >= start && (!end || dt <= end);
        let diffDays = 0;
        if (!inRange) {
          const endDate = end ?? start;
          const nearest = dt < start ? start : endDate;
          diffDays = Math.ceil(Math.abs(dt.getTime() - nearest.getTime()) / 86400000);
        }
        const score = inRange ? 100 : Math.max(10, 95 - diffDays * 5);
        const compliance_status = h.agreement_url ? (h.agreement_has_liability_clause ? 'Compliant' : 'At Risk') : 'Missing';
        return { ...h, match_score: score, match_label: inRange ? 'Overlaps date' : `${diffDays}d away`, compliance_status };
      });

      return scored.sort((a, b) => b.match_score - a.match_score).slice(0, limit);
    }

    const orgId = await getCurrentOrganisationId();
    const vrm = params.vrm.replace(/\s+/g, '').toUpperCase();
    const target = new Date(params.contraventionDate);
    if (!Number.isFinite(target.getTime())) throw new Error('Invalid contravention date');

    const { data: vehicle, error: vehicleError } = await supabase
      .from('vehicles')
      .select('id, vrm')
      .eq('organisation_id', orgId)
      .eq('vrm', vrm)
      .maybeSingle();

    if (vehicleError) throw vehicleError;
    if (!vehicle?.id) return [];

    const { data: hires, error: hiresError } = await supabase
      .from('hires')
      .select('*')
      .eq('organisation_id', orgId)
      .eq('vehicle_id', vehicle.id)
      .order('start_date', { ascending: false })
      .limit(50);

    if (hiresError) throw hiresError;

    const scored = (hires || []).map((h: any) => {
      const start = new Date(h.start_date);
      const end = h.end_date ? new Date(h.end_date) : null;
      const inRange = target >= start && (!end || target <= end);
      let diffDays = 0;
      if (!inRange) {
        const endDate = end ?? start;
        const nearest = target < start ? start : endDate;
        diffDays = Math.ceil(Math.abs(target.getTime() - nearest.getTime()) / 86400000);
      }
      const score = inRange ? 100 : Math.max(10, 95 - diffDays * 5);
      const compliance_status = h.agreement_url ? (h.agreement_has_liability_clause ? 'Compliant' : 'At Risk') : 'Missing';
      return { ...h, match_score: score, match_label: inRange ? 'Overlaps date' : `${diffDays}d away`, compliance_status };
    });

    return scored.sort((a, b) => b.match_score - a.match_score).slice(0, limit);
  },

  // Updated saveHire to accept an optional file for the agreement
  async saveHire(h: Partial<Hire>, file?: File) {
    if (isMockMode) {
      if (file) {
        h.agreement_url = URL.createObjectURL(file); // Mock URL generation
        h.agreement_signed_at = new Date().toISOString();
        h.agreement_has_liability_clause = true; // Assume true if uploaded manually for now
      }

      if (h.id) {
        // Edit existing
        const idx = MOCK_HIRES.findIndex(existing => existing.id === h.id);
        if (idx !== -1) MOCK_HIRES[idx] = { ...MOCK_HIRES[idx], ...h };
      } else {
        // Create new
        const vehicle = MOCK_VEHICLES.find(v => v.id === h.vehicle_id);
        const newHire = {
          id: `h-${Date.now()}`,
          organisation_id: 'mock',
          ...h,
          vehicles: vehicle ? { vrm: vehicle.vrm } : undefined
        } as Hire;
        MOCK_HIRES.push(newHire);
      }
      return;
    }

    // Real Supabase implementation
    try {
      const orgId = await getCurrentOrganisationId();
      const clauseConfirmed = Boolean(h.agreement_has_liability_clause);

      // Best-effort: link hire to a Driver record (so driver history works reliably).
      const resolveDriverId = async () => {
        if (h.driver_id) return h.driver_id;

        const name = String((h as any)?.driver_name || '').trim();
        const email = String((h as any)?.driver_email || '').trim();
        const phone = String((h as any)?.driver_phone || '').trim();

        if (!name && !email) return null;

        if (email) {
          const { data } = await supabase
            .from('drivers')
            .select('id')
            .eq('organisation_id', orgId)
            .ilike('email', email)
            .limit(1)
            .maybeSingle();
          if (data?.id) return data.id;
        }

        if (name) {
          const { data } = await supabase
            .from('drivers')
            .select('id')
            .eq('organisation_id', orgId)
            .ilike('name', name)
            .limit(1)
            .maybeSingle();
          if (data?.id) return data.id;
        }

        if (!name) return null;

        // Create a minimal driver profile (user can enrich later).
        const { data, error } = await supabase
          .from('drivers')
          .insert({
            organisation_id: orgId,
            name,
            email: email || null,
            phone: phone || null,
            license_number: null,
            risk_score: 'Low'
          })
          .select('id')
          .single();
        if (error) {
          console.warn('Failed to auto-create driver from hire:', error);
          return null;
        }
        return data?.id ?? null;
      };

      const resolvedDriverId = await resolveDriverId();

      // Handle file upload if provided
      let agreementUrl = h.agreement_url;
      if (file) {
        const fileName = `${Date.now()}-${file.name}`;
        const filePath = `${orgId}/hires/${h.id || 'new'}/${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('fleet-documents')
          .upload(filePath, file, { upsert: true });

        if (uploadError) {
          console.error('Error uploading agreement:', uploadError);
          throw uploadError;
        }

        const { data: { publicUrl } } = supabase.storage
          .from('fleet-documents')
          .getPublicUrl(filePath);

        agreementUrl = publicUrl;
      }

      if (h.id) {
        // Fetch the existing hire record for comparison (audit log purposes)
        const { data: existingHire } = await supabase
          .from('hires')
          .select('*')
          .eq('id', h.id)
          .eq('organisation_id', orgId)
          .single();

        // Update existing hire
        const { error } = await supabase
          .from('hires')
          .update({
            vehicle_id: h.vehicle_id,
            driver_id: resolvedDriverId ?? h.driver_id,
            hire_ref: h.hire_ref,
            driver_name: h.driver_name,
            driver_email: h.driver_email,
            driver_phone: h.driver_phone,
            start_date: h.start_date,
            end_date: h.end_date,
            actual_return_date: h.actual_return_date,
            assignment_type: h.assignment_type,
            hirer_address: h.hirer_address,
            agreement_url: agreementUrl || h.agreement_url,
            agreement_signed_at: file ? new Date().toISOString() : h.agreement_signed_at,
            agreement_has_liability_clause: h.agreement_has_liability_clause,
            acceptance_type: h.acceptance_type,
            updated_at: new Date().toISOString()
          })
          .eq('id', h.id)
          .eq('organisation_id', orgId);

        if (error) {
          console.error('Error updating hire:', error);
          throw error;
        }

        // Log the update to audit_logs
        if (existingHire) {
          const changes: Record<string, { old: any; new: any }> = {};
          const fieldsToTrack = [
            'vehicle_id', 'driver_id', 'hire_ref', 'driver_name', 'driver_email',
            'driver_phone', 'start_date', 'end_date', 'actual_return_date',
            'assignment_type', 'hirer_address', 'agreement_url', 'agreement_has_liability_clause'
          ];

          for (const field of fieldsToTrack) {
            const oldValue = existingHire[field];
            const newValue = h[field as keyof typeof h];
            if (newValue !== undefined && oldValue !== newValue) {
              changes[field] = { old: oldValue, new: newValue };
            }
          }

          // Only log if there are actual changes
          if (Object.keys(changes).length > 0) {
            // Get current user information
            const { data: { user } } = await supabase.auth.getUser();
            const userInfo = user ? {
              user_id: user.id,
              user_email: user.email || null,
              user_role: null as string | null
            } : {
              user_id: null,
              user_email: null,
              user_role: null
            };

            // Try to get user role from profile
            if (user) {
              const { data: profile } = await supabase
                .from('profiles')
                .select('role, email')
                .eq('id', user.id)
                .single();

              if (profile) {
                userInfo.user_email = profile.email || user.email || null;
                userInfo.user_role = profile.role || null;
              }
            }

            const { error: auditError } = await supabase
              .from('audit_logs')
              .insert({
                organisation_id: orgId,
                user_id: userInfo.user_id,
                user_email: userInfo.user_email,
                user_role: userInfo.user_role,
                action: 'hire_updated',
                category: 'hire',
                resource_type: 'hire',
                resource_id: h.id,
                description: `Hire record updated: ${h.hire_ref || existingHire.hire_ref}`,
                metadata: {
                  changes,
                  hire_ref: h.hire_ref || existingHire.hire_ref,
                  driver_name: h.driver_name || existingHire.driver_name
                }
              });

            if (auditError) {
              console.error('Failed to create audit log:', auditError);
              // Don't throw - audit log failure shouldn't block hire update
            } else {
              console.log(`📝 Audit log created for hire update: ${h.id}`);
            }
          }
        }

        const clauseChanged = existingHire?.agreement_has_liability_clause !== clauseConfirmed;
        if (clauseConfirmed && clauseChanged && h.vehicle_id) {
          await refreshAutoAssignForVehicle(orgId, String(h.vehicle_id));
        }
      } else {
        // Create new hire
        const { error } = await supabase
          .from('hires')
          .insert({
            organisation_id: orgId,
            vehicle_id: h.vehicle_id,
            driver_id: resolvedDriverId ?? h.driver_id,
            hire_ref: h.hire_ref,
            driver_name: h.driver_name || 'Unknown',
            driver_email: h.driver_email,
            driver_phone: h.driver_phone,
            start_date: h.start_date || new Date().toISOString(),
            end_date: h.end_date,
            actual_return_date: h.actual_return_date,
            assignment_type: h.assignment_type,
            hirer_address: h.hirer_address,
            agreement_url: agreementUrl,
            agreement_signed_at: file ? new Date().toISOString() : h.agreement_signed_at,
            agreement_has_liability_clause: h.agreement_has_liability_clause || false,
            acceptance_type: h.acceptance_type
          });

        if (error) {
          console.error('Error creating hire:', error);
          throw error;
        }

        if (clauseConfirmed && h.vehicle_id) {
          await refreshAutoAssignForVehicle(orgId, String(h.vehicle_id));
        }
      }
    } catch (error) {
      console.error('Failed to save hire:', error);
      throw error;
    }
  },

  async updateHireAgreement(
    hireId: string,
    updates: {
      agreement_url?: string | null;
      agreement_signed_at?: string | null;
      agreement_has_liability_clause?: boolean;
    }
  ): Promise<void> {
    if (!hireId) throw new Error('Missing hire ID');
    const orgId = await getCurrentOrganisationId();

    const { data: existingHire, error: existingError } = await supabase
      .from('hires')
      .select('agreement_has_liability_clause, vehicle_id')
      .eq('id', hireId)
      .eq('organisation_id', orgId)
      .maybeSingle();

    if (existingError) {
      console.warn('Failed to load hire for agreement update:', existingError);
    }

    const payload: any = {
      updated_at: new Date().toISOString()
    };
    if (updates.agreement_url !== undefined) payload.agreement_url = updates.agreement_url;
    if (updates.agreement_signed_at !== undefined) payload.agreement_signed_at = updates.agreement_signed_at;
    if (updates.agreement_has_liability_clause !== undefined) {
      payload.agreement_has_liability_clause = updates.agreement_has_liability_clause;
    }

    const { error } = await supabase
      .from('hires')
      .update(payload)
      .eq('id', hireId)
      .eq('organisation_id', orgId);

    if (error) throw error;

    const clauseChanged =
      updates.agreement_has_liability_clause !== undefined &&
      existingHire?.agreement_has_liability_clause !== updates.agreement_has_liability_clause;

    if (clauseChanged && updates.agreement_has_liability_clause && existingHire?.vehicle_id) {
      await refreshAutoAssignForVehicle(orgId, String(existingHire.vehicle_id));
    }
  },
  async searchHires(term: string) {
    if (isMockMode) return MOCK_HIRES.filter(h => h.driver_name.toLowerCase().includes(term.toLowerCase()));
    const orgId = await getCurrentOrganisationId();
    const q = (term || '').trim();
    if (!q) return [];
    const { data, error } = await supabase
      .from('hires')
      .select('*')
      .eq('organisation_id', orgId)
      .ilike('driver_name', `%${q}%`)
      .order('start_date', { ascending: false })
      .limit(10);
    if (error) throw error;
    return data || [];
  },
  async findMatchingHires(vId: string, _date: string) { return isMockMode ? MOCK_HIRES.filter(h => h.vehicle_id === vId) : []; },

  async lookupVehicle(vrm: string) {
    if (isMockMode) {
      await new Promise(resolve => setTimeout(resolve, 1500));
      const makes = ['Ford', 'Vauxhall', 'BMW', 'Mercedes', 'Tesla', 'Volkswagen'];
      const models = { 'Ford': ['Transit', 'Fiesta', 'Focus'], 'Vauxhall': ['Vivaro', 'Corsa', 'Astra'], 'BMW': ['3 Series', '5 Series', 'X5'], 'Mercedes': ['Sprinter', 'C-Class', 'E-Class'], 'Tesla': ['Model 3', 'Model Y'], 'Volkswagen': ['Transporter', 'Golf', 'Passat'] };
      const colours = ['White', 'Silver', 'Black', 'Blue', 'Grey'];
      const make = makes[Math.floor(Math.random() * makes.length)];
      // @ts-ignore
      const model = models[make][Math.floor(Math.random() * models[make].length)];
      const colour = colours[Math.floor(Math.random() * colours.length)];
      const taxed = Math.random() > 0.2;
      const motValid = Math.random() > 0.15;
      const taxDue = new Date(Date.now() + 86400000 * (30 + Math.floor(Math.random() * 330))).toISOString().slice(0, 10);
      const motExpiry = new Date(Date.now() + 86400000 * (30 + Math.floor(Math.random() * 330))).toISOString().slice(0, 10);
      return {
        make,
        model,
        colour,
        yearOfManufacture: 2018 + Math.floor(Math.random() * 6),
        fuelType: Math.random() > 0.5 ? 'Diesel' : 'Electric',
        taxStatus: taxed ? 'Taxed' : 'Untaxed',
        taxDueDate: taxed ? taxDue : null,
        motStatus: motValid ? 'Valid' : 'No details held by DVLA',
        motExpiryDate: motValid ? motExpiry : null,
        monthOfFirstRegistration: `${2000 + Math.floor(Math.random() * 25)}-0${1 + Math.floor(Math.random() * 9)}`,
        engineCapacity: 1000 + Math.floor(Math.random() * 2000),
        co2Emissions: 90 + Math.floor(Math.random() * 160),
        euroStatus: `EURO ${4 + Math.floor(Math.random() * 3)}`,
        markedForExport: false,
        dateOfLastV5CIssued: new Date(Date.now() - 86400000 * (30 + Math.floor(Math.random() * 1000))).toISOString().slice(0, 10)
      };
    }

    const normalize = (raw: string) => raw.replace(/\s+/g, '').toUpperCase();
    const vrmNorm = normalize(vrm);

    const hasStorage = () => {
      try {
        return typeof localStorage !== 'undefined';
      } catch {
        return false;
      }
    };

    const cacheKey = `dvla:vehicle:${vrmNorm}`;
    const cacheTtlMs = 24 * 60 * 60 * 1000; // 24 hours

    if (hasStorage()) {
      try {
        const cachedRaw = localStorage.getItem(cacheKey);
        if (cachedRaw) {
          const cached = JSON.parse(cachedRaw) as { ts: number; data: any };
          if (cached?.ts && Date.now() - cached.ts < cacheTtlMs && cached.data) {
            return cached.data;
          }
        }
      } catch {
        // ignore cache read errors
      }
    }

    // Basic client-side rate limiting to reduce DVLA quota risk (10 requests/minute)
    const rateKey = 'dvla:ratelimit:v1';
    const rateWindowMs = 60_000;
    const rateMax = 10;

    if (hasStorage()) {
      try {
        const raw = localStorage.getItem(rateKey);
        const now = Date.now();
        const parsed = raw ? (JSON.parse(raw) as { windowStart: number; count: number }) : null;
        const windowStart = parsed?.windowStart && now - parsed.windowStart < rateWindowMs ? parsed.windowStart : now;
        const count = parsed?.windowStart && now - parsed.windowStart < rateWindowMs ? (parsed?.count ?? 0) : 0;
        if (count >= rateMax) {
          throw new Error('DVLA lookup rate limit exceeded. Please wait a moment and try again.');
        }
        localStorage.setItem(rateKey, JSON.stringify({ windowStart, count: count + 1 }));
      } catch (e) {
        if (e instanceof Error) throw e;
      }
    }

    // Call DVLA via Supabase Edge Function to avoid CORS issues
    const supabaseUrl = (import.meta as any).env?.VITE_SUPABASE_URL;
    const { data: { session } } = await supabase.auth.getSession();

    if (!session) {
      throw new Error('You must be logged in to use DVLA lookup');
    }

    const edgeFunctionUrl = `${supabaseUrl}/functions/v1/dvla`;

    const resp = await fetch(edgeFunctionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`
      },
      body: JSON.stringify({ registrationNumber: vrmNorm })
    });

    if (!resp.ok) {
      if (resp.status === 404) throw new Error('Vehicle not found');
      if (resp.status === 401) throw new Error('Unauthorized - please log in again');
      if (resp.status === 500) throw new Error('DVLA service unavailable');
      let message = `DVLA lookup failed (${resp.status})`;
      try {
        const data = await resp.json();
        message = (data as any)?.error || (data as any)?.message || message;
      } catch {
        // ignore
      }
      throw new Error(message);
    }

    const responseData = await resp.json().catch(() => ({}));

    if (!responseData.ok) {
      throw new Error(responseData.error || 'DVLA lookup failed');
    }

    const raw = responseData.data || {};

    const result = {
      make: (raw as any)?.make,
      model: (raw as any)?.model,
      colour: (raw as any)?.colour,
      yearOfManufacture: (raw as any)?.yearOfManufacture,
      fuelType: (raw as any)?.fuelType,
      // Useful compliance fields for hire/fleet ops
      taxStatus: (raw as any)?.taxStatus,
      taxDueDate: (raw as any)?.taxDueDate,
      motStatus: (raw as any)?.motStatus,
      motExpiryDate: (raw as any)?.motExpiryDate,
      // Additional vehicle details often useful for verification / compliance packs
      monthOfFirstRegistration: (raw as any)?.monthOfFirstRegistration,
      engineCapacity: (raw as any)?.engineCapacity,
      co2Emissions: (raw as any)?.co2Emissions,
      euroStatus: (raw as any)?.euroStatus,
      wheelplan: (raw as any)?.wheelplan,
      revenueWeight: (raw as any)?.revenueWeight,
      markedForExport: (raw as any)?.markedForExport,
      typeApproval: (raw as any)?.typeApproval,
      dateOfLastV5CIssued: (raw as any)?.dateOfLastV5CIssued
    };

    if (hasStorage()) {
      try {
        localStorage.setItem(cacheKey, JSON.stringify({ ts: Date.now(), data: result }));
      } catch {
        // ignore cache write errors
      }
    }

    return result;
  },

  async uploadDocument(file: File, type: 'agreement' | 'license' | 'submission_proof', relatedId: string): Promise<{ path: string; signedUrl?: string }> {
    if (isMockMode) {
        console.log(`Uploading ${type} for ${relatedId}: ${file.name}`);
        await new Promise(resolve => setTimeout(resolve, 1500)); // Simulate upload
        
        // Update Mock Data Stores
        if (type === 'agreement') {
            const hire = MOCK_HIRES.find(h => h.id === relatedId);
            if (hire) {
                hire.agreement_url = URL.createObjectURL(file); // Use local blob for demo
                hire.agreement_signed_at = new Date().toISOString();
                hire.agreement_has_liability_clause = true; // Assume true on upload for demo
            }
        } else if (type === 'license') {
            const driver = MOCK_DRIVERS.find(d => d.id === relatedId);
            if (driver) {
                driver.license_document_url = URL.createObjectURL(file);
            }
        } else if (type === 'submission_proof') {
            const ticket = MOCK_TICKETS.find(t => t.id === relatedId);
            if (ticket) {
               // Update ticket status
               ticket.status = 'transferred'; // Assume transferred
               ticket.decision = 'transfer';
               ticket.document_url = URL.createObjectURL(file);
            }
        }
        return { path: URL.createObjectURL(file), signedUrl: URL.createObjectURL(file) };
    }

    const orgId = await getCurrentOrganisationId();
    const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');

    let folder = 'misc';
    if (type === 'agreement') folder = `hires/${relatedId}`;
    if (type === 'license') folder = `drivers/${relatedId}`;
    if (type === 'submission_proof') folder = `transfers/${relatedId}`;

    const path = `${orgId}/${folder}/${Date.now()}-${safeName}`;
    const { error } = await supabase.storage
      .from('fleet-documents')
      .upload(path, file, { upsert: false, contentType: file.type });

    if (error) {
      console.error('Error uploading document:', error);
      throw error;
    }

    // Signed URL helps the immediate UI preview private documents without persisting bearer URLs.
    const { data: signed, error: signedError } = await supabase.storage
      .from('fleet-documents')
      .createSignedUrl(path, 60 * 60);

    if (signedError) {
      console.warn('Failed to create signed URL:', signedError);
    }

    const signedUrl = signed?.signedUrl;

    // Best-effort persist storage paths for key flows. Do not store signed URLs in the database.
    try {
      if (type === 'agreement') {
        await supabase
          .from('hires')
          .update({
            agreement_url: path,
            agreement_signed_at: new Date().toISOString()
          })
          .eq('id', relatedId)
          .eq('organisation_id', orgId);
      }
      if (type === 'license') {
        await supabase
          .from('drivers')
          .update({ license_doc_url: path })
          .eq('id', relatedId)
          .eq('organisation_id', orgId);
      }
    } catch (e) {
      console.warn('Failed to persist uploaded document link:', e);
    }

    return { path, signedUrl };
  },

  async resolveDocumentUrl(ref: string, expiresInSeconds = 60 * 60): Promise<string> {
    const raw = String(ref || '').trim();
    if (!raw) return '';
    if (/^(https?:\/\/|data:|blob:)/i.test(raw)) return raw;

    if (isMockMode) return raw;

    const { data, error } = await supabase.storage
      .from('fleet-documents')
      .createSignedUrl(raw, expiresInSeconds);

    if (error) throw error;
    return data?.signedUrl || '';
  },

  // Upload a scan at ticket intake time (before ticketId exists). Returns the storage path.
  async uploadTicketScanAsset(file: File): Promise<{ path: string; signedUrl?: string }> {
    if (isMockMode) {
      const url = URL.createObjectURL(file);
      return { path: url, signedUrl: url };
    }

    const orgId = await getCurrentOrganisationId();
    const prepared = await compressImageFile(file, { maxBytes: 1_000_000, maxDimension: 1920 }).catch(() => file);
    const safeName = String(prepared.name || file.name).replace(/[^a-zA-Z0-9._-]/g, '_');
    const nonce = Math.random().toString(16).slice(2, 8);
    const path = `${orgId}/tickets/scans/${Date.now()}-${nonce}-${safeName}`;

    const { error } = await supabase.storage
      .from('fleet-documents')
      .upload(path, prepared, { upsert: false, contentType: prepared.type || file.type || 'application/octet-stream' });

    if (error) {
      console.error('Error uploading ticket scan asset:', error);
      throw error;
    }

    const signedUrl = await ApiService.resolveDocumentUrl(path).catch(() => undefined);
    return { path, signedUrl };
  },

  async uploadTicketScan(ticketId: string, file: File): Promise<{ path: string; signedUrl?: string }> {
    if (isMockMode) {
      const url = URL.createObjectURL(file);
      const idx = MOCK_TICKETS.findIndex(t => t.id === ticketId);
      if (idx !== -1) MOCK_TICKETS[idx] = { ...MOCK_TICKETS[idx], document_url: url };
      return { path: url, signedUrl: url };
    }

    const orgId = await getCurrentOrganisationId();
    const prepared = await compressImageFile(file, { maxBytes: 1_000_000, maxDimension: 1920 }).catch(() => file);
    const safeName = (prepared.name || file.name).replace(/[^a-zA-Z0-9._-]/g, '_');
    const path = `${orgId}/tickets/${ticketId}/scan/${Date.now()}-${safeName}`;

    const { error } = await supabase.storage
      .from('fleet-documents')
      .upload(path, prepared, { upsert: true, contentType: prepared.type || file.type });
    if (error) throw error;

    // Store the storage path in the ticket so we can re-sign on demand.
    await ApiService.updateTicket(ticketId, { document_url: path } as any);

    const signedUrl = await ApiService.resolveDocumentUrl(path).catch(() => undefined);
    return { path, signedUrl };
  },

  async uploadTicketEvidence(ticketId: string, file: File): Promise<{ path: string; signedUrl?: string }> {
    if (isMockMode) {
      const url = URL.createObjectURL(file);
      MOCK_TICKET_EVIDENCE[ticketId] = [...(MOCK_TICKET_EVIDENCE[ticketId] || []), { name: file.name, url }];
      return { path: url, signedUrl: url };
    }

    const orgId = await getCurrentOrganisationId();
    const prepared = await compressImageFile(file, { maxBytes: 1_000_000, maxDimension: 1920 }).catch(() => file);
    const safeName = (prepared.name || file.name).replace(/[^a-zA-Z0-9._-]/g, '_');
    const path = `${orgId}/tickets/${ticketId}/evidence/${Date.now()}-${safeName}`;

    const { error } = await supabase.storage
      .from('fleet-documents')
      .upload(path, prepared, { upsert: false, contentType: prepared.type || file.type });
    if (error) throw error;

    const signedUrl = await ApiService.resolveDocumentUrl(path).catch(() => undefined);
    return { path, signedUrl };
  },

  async listTicketEvidence(ticketId: string): Promise<Array<{ name: string; path: string; signedUrl?: string }>> {
    if (isMockMode) {
      return (MOCK_TICKET_EVIDENCE[ticketId] || []).map(item => ({ name: item.name, path: item.url, signedUrl: item.url }));
    }

    const orgId = await getCurrentOrganisationId();
    const folder = `${orgId}/tickets/${ticketId}/evidence`;
    const { data, error } = await supabase.storage
      .from('fleet-documents')
      .list(folder, { limit: 100, sortBy: { column: 'created_at', order: 'desc' } as any });
    if (error) throw error;

    const items = (data || [])
      .filter((o: any) => o && o.name && o.name !== '.emptyFolderPlaceholder')
      .map((o: any) => ({ name: o.name as string, path: `${folder}/${o.name}` }));

    const resolved = await Promise.all(items.map(async (item) => {
      try {
        const signedUrl = await ApiService.resolveDocumentUrl(item.path);
        return { ...item, signedUrl };
      } catch {
        return item;
      }
    }));

    return resolved;
  },

  async deleteTicketEvidence(pathOrUrl: string): Promise<void> {
    const raw = String(pathOrUrl || '').trim();
    if (!raw) return;

    if (isMockMode) {
      for (const key of Object.keys(MOCK_TICKET_EVIDENCE)) {
        MOCK_TICKET_EVIDENCE[key] = (MOCK_TICKET_EVIDENCE[key] || []).filter(item => item.url !== raw);
      }
      return;
    }

    // Only storage paths can be removed; signed URLs won't work here.
    if (/^(https?:\/\/|data:|blob:)/i.test(raw)) return;
    await supabase.storage.from('fleet-documents').remove([raw]);
  },

  // --- Billing ---
  async createCheckoutSession(tier: 'starter' | 'growth' | 'pro_fleet', billingCycle: 'monthly' | 'yearly' = 'monthly'): Promise<{ url: string }> {
    // Construct return URL for Stripe callback (HashRouter compatible)
    const origin = window.location.origin;
    const path = window.location.pathname.replace(/\/$/, ''); // Remove trailing slash
    const returnUrl = `${origin}${path}/#/stripe-callback?tier=${tier}`;

    if (isMockMode) {
      console.log(`Creating mock checkout session for ${tier}`);
      await new Promise(resolve => setTimeout(resolve, 1500)); // Simulate API call

      // Construct the absolute callback URL with hash path
      const successUrl = `${returnUrl}&session_id=cs_test_mock123`;

      return { url: successUrl };
    }

    // Call Stripe checkout session edge function
    const supabaseUrl = (import.meta as any).env?.VITE_SUPABASE_URL;
    const { data: { session } } = await supabase.auth.getSession();

    if (!session) {
      throw new Error('You must be logged in to create a checkout session');
    }

    const edgeFunctionUrl = `${supabaseUrl}/functions/v1/create-checkout-session`;

    console.log('Creating Stripe checkout session:', { tier, billingCycle, returnUrl });

    const resp = await fetch(edgeFunctionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`
      },
      body: JSON.stringify({ tier, billingCycle, returnUrl })
    });

    if (!resp.ok) {
      const errorData = await resp.json().catch(() => ({}));
      throw new Error(errorData.error || `Failed to create checkout session (${resp.status})`);
    }

    const responseData = await resp.json();

    if (!responseData.ok || !responseData.url) {
      throw new Error(responseData.error || 'Failed to create checkout session');
    }

    console.log('Checkout session created:', responseData.sessionId);

    return { url: responseData.url };
  },

  // --- NEW: Onboarding stats (lightweight counts) ---
  async getOnboardingStats(): Promise<{ vehicles: number; drivers: number; hires: number; verifiedAgreements: number; tickets: number; openTasks: number }> {
    if (isMockMode) {
      return {
        vehicles: MOCK_VEHICLES.length,
        drivers: MOCK_DRIVERS.length,
        hires: MOCK_HIRES.length,
        verifiedAgreements: MOCK_HIRES.filter(h => Boolean(h.agreement_url && h.agreement_has_liability_clause)).length,
        tickets: MOCK_TICKETS.length,
        openTasks: MOCK_TASKS.filter(t => t.status === 'open').length
      };
    }

    try {
      const orgId = await getCurrentOrganisationId();

      const [
        vehiclesRes,
        driversRes,
        hiresRes,
        verifiedAgreementsRes,
        ticketsRes,
        openTasksRes
      ] = await Promise.all([
        supabase.from('vehicles').select('id', { count: 'exact', head: true }).eq('organisation_id', orgId),
        supabase.from('drivers').select('id', { count: 'exact', head: true }).eq('organisation_id', orgId),
        supabase.from('hires').select('id', { count: 'exact', head: true }).eq('organisation_id', orgId),
        supabase
          .from('hires')
          .select('id', { count: 'exact', head: true })
          .eq('organisation_id', orgId)
          .not('agreement_url', 'is', null)
          .eq('agreement_has_liability_clause', true),
        supabase.from('tickets').select('id', { count: 'exact', head: true }).eq('organisation_id', orgId),
        supabase.from('tasks').select('id', { count: 'exact', head: true }).eq('organisation_id', orgId).eq('status', 'open')
      ]);

      // If any query errors, surface the first one (but still keep counts safe)
      const firstError = vehiclesRes.error || driversRes.error || hiresRes.error || verifiedAgreementsRes.error || ticketsRes.error || openTasksRes.error;
      if (firstError) throw firstError;

      return {
        vehicles: vehiclesRes.count || 0,
        drivers: driversRes.count || 0,
        hires: hiresRes.count || 0,
        verifiedAgreements: verifiedAgreementsRes.count || 0,
        tickets: ticketsRes.count || 0,
        openTasks: openTasksRes.count || 0
      };
    } catch (error) {
      console.error('Failed to fetch onboarding stats:', error);
      return { vehicles: 0, drivers: 0, hires: 0, verifiedAgreements: 0, tickets: 0, openTasks: 0 };
    }
  },

  // --- NEW: Feedback & Support ---
  async submitFeedback(data: { type: 'bug' | 'feature'; subject: string; description: string; email: string, image?: File }) {
    if (isMockMode) {
      console.log('Feedback submitted:', data);
      if (data.image) {
        console.log('Attached Image:', data.image.name);
      }
      await new Promise(resolve => setTimeout(resolve, 1000));
      return true;
    }

    try {
      const orgId = await getCurrentOrganisationId();
      const { data: { user } } = await supabase.auth.getUser();

      let screenshotPath: string | null = null;

      if (data.image) {
        const safeName = data.image.name.replace(/[^a-zA-Z0-9._-]/g, '_');
        const filePath = `${orgId}/feedback/${Date.now()}-${safeName}`;

        const { error: uploadError } = await supabase.storage
          .from('fleet-documents')
          .upload(filePath, data.image, { upsert: false, contentType: data.image.type });

        if (uploadError) {
          console.error('Error uploading feedback screenshot:', uploadError);
          throw uploadError;
        }

        screenshotPath = filePath;
      }

      const { error: insertError } = await supabase
        .from('feedback')
        .insert({
          organisation_id: orgId,
          user_id: user?.id || null,
          type: data.type,
          subject: data.subject,
          description: data.description,
          email: data.email,
          page_url: typeof window !== 'undefined' ? window.location.href : null,
          user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
          screenshot_path: screenshotPath
        });

      if (insertError) {
        console.error('Error inserting feedback:', insertError);
        throw insertError;
      }

      return true;
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      throw error;
    }
  }
};

const refreshAutoAssignForVehicle = async (orgId: string, vehicleId: string) => {
  if (!vehicleId) return;
  const { data: tickets, error } = await supabase
    .from('tickets')
    .select('id, timeline')
    .eq('organisation_id', orgId)
    .eq('vehicle_id', vehicleId)
    .eq('status', 'unassigned')
    .is('hire_id', null)
    .limit(50);

  if (error) throw error;
  if (!tickets?.length) return;

  const auditLimit = pLimit(AUDIT_CONCURRENCY_LIMIT);
  await Promise.all(
    tickets.map((ticket: any) =>
      auditLimit(async () => {
        try {
          const audit = await auditAgreement(ticket.id, orgId);
          const updatePayload: any = { audit_result: audit };

          if (audit.autoAssignable && audit.hireId) {
            const existingTimeline = typeof ticket.timeline === 'string'
              ? JSON.parse(ticket.timeline)
              : (ticket.timeline || []);
            updatePayload.hire_id = audit.hireId;
            updatePayload.status = 'active';
            updatePayload.auto_assigned = true;
            updatePayload.timeline = [
              ...existingTimeline,
              {
                id: `auto-${Date.now()}-${Math.random().toString(16).slice(2, 6)}`,
                date: new Date().toISOString(),
                title: 'Auto-assigned to Hire',
                description: `Confidence: ${audit.confidenceScore}% | ${audit.reason}`,
                type: 'system'
              } as TimelineEvent
            ];
          }

          await supabase
            .from('tickets')
            .update(updatePayload)
            .eq('id', ticket.id);
        } catch (err) {
          console.warn(`Auto-assign refresh failed for ticket ${ticket.id}:`, err);
        }
      })
    )
  );
};
